import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { dealValue } from '@/lib/deals';
import { documentDisplayTitle, formatDecimalString } from '@/lib/format';
import type {
  AccountLastNote,
  Company,
  CompanyCreditBalance,
  CompanyListParams,
  CompanyListSort,
  CompanyPurchaseCycle,
  CompanyStageStatusRef,
  Deal,
  DealListSort,
  DealQuoteSigned,
  Invoice,
  InvoiceListSort,
  ListOrder,
  MasterRef,
  Paginated,
  Quote,
  QuoteListSort,
  TaskTargetType,
  ViewerLatestNote,
} from '@/lib/types';
import { purchaseHistoryOf } from '@/lib/company-purchases';
import { documentCustomerName } from '@/lib/company-names';
import {
  STAGE_STATUS_COLUMN_PREFIX,
  columnSelection,
  parseStageStatusColumnId,
  type StageStatusColumn,
} from '@/lib/stage-status-columns';

/*
 * The five record tables behind the University Partnerships dashboard: the
 * signed-in person's leads, deals, quotes, invoices and companies, five rows
 * each.
 *
 * This module fetches and orders. It renders nothing and words nothing — every
 * sentence on the screen belongs to the page, including the sentence about how
 * a table is ordered. What this hook owes the page is the FACTS it needs to
 * write that sentence honestly, which is why `scopedBy`, `ordering` and
 * `updatedAtAlsoWrittenByXero` are on the state rather than assumed by either
 * side.
 *
 * ⚠️ **Leads and Companies are two halves of one master, not two tables.** Both
 * read `/companies`; Leads asks for `LEAD_COMPANY_STAGE_ID` and Companies asks
 * for every other ACTIVE stage id, derived from `/company-stages` at fetch time
 * rather than written down. They were both unfiltered once and every lead
 * appeared twice on the page. Change either half and you have changed the other
 * — `nonLeadStageFilter` carries the reasoning, including why it refuses rather
 * than falling back to an unfiltered fetch.
 *
 * ⚠️ THIS PAGE USED TO ORDER BY `noteSummary.lastNoteAt`, AND THAT WAS A
 * PER-VIEWER FIGURE PRESENTED AS AN ACCOUNT FACT.
 *
 * Every notes read runs through the same visibility clause. Without the
 * `administrator` audit grant — which migration 040 gives to the administrator
 * set alone, 4 of 11 live accounts — that clause is `n.author_id = :actorId`,
 * so `lastNoteAt` is `MAX(created_at)` over **the viewer's own notes only**.
 * `taskSummary.nextDue` is scoped identically.
 *
 * The consequences were both silent. The same five tables ordered differently
 * for different people with nothing on screen saying so; and for most staff the
 * stamp was blank on every account they had not personally written on, which
 * read as "nothing has ever happened here" when the truthful reading was "you
 * have not written a note here". A blank cell cannot carry two opposite
 * meanings, and nothing typechecks a column header.
 *
 * ⚠️ **THE SENTENCE THAT USED TO SIT HERE — "there is still no server-side
 * activity ordering anywhere in this API" — WAS FALSE, and it was false while
 * this same file sent `activityFirst` on all five requests.** `activityFirst`
 * shipped 2026-08-29 and ranks by `latest_activity_at` in SQL BEFORE the limit,
 * which is the one thing no client-side pass can do. The claim and the code
 * contradicted each other about seventy lines apart and nothing failed, because
 * a stale sentence in a comment is exactly what no build, lint or test looks at.
 *
 * Nothing here calls `noteSummary` any more. Do not put it back as an ordering
 * key: there is no reliable client signal for "holds the notes audit grant"
 * (`ability.isAdmin` is a different question and reading it as this one is the
 * proxy-field mistake this codebase documents at length), so a stamp derived
 * from it cannot be labelled truthfully for both kinds of viewer at once.
 *
 * ## ⚠️ A VIEWER-SCOPED FIGURE IS BACK ON THIS PAGE, DELIBERATELY — `latestNote`
 *
 * ✅ Added 2026-09-03. The four list endpoints return `latestNote` per row, and
 * it runs through the SAME visibility clause the paragraphs above describe: for
 * a reader without the audit grant it is the newest note **they wrote**, and
 * `null` is *no note you may see*, not *nothing has happened here*.
 *
 * 🔴 **THAT IS THE EXACT AMBIGUITY THIS PAGE WAS REPAIRED FOR, SO THE
 * MITIGATION IS NOT OPTIONAL AND IT IS NOT IN THIS MODULE.** The owner chose the
 * viewer-scoped reading knowingly; what makes it safe is that the COLUMN NAMES
 * THE READER — `COLUMN_LABEL.note` on the page reads **"Your last note"**, and
 * an empty cell reads **"None from you"** rather than a dash. A column headed
 * "Notes" over this field would be the removed defect, restored.
 *
 * ⛔ **It is NOT an ordering key and must never become one.** Both reasons above
 * still bind, unchanged: it cannot be labelled truthfully for a grant holder and
 * a non-holder at once, and a client-side re-sort of a returned page is not an
 * ordering of a table. Ordering stays `activityFirst` / `sort`, which are
 * viewer-INDEPENDENT.
 *
 * ⚠️ **`latestActivityAt` and `latestNote` sit two fields apart on the wire and
 * answer opposite questions** — the first is the same for everybody, the second
 * is per reader. `src/lib/types.ts` says so at both fields; do not read one as a
 * cheaper version of the other.
 *
 * ## Ordering is CHOSEN per card, and the A–Z KEY DIFFERS PER ENDPOINT
 *
 * ⚠️ **All five cards can now be ordered alphabetically, and this header said
 * for a while that three of them could not.** That paragraph was true when it
 * was written and was falsified by a change in the OTHER repo, with nothing
 * here able to fail: `/deals`, `/quotes` and `/invoices` gained `sort` and
 * `order`, so the three cards that offered only "Recent activity · Newest
 * first" now offer A–Z and Z–A as well. It is replaced rather than annotated
 * because the claim it made — "asking for it is a 422" — is now exactly
 * backwards, and a struck-through 422 warning still reads as a warning.
 *
 * 🔴 **THERE IS NO SHARED `name` KEY, AND ASKING FOR ONE IS A 422.** This is the
 * single thing to get right here, and it is a trap because four endpoints that
 * answer the same question spell it three different ways:
 *
 * | card              | endpoint     | A–Z key    |
 * | ----------------- | ------------ | ---------- |
 * | Leads, Companies  | `/companies` | `name`     |
 * | Deals             | `/deals`     | `dealName` |
 * | Quotes, Invoices  | `/quotes`, `/invoices` | `subject` |
 *
 * The backend refused a `name` alias deliberately: on an invoice it would have
 * to mean `subject`, and nothing on the wire would then say which column was
 * actually sorted. So the key is a property of the CARD's endpoint and lives on
 * each spec as `azSortKey` — one declaration per card, translated by the one
 * builder below. Do not re-derive it at a call site and do not write a `switch`
 * on the card's name: a fifth spelling arrives as a `tsc` error only while the
 * key is a typed field on the spec.
 *
 * ⚠️ **`activityFirst` is a PREFIX, not a peer of `sort` — it DOMINATES it.**
 * `attentionOrderPrefix` returns `latest_activity_at DESC, `, which is
 * concatenated in front of the chosen column, so `activityFirst=true` together
 * with `sort=dealName&order=asc` yields
 * `ORDER BY latest_activity_at DESC, deal_name ASC, id DESC` and the
 * alphabetical order survives only as a tie-break inside the block of rows
 * sharing an activity stamp. A card offering "A–Z" while still sending
 * `activityFirst` would therefore be offering a control whose effect is mostly
 * a lie — so the two are MUTUALLY EXCLUSIVE here, and `orderParamsFor` below is
 * the single place that decides it. Do not send both.
 *
 * ⚠️ **"Newest first" sends NOTHING, and that is the correct way to ask for
 * it.** The endpoints' own default is `sort=id&order=desc`, which emits
 * byte-identically the `ORDER BY <t>.id DESC` they emitted before `sort`
 * existed. Sending the pair explicitly would buy no behaviour and would leak a
 * redundant default into the URL and into every link built from it.
 *
 * ⚠️ **There is deliberately NO client-side sort fallback anywhere here**, and
 * that survives the endpoints gaining `sort` — it was never an argument about
 * what the server could do. See the note above `TABLES`.
 *
 * ## The quote's STAGE and the invoice's STATUS ride on the row
 *
 * ✅ Added 2026-09-03, owner's instruction: *"the quote status and invoice
 * status need to show up as well in the individual rows in the cards"*.
 *
 * ⚠️ **THE PLACEMENT MOVED LATER THE SAME DAY, AND ONLY THE PLACEMENT.**
 * *SUPERSEDED, wherever this section said the status renders **beside the
 * record's name**.* Owner's instruction: *"in quotes and invoices cards on the
 * up dashboard i want the status as other column and not below in each row"* —
 * so it is a COLUMN of its own, between the record column and the value column,
 * declared per card in `extraColumns` and rendered by the page. The quotes card
 * carries a signature column beside it and the invoices card an overdue one,
 * for the same reason and by the same mechanism.
 *
 * 🔴 **Every word below about the VALUE still holds and none of it is about
 * layout.** The row carries `{id, name}` and NO FLAGS; the masters are resolved
 * by the PAGE, one request each; and nothing here may ever match on a status
 * NAME. Moving a badge into a column changed where it is painted and nothing
 * about what it is allowed to mean.
 *
 * 🔴 **THE ROW CARRIES `{id, name}` AND NO FLAGS, AND THAT IS THE WHOLE
 * DIFFICULTY.** `Quote.stage` and `Invoice.status` are both `MasterRef | null`
 * — the server's `toLookup()` projection — so `isAccepted`, `isDeclined`,
 * `isSettled` and `isPaid` are **absent from every row this module fetches**.
 * They live on the `/quote-stages` and `/invoice-statuses` MASTER rows, which is
 * why `isStageAccepted()` and `isStatusSettled()` both take the master list as a
 * parameter and resolve by id.
 *
 * So this module emits the `MasterRef` **verbatim, tagged with which master it
 * came from**, and resolves nothing. The page holds the two masters — one
 * request each for the whole page, never one per table and never one per row —
 * and hands them to `StageBadge` / `StatusBadge`, which is where flag resolution
 * already lives and must stay. A second copy here would agree the day it was
 * written and drift on the first correction, and what would drift is whether a
 * document reads as settled.
 *
 * ⛔ **Do not resolve a name here, and never match one.** `"Unpaid"` contains
 * `"paid"`; `"Not Accepted"` contains `"accept"`. Both masters are
 * admin-editable free text and this codebase has been bitten three times by
 * exactly that.
 *
 * ⚠️ **A TAGGED UNION rather than a bare `MasterRef`, and the tag is the guard.**
 * A company's stage, a quote's stage and an invoice's status are all `{id, name}`
 * and are three different masters with three different flag sets. Passing one
 * where another is expected would compile and would paint a company stage with
 * quote semantics; the discriminant makes that a `tsc` error at the call site
 * instead. This is the same reason `AZ_SORT_KEY` is typed per endpoint.
 */

export type PartnershipTableKey = 'leads' | 'deals' | 'quotes' | 'invoices' | 'companies';

/* --- how a card is ordered ------------------------------------------------ */

/**
 * The orderings a card can offer. Every member is a real server-side order —
 * there is no member here that this module fakes on the client.
 *
 *  - `activity`  — `activityFirst=true`: most recently touched account first.
 *  - `az` / `za` — the card's own `azSortKey` with `order=asc`/`desc`. Available
 *                  on ALL FIVE cards; the KEY differs per endpoint, which is
 *                  why it is not written here.
 *  - `newest`    — the endpoint's own default, `id DESC`, requested by sending
 *                  nothing. **The three document cards only** — see
 *                  `DOCUMENT_SORTS` for why `/companies` cannot offer it.
 *  - `status-asc` / `status-desc` — the document's stage or status column.
 *                  **The QUOTES and INVOICES cards only**, and see
 *                  {@link STATUS_SORT_KEY}: it is `stage` on one and `status` on
 *                  the other, and neither orders alphabetically.
 *  - `value-asc` / `value-desc` — the money column. **The three DOCUMENT cards
 *                  only**, and see {@link VALUE_SORT_KEY}: the two `/companies`
 *                  cards render a Value column too and it holds a company STAGE,
 *                  not money.
 *  - `overdue-asc` / `overdue-desc` — the invoice due date. **The INVOICES card
 *                  alone** — `d_quotes` has no such column at all.
 *  - `task-asc` / `task-desc` — whether an open task touches the account.
 *                  **All five cards**, the only pair besides the A–Z one that
 *                  every endpoint here can express.
 *
 * 🔴 **THE LAST THREE PAIRS SHIPPED 2026-09-13 (backend, this session) AND THE
 * ENDPOINTS COULD NOT EXPRESS ANY OF THEM THE DAY BEFORE.** Three further
 * orderings the owner asked for were REFUSED rather than built, and they are
 * named here so nobody reads their absence as an oversight and "finishes" the
 * set: **your last note** (`latestNote` is VIEWER-SCOPED, so it returns the same
 * list in a different order to two people with a 200 and nothing on screen
 * saying why — which is precisely the property `task` was built NOT to have),
 * **awaiting signature** (DocuSign state is in no table this API reads), and
 * **projects of interest** (a SET, so there is no single value per row to order
 * by). The backend pins all three as refusals, so adding one later fails loudly
 * there rather than silently here.
 *
 * ⚠️ *SUPERSEDED 2026-10-09, the third refusal only:* backend `32057ed` orders
 * every set-valued column by its COUNT of distinct members, so projects of
 * interest, line projects, projects participated in and products purchased are
 * all sortable now. Your-last-note and awaiting-signature stay refused.
 *
 * ⚠️ Which of these a given card may offer is NOT a property of this union — it
 * is a property of the ENDPOINT, and it lives in `sortOptions` on each spec.
 * Reading this union as "any card can be sorted six ways" is the mistake
 * available here: `newest` is unreachable on the two `/companies` cards, the
 * status pair is unreachable on three of the five, and offering either where it
 * cannot be honoured would put a label over an order the server never applied.
 */
export type PartnershipSort =
  | 'activity'
  | 'newest'
  | 'az'
  | 'za'
  | 'status-asc'
  | 'status-desc'
  | 'country-asc'
  | 'country-desc'
  | 'source-asc'
  | 'source-desc'
  | 'company-type-asc'
  | 'company-type-desc'
  | 'value-asc'
  | 'value-desc'
  | 'overdue-asc'
  | 'overdue-desc'
  | 'task-asc'
  | 'task-desc'
  /*
   * The ACCOUNT's last human note, by its date — `/companies` `?sort=lastNoteAt`,
   * the two company cards alone (2026-10-02). Viewer-INDEPENDENT, unlike the
   * `note` column the three document cards keep, which is why this one may order
   * a list and that one may not.
   */
  | 'last-note-asc'
  | 'last-note-desc'
  /*
   * 2026-10-09 (backend `32057ed`): every filterable heading on this page now
   * orders server-side. `stage` is the Companies card's Value column (a company
   * STAGE, never money — so it is its own pair and not `value`); the four count
   * pairs order a SET by how many distinct members it holds, which is the one
   * single value per row a set has. `projectsParticipated` and
   * `productsPurchased` count within the card's cycle filter when one is set —
   * the server does that, not this module.
   */
  | 'stage-asc'
  | 'stage-desc'
  | 'interest-count-asc'
  | 'interest-count-desc'
  | 'participated-count-asc'
  | 'participated-count-desc'
  | 'purchased-count-asc'
  | 'purchased-count-desc'
  | 'line-projects-asc'
  | 'line-projects-desc'
  /*
   * 2026-10-27: the DEALS card's "Quote signed" column — `/deals`
   * `?sort=quoteSigned`, by the signed date; unsigned deals sort last.
   */
  | 'quote-signed-asc'
  | 'quote-signed-desc'
  /*
   * 2026-10-27: the "Account lead" column on all five cards — `sort=accountLead`
   * on all four endpoints, by the resolved lead's name, unassigned last.
   */
  | 'account-lead-asc'
  | 'account-lead-desc'
  /* Per-stage status columns — see the STAGE STATUS COLUMNS block below. */
  | StageStatusSort;

/*
 * === STAGE STATUS COLUMNS — the contract this module offers the page (2026-10-25) ===
 *
 * The two `/companies` cards render ONE column per company stage that has
 * statuses (`{Stage name} status`), but only for the stages the card holds.
 * Everything is matched by stage ID, never by name.
 *
 *  - WHICH STAGES: `PartnershipTableState.stageStatusStageIds` —
 *    `ReadonlySet<number> | null`. `null` = this card has no stage status columns
 *    (the three document cards). Leads = `{LEAD_COMPANY_STAGE_ID}` (a module
 *    constant). Companies = the active non-lead stage ids its own last request
 *    sent (the `nonLeadStageFilter` CSV), cached by value so the Set is
 *    referentially stable across reloads; an EMPTY set until the first load
 *    lands. Pass it straight to `useStageStatusColumns({ onlyStageIds, enabled:
 *    ids !== null })`.
 *  - WHERE: the slot `'stageStatuses'` in `extraColumns` (before the universal
 *    tail). Expand it with {@link expandStageStatusSlot}; the dynamic keys are
 *    {@link StageStatusColumnKey} = `stageStatus:<stageId>` (the same id as the
 *    saved-layout column and the export column). {@link PartnershipColumnKey} is
 *    the union of both. Guard with {@link isStageStatusColumnKey}.
 *  - ROW: `PartnershipRow.stageStatus` (`undefined` = older API / not read,
 *    `null` = no status, object = the status) beside `companyStage`.
 *  - FILTER: kind `'stageStatus'`, member `PartnershipFilter.stageStatus` — ONE
 *    shared id list for every column, sent as `?stageStatusId=` CSV to
 *    `/companies` on both company cards. Suggested URL suffix `StageStatus`
 *    (`?leadsStageStatus=`, `?companiesStageStatus=`). Use
 *    `columnSelection` / `replaceColumnSelection` from stage-status-columns.
 *    ⚠️ Put ONLY {@link stageStatusFilterForColumns}'s output in the filter
 *    passed to the hook (ids of the card's CURRENT columns; unchanged while the
 *    masters load), so a stale id is neither sent nor counted as active. The
 *    hook keys on the list's value, so a fresh array per render is safe.
 *  - SORT: dynamic orderings `stage-status-asc:<stageId>` /
 *    `stage-status-desc:<stageId>` ({@link StageStatusSort}), sent as
 *    `sort=stageStatus&sortStageId=<id>&order=…`. They are NOT in `sortOptions`
 *    (the dropdown); a card accepts them when `state.stageStatusSortable`. Use
 *    {@link stageStatusSortPair} for a column's asc/desc pair,
 *    {@link parseStageStatusSort} to read one, {@link partnershipSortLabel} to
 *    word any ordering. `resolvePartnershipSort` accepts them from the URL.
 *  - EXPORT: {@link stageStatusExportColumn} — the key IS the export id.
 */

/** A per-stage status ordering: `stage-status-asc:<stageId>` / `-desc:`. */
export type StageStatusSort = `stage-status-asc:${number}` | `stage-status-desc:${number}`;

/** Every ordering except the per-stage ones — what `sortOptions` lists. */
export type StaticPartnershipSort = Exclude<PartnershipSort, StageStatusSort>;

/** The dynamic column key for one stage — same as the saved-layout / export id. */
export type StageStatusColumnKey = `stageStatus:${number}`;

/** Any column key a card can render: a fixed column or a per-stage one. */
export type PartnershipColumnKey = Exclude<PartnershipColumn, 'stageStatuses'> | StageStatusColumnKey;

export function isStageStatusColumnKey(key: string): key is StageStatusColumnKey {
  return parseStageStatusColumnId(key) !== null;
}

/** The stage id of a `stageStatus:<id>` key, or `null`. */
export function stageIdOfColumnKey(key: string): number | null {
  return parseStageStatusColumnId(key);
}

/**
 * Replace the `'stageStatuses'` slot with one key per stage column, in the
 * columns' own order. No slot (a document card) leaves the list unchanged; an
 * empty column list removes the slot.
 */
export function expandStageStatusSlot(
  columns: readonly PartnershipColumn[],
  stageColumns: readonly Pick<StageStatusColumn, 'id' | 'stageId'>[],
): PartnershipColumnKey[] {
  const out: PartnershipColumnKey[] = [];
  for (const column of columns) {
    if (column === 'stageStatuses') {
      for (const stage of stageColumns) out.push(`${STAGE_STATUS_COLUMN_PREFIX}${stage.stageId}` as StageStatusColumnKey);
    } else {
      out.push(column);
    }
  }
  return out;
}

/** The `/companies/export` column id for a key — itself for a stage column. */
export function stageStatusExportColumn(key: string): string | null {
  return isStageStatusColumnKey(key) ? key : null;
}

/** The asc/desc ordering pair for one stage column. */
export function stageStatusSortPair(stageId: number): { asc: StageStatusSort; desc: StageStatusSort } {
  return { asc: `stage-status-asc:${stageId}`, desc: `stage-status-desc:${stageId}` };
}

/** Read a per-stage ordering, or `null` for anything else (bad ids included). */
export function parseStageStatusSort(
  sort: string | null,
): { stageId: number; ascending: boolean } | null {
  if (sort === null) return null;
  const match = /^stage-status-(asc|desc):([1-9]\d*)$/.exec(sort);
  if (!match) return null;
  const stageId = Number(match[2]);
  if (!Number.isSafeInteger(stageId)) return null;
  return { stageId, ascending: match[1] === 'asc' };
}

/**
 * The words for any ordering. A per-stage one needs the stage's name, from the
 * column (`StageStatusColumn.stageName`); without it the stage is not named.
 */
export function partnershipSortLabel(
  sort: PartnershipSort,
  stageNameOf?: (stageId: number) => string | null,
): string {
  const stage = parseStageStatusSort(sort);
  if (stage === null) return PARTNERSHIP_SORT_LABEL[sort as StaticPartnershipSort];
  const name = stageNameOf?.(stage.stageId) ?? null;
  const subject = name === null ? 'Stage status' : `${name} status`;
  return `${subject} order${stage.ascending ? '' : ', reversed'}`;
}

/**
 * What the control calls each ordering.
 *
 * ⚠️ **"Recent activity" is never "Last updated" and must not be relabelled as
 * one.** `latestActivityAt` is the greatest of the newest OPEN task, a note
 * inside a recent window, **and the record's own `updated_at`** — so completing
 * a task moves a record BACKWARDS or out of that term entirely, which "last
 * updated" cannot do. That one property is enough to make the relabel false,
 * and it is the half of this argument that survives.
 *
 * 🔴 **TWO OF THE THREE CLAUSES THIS PARAGRAPH USED TO MAKE ARE NOW FALSE, and
 * they were still here a day after the change that falsified them.**
 * ⚠️ *SUPERSEDED 2026-09-12: "a record with neither is unranked rather than
 * old. An edit to the record itself does not move it at all."* An edit is now
 * the third term (owner's instruction — an edit made on the dashboard must
 * re-rank the row), and because `updated_at` is `NOT NULL` on all four tables
 * **nothing is unranked any more**: `latest_activity_at` can never come back
 * NULL, so the "untouched rows last" bucket has effectively disappeared.
 *
 * ⛔ **AND ITS CONSEQUENCE IS A BEHAVIOUR CHANGE WITH NOTHING TO SEE: AN
 * UNTOUCHED RECORD NOW SORTS TO THE TOP, NOT THE BOTTOM.** Until anything
 * happens to it `updated_at` holds the moment it was CREATED, so a brand-new
 * account ranks above one somebody tasked last week. That follows from the
 * owner's rule — an edit lifts a row, and creation is the first edit — and is
 * not a fault to route around; `open-tasks.ts` says the same at its
 * `ACTIVITY_ORDER`. **Rows in a different sequence read as somebody having
 * entered records, never as a regression**, which is why it is written down
 * here rather than left to be noticed.
 *
 * ⚠️ **THE TWO ARMS HAVE DIFFERENT REACHES AND NO LABEL ON THE CONTROL CAN
 * EXPRESS IT.** A task or a note spreads across the WHOLE ACCOUNT — both
 * subqueries match on the company — where `updated_at` is the ROW's own column,
 * so an edit lifts only the record edited. One `GREATEST`, two scopes, one word
 * over both.
 *
 * ⚠️ **ON QUOTES AND INVOICES A XERO PUSH MOVES `updated_at`.** The push writes
 * the document number and the currency rate back onto the row, and those columns
 * carry `ON UPDATE CURRENT_TIMESTAMP` — so an integration can lift a document to
 * the top of "Recent activity" with nobody having touched it. **The owner
 * accepted that.** Do not report it as a fault, and do not let it tempt the
 * relabel the first paragraph forbids: it makes "Last updated" look MORE apt and
 * no more correct.
 *
 * ⚠️ **WHY IT SURVIVED, which is the part worth keeping.** This paragraph
 * exists to stop somebody relabelling the control, so it argues by
 * ENUMERATING NEGATIVES — three things this order is not. **An enumeration of
 * negatives is falsified by any ADDITION while still reading as the careful
 * note it was**: nothing about the prose looks stale, every sentence is
 * well-formed, and the one clause that still holds is doing the visible work.
 * Nothing typechecks a sentence, and the sort still behaved plausibly either
 * way. **When a term is added to a derivation, re-read every negative claim
 * about it** — they are the ones that rot silently.
 *
 * ⛔ **THE STATUS PAIR IS NOT "A–Z" AND MUST NEVER BE LABELLED AS ONE.**
 * `/quotes?sort=stage` and `/invoices?sort=status` order by the REFERENCE ROW,
 * which carries its own `sortOrder` — the pipeline sequence somebody arranged in
 * the master, which is the useful one and is not alphabetical. `src/lib/types.ts`
 * says so at both unions. Calling it A–Z makes a correct order look broken, and
 * the reader then reports the data rather than the label.
 *
 * ⚠️ **One label for two different masters, deliberately.** The quotes card
 * sorts a `/quote-stages` row and the invoices card an `/invoice-statuses` row,
 * and the COLUMN both sit under is already headed "Status" for exactly that
 * reason — see `COLUMN_LABEL` on the page. A per-card label would need this flat
 * map to become a function of the card, which buys one word and costs the single
 * place these orderings are named.
 */
export const PARTNERSHIP_SORT_LABEL: Record<StaticPartnershipSort, string> = {
  activity: 'Recent activity',
  newest: 'Newest first',
  az: 'A–Z',
  za: 'Z–A',
  'status-asc': 'Status order',
  'status-desc': 'Status order, reversed',
  /*
   * ✅ **The country pair IS alphabetical, unlike the status pair directly above
   * it, so it is labelled as such.** `?sort=country` on `/companies` orders on
   * `f_countries.country_name` — a real alphabet — where the status pair orders
   * by a reference row's own `sortOrder`, which is a pipeline sequence and is
   * why calling THAT one "A–Z" is forbidden two paragraphs up. The two look like
   * one pattern and are not: read the ordering, not the shape of the key.
   *
   * ⚠️ **The column is NAMED, where `az` / `za` are bare.** Bare A–Z is the
   * record column, the card's own subject; a second alphabet on the same card
   * has to say what it is alphabetising or the dropdown offers "A–Z" twice.
   */
  'country-asc': 'Country A–Z',
  'country-desc': 'Country Z–A',
  /*
   * 🔴 **THE SOURCE PAIR — 2026-09-14, and it CLOSES A GAP RATHER THAN ADDING A
   * CAPABILITY.** `?sort=source` has been a member of `CompanyListSort` since
   * the column shipped; the dashboard rendered the funnel and never the caret,
   * so a reader could narrow by source and not order by it while the wire had
   * supported both all along. **Owner's decision: fix it in the same change that
   * adds the type column.**
   *
   * ⚠️ **THE COLUMN IS NAMED, exactly as `country` above it is** — a second and
   * now a THIRD alphabet on one card, so a bare "A–Z" would appear three times in
   * one dropdown naming three different columns. `az` / `za` stay bare because
   * they are the record column, the card's own subject.
   *
   * ✅ **It IS alphabetical, so "A–Z" is honest here** — the backend orders
   * `src.name`, a real alphabet, and never `source_id`. That is the same
   * distinction `country` draws against the status pair above, which orders a
   * reference row's own `sortOrder` and may never be called A–Z.
   *
   * ⚠️ **`source_id` IS NULLABLE with no sentinel coalesced**, so "Source A–Z"
   * OPENS on the companies nobody has recorded a source for — and on this column
   * that is MOST of the table, which makes the first screen of an ascending sort
   * look like a broken column rather than an ordering. The empty cells are what
   * say so; this label cannot.
   */
  'source-asc': 'Source A–Z',
  'source-desc': 'Source Z–A',
  /*
   * 🔴 **THE COMPANY-TYPE PAIR — 2026-09-14, shipped with the column.**
   *
   * ⚠️ **"Company type", the full two words, where the COLUMN HEADING reads
   * "Company type" too and the `source` heading one column over is the bare
   * "Source".** The headings can differ from these labels because a heading sits
   * above its own column and a label sits in a list of six; here the qualifier is
   * what stops "Type A–Z" being read as a DEAL type or a quote type, neither of
   * which this card orders.
   *
   * ✅ **Alphabetical on the type's NAME (`ct.name`), never `company_type_id`** —
   * ordering by the id would produce a sequence that looks alphabetical at a
   * glance and is not, which reads as bad DATA rather than as the wrong column.
   * The same ruling `country` carries.
   *
   * ⚠️ **`company_type_id` IS NULLABLE with no sentinel**, so ascending opens on
   * the untyped companies — the majority state here, exactly as on `source`.
   */
  'company-type-asc': 'Company type A–Z',
  'company-type-desc': 'Company type Z–A',
  /*
   * ⚠️ **"Lowest first" / "Highest first", never "Value A–Z".** This is the one
   * ordering here whose direction words are not interchangeable with the
   * alphabet's: `asc` on a money column is the CHEAPEST work, and a reader who
   * reads "A–Z" over it and finds small numbers reports the data.
   *
   * 🔴 **ON DEALS IT ORDERS `COALESCE(lines_total, amount)` — the same figure
   * `dealValue()` renders, not the typed `amount` column.** The backend chose
   * the expression over the column deliberately: `?sort=amount` against
   * `d.amount` alone is a clean `200` that orders every deal carrying line items
   * by a number the page does not show. **A sort that answers 200 and orders by
   * an invisible figure is the quietest wrong answer this control could give.**
   *
   * ⚠️ **`asc` OPENS ON THE UNPRICED, on all three cards, and it is not the same
   * emptiness on each.** Deals are genuinely NULL (MariaDB sorts NULLs first
   * ascending, no sentinel); quotes and invoices carry `grand_total NOT NULL
   * DEFAULT 0.00`, so an unpriced document sorts as a real `0.00` and is
   * **indistinguishable from one priced at nothing**. Both are the ordering
   * rather than a fault. ⛔ A `NULLIF(…, 0)` "fix" is the forbidden sentinel
   * rule inverted, and the backend refuses it at both entries.
   */
  'value-asc': 'Value, lowest first',
  'value-desc': 'Value, highest first',
  /*
   * ⚠️ **INVOICES ONLY, and the label says "due" where the column heading says
   * "Overdue by".** They are one ordering read from two ends: `due_date` ascending
   * is the longest-overdue first, which is what somebody opening that column
   * wants. Naming the ordering after the heading would read as sorting a derived
   * age; naming it after the column says what the server actually did.
   *
   * 🔴 **`due_date` IS NULLABLE AND NO SENTINEL IS COALESCED, so "soonest first"
   * OPENS ON THE UNDATED INVOICES — and under a heading reading "Overdue by"
   * they LOOK LIKE THE MOST OVERDUE ROWS.** The backend named that cost rather
   * than papering over it, on the same ruling as `?sort=startDate` on
   * `/projects`: a sentinel does not move an undated row out of the way, it
   * files it under a day it is not due on. **The empty cell is what has to say
   * so** — this label cannot.
   *
   * ⚠️ **`dueDate` is a DIFFERENT COLUMN from `billDate`**, which `/invoices`
   * has sorted by since 2026-09-02. Bill date is when the invoice was raised;
   * due date is when it must be paid. This is an ADDITION and repointing one at
   * the other would be a silent change to what a saved link means.
   */
  'overdue-asc': 'Due soonest first',
  'overdue-desc': 'Due latest first',
  /*
   * ⚠️ **"Open task" is the heading's word and must stay it.** The flag tests
   * whether an open task EXISTS on the account — it looks at no due date at all,
   * so a task three weeks overdue and one with no date read alike. A label
   * reading "Upcoming" or "Due" promises a future date nothing behind the column
   * has consulted, and a reader planning their week off it would skip exactly
   * the accounts carrying the oldest unfinished work.
   *
   * ⚠️ **The flag is ACCOUNT-LEVEL in both directions** — the SQL spreads across
   * the company and its deals, quotes, invoices and deliverables — so ordering a
   * quotes card by it groups by *the account having something open*, not by that
   * quote. No label of a sensible length can say that; the column's own per-row
   * `title` does.
   *
   * ✅ **It is VIEWER-INDEPENDENT, which is the whole reason this pair could be
   * built where a `note` ordering could not.** Both sit one row apart in the same
   * dropdown and only one of them returns the same list to two people.
   */
  'task-asc': 'Open task last',
  'task-desc': 'Open task first',
  /*
   * ⚠️ **An account with NO note sorts LAST in BOTH directions** — the server
   * puts an explicit `IS NULL` term first, unlike every other nullable sort on
   * this card. So "oldest first" opens on the oldest REAL note, not on the
   * accounts nobody has written on.
   */
  'last-note-asc': 'Last note, oldest first',
  'last-note-desc': 'Last note, newest first',
  /*
   * ⚠️ **"Stage order", never "A–Z"** — the same ruling as the status pair: a
   * company stage orders by the master's own `sortOrder`, which is a pipeline
   * sequence and not an alphabet.
   */
  'stage-asc': 'Stage order',
  'stage-desc': 'Stage order, reversed',
  /*
   * ⚠️ **"Fewest" / "most", never "A–Z"** — a set has no alphabet, and these
   * order by the COUNT of distinct members, so the label names a count.
   */
  'interest-count-asc': 'Fewest projects of interest first',
  'interest-count-desc': 'Most projects of interest first',
  'participated-count-asc': 'Fewest participating projects first',
  'participated-count-desc': 'Most participating projects first',
  'purchased-count-asc': 'Fewest products purchased first',
  'purchased-count-desc': 'Most products purchased first',
  'line-projects-asc': 'Fewest projects on the document first',
  'line-projects-desc': 'Most projects on the document first',
  'quote-signed-asc': 'Quote signed, earliest first',
  'quote-signed-desc': 'Quote signed, latest first',
  'account-lead-asc': 'Account lead A–Z',
  'account-lead-desc': 'Account lead Z–A',
};

/**
 * What every card starts on, and what an unusable choice falls back to.
 *
 * `activity` because it is the only ordering all five endpoints can express, so
 * the default needs no per-card branch — and because it is what all five sent
 * before this control existed, so adding the control changed nobody's page.
 */
const DEFAULT_SORT: PartnershipSort = 'activity';

/**
 * Resolve a raw value — a query-string param, typed by anybody — to an ordering
 * this card can actually honour.
 *
 * ⚠️ **Unsupported falls back SILENTLY to the default, and that is deliberate.**
 * `?dealsSort=az` names an ordering `/deals` cannot express; rendering "A–Z" as
 * the selected option while sending `activityFirst` would be the control lying
 * about itself, which is worse than quietly showing the order actually applied.
 * The control then reads back the resolved value, so the selection on screen is
 * always the order that was requested.
 */
export function resolvePartnershipSort(
  key: PartnershipTableKey,
  raw: string | null,
): PartnershipSort {
  const options = PARTNERSHIP_SORT_OPTIONS[key];
  const fixed = options.find((option) => option === raw);
  if (fixed !== undefined) return fixed;
  // A per-stage status ordering, on a card that renders stage status columns.
  const stage = SPEC[key].stageStatusScope === null ? null : parseStageStatusSort(raw);
  if (stage !== null) {
    const pair = stageStatusSortPair(stage.stageId);
    return stage.ascending ? pair.asc : pair.desc;
  }
  return DEFAULT_SORT;
}

/** Whether a resolved ordering is the one the URL leaves unwritten. */
export function isDefaultPartnershipSort(sort: PartnershipSort): boolean {
  return sort === DEFAULT_SORT;
}

/** One chosen ordering per card. */
export type PartnershipSorts = Record<PartnershipTableKey, PartnershipSort>;

/* --- how a card is narrowed ----------------------------------------------- */

/**
 * A filter a card can offer. Owner's instruction, 2026-09-03: *"add filters on
 * each card for status, country, etc"*.
 *
 * 🔴 **WHICH CARD OFFERS WHICH IS A PROPERTY OF THE ENDPOINT, NOT A PREFERENCE
 * — and the two do not overlap on any card, for two different reasons.**
 *
 *  - **`status`** is `stageId` on `/deals` and `/quotes` and **`statusId`** on
 *    `/invoices` — three different columns on three tables, which is the same
 *    reason {@link STATUS_SORT_KEY} is a different word on each. It is offered
 *    on those three and on neither `/companies` card.
 *  - **`country`** is `countryId` on `/companies` and exists on no other list
 *    schema here (measured against the deployed schemas, 2026-09-03:
 *    `/deals`, `/quotes` and `/invoices` declare no such key, and all three are
 *    `.strict()`, so sending one is a **422 naming it**).
 *
 *  - **`source`** is `sourceId` on `/companies` and, like `country`, exists on
 *    no other list schema here. Added 2026-09-14 beside `country`, on the same
 *    endpoint, for the same reason: it is a column on `d_companies` and on no
 *    document table.
 *  - **`companyType`** is **`companyTypeId`** on `/companies` — the third member
 *    of that family and the third key on the same endpoint. ⚠️ **`companyTypeId`,
 *    NOT `typeId`**: `src/lib/types.ts` states the long spelling at
 *    {@link Company.companyType}, and the schema is `.strict()`, so the short one
 *    is a 422 naming the key rather than a no-op.
 *
 * ⛔ **THE TWO `/companies` CARDS MUST NEVER OFFER A STATUS FILTER, AND THAT IS
 * NOT AN OMISSION — IT IS THE PARTITION.** Those two cards already OWN
 * `stageId`: Leads sends {@link LEAD_COMPANY_STAGE_ID} and Companies sends the
 * derived complement of it (see `nonLeadStageFilter`). A second writer of that
 * one key would either be overwritten silently or would break the partition and
 * put the same company on both cards — which is the exact duplication the split
 * exists to remove, and it looks completely normal on screen.
 *
 * ⚠️ **`country`, `source` and `companyType` all overlap on the two `/companies`
 * cards**, and they are safe together for the reason the partition note gives
 * above: they are INDEPENDENT KEYS on the wire (`countryId` AND `sourceId` AND
 * `companyTypeId`), so none can overwrite another and the server ANDs them. The
 * rule this union states is not "one filter per card" — it is that no two
 * filters may write one key.
 */
/*
 * 2026-10-09 (backend `32057ed`): `participatedCycle` and `purchasedCycle` are
 * `/companies` `?participatedCycleId=` and `?purchasedCycleId=` — CSV cycle ids,
 * companies-only, independent keys beside the three above. Offered on the
 * COMPANIES card alone, the only card rendering the two purchase-history
 * columns they sit under.
 */
export type PartnershipFilterKind =
  | 'status'
  | 'country'
  | 'source'
  | 'companyType'
  | 'participatedCycle'
  | 'purchasedCycle'
  /*
   * 2026-10-25: the per-stage status columns' ONE shared list — `/companies`
   * `?stageStatusId=` CSV, both company cards. Each column's funnel edits only
   * its own stage's slice (`columnSelection` / `replaceColumnSelection`).
   */
  | 'stageStatus';

/**
 * One card's chosen filters. **`null` is "not filtered", never "filtered to
 * nothing"** — the key is omitted from the request entirely, because an empty
 * value is a 422 on these strict schemas rather than "no filter".
 *
 * Both members exist on every card; a card renders and honours only the ones in
 * its own {@link TableSpec.filterKinds}. A value sitting in the other member is
 * inert rather than dangerous: `load` reads the key it declares and nothing
 * else, so `?leadsStatus=3` typed by hand narrows nothing instead of 422ing.
 */
export interface PartnershipFilter {
  /**
   * Deal stage, quote stage or invoice status ids. **Empty is "not filtered".**
   *
   * ✅ 2026-10-28: any number now reaches the server as a CSV — see
   * `statusIdList`. ⚠️ *SUPERSEDED below:*
   *
   * 🔴 **SEVERAL IDS ARE EXPRESSIBLE HERE AND THE WIRE CANNOT CARRY THEM.**
   * `?stageId=1,2` is a **422 "expected number, received NaN"** on `/deals`,
   * `/quotes` and `/invoices` alike — CSV filters are `/companies`-only, which
   * this file already states at {@link PartnershipFilterKind}. So
   * {@link stageParams} and {@link statusParams} send the key **only when
   * exactly one id is chosen**, and the CARD narrows the loaded rows itself for
   * anything more.
   *
   * ⚠️ **That split is not a workaround, it is what every other funnel in this
   * app does** — `use-table-filters.ts` documents the identical fallback for a
   * server column that is not `multi`, and `ColumnFilter`'s `scopeNote` exists
   * to say which of the two is happening. What must never happen is sending one
   * of the ticked ids and drawing the funnel as though all of them applied.
   */
  status: readonly number[];
  /**
   * `f_countries` ids — the company's country OF RECORD. **Empty is "not
   * filtered".**
   *
   * ✅ **Any number of these reaches the server**, because `/companies` is the
   * one endpoint in this API that parses a comma-separated filter, and
   * `countryId` is CSV there. So this member has no page-scoped fallback and
   * needs none — see {@link countryParams}.
   */
  country: readonly number[];
  /**
   * `f_company_sources` ids — where the account came from. **Empty is "not
   * filtered".**
   *
   * ✅ **Any number of these reaches the server**, for the same reason
   * {@link PartnershipFilter.country} does: `/companies` is the one endpoint in
   * this API that parses a comma-separated filter, and `sourceId` is CSV there.
   * So this member has no page-scoped fallback either — see {@link sourceParams}.
   *
   * ⚠️ **There is no way to ask for "no source recorded", and on THIS column
   * that bites harder than it does on country.** The server renders
   * `source_id IN (...)`, which is UNKNOWN for a NULL — and the source column is
   * OPTIONAL and blank on every company that predates it, so filtering by any
   * source hides the majority of the table. That is the contract rather than a
   * fault; it is stated here because the ratio makes it look like a broken
   * filter.
   */
  source: readonly number[];
  /**
   * `f_company_types` ids — what KIND of institution the account is. **Empty is
   * "not filtered".**
   *
   * ✅ **Any number of these reaches the server**, for the third time on the same
   * endpoint and the same reason: `/companies` is the one list in this API that
   * parses a comma-separated filter, and `companyTypeId` is declared
   * `IdListFilter` there. So this member has no page-scoped fallback either — see
   * {@link companyTypeParams}.
   *
   * ⚠️ **The wire key is `companyTypeId`, not `typeId`**, and the SORT beside it
   * is `?sort=companyType` with no `Id` at all — three spellings of one idea, of
   * which two are wire keys that differ by two characters. `src/lib/types.ts`
   * states both at {@link CompanyListSort}; the schema is `.strict()`, so the
   * wrong one takes the whole card out with a 422 rather than narrowing nothing.
   *
   * ⚠️ **`companyTypeId IN (...)` is UNKNOWN for a NULL, exactly as
   * {@link PartnershipFilter.source} records** — the column is optional, so a
   * company nobody has typed is unreachable by any value of this filter. The
   * funnel's Is-empty test is the only way to those rows.
   */
  companyType: readonly number[];
  /**
   * `/cycles` ids — companies that took part in a project in any of these
   * cycles, and companies that bought in any of these cycles. **Empty is "not
   * filtered".** CSV on `/companies`, so any number reaches the server and
   * there is no page-scoped fallback. The two COUNT sorts on those columns are
   * scoped to these cycles server-side when a filter is set.
   */
  participatedCycle: readonly number[];
  purchasedCycle: readonly number[];
  /**
   * `f_company_stage_statuses` ids, across every stage column — **empty is "not
   * filtered"**. CSV `stageStatusId` on `/companies`; several ids are OR'd by
   * the server (a company is in one stage only).
   */
  stageStatus: readonly number[];
  /**
   * The ACCOUNT's last human note, an inclusive `YYYY-MM-DD` date range —
   * `/companies` `?lastNoteAfter=` / `?lastNoteBefore=`, the two company cards
   * alone (2026-10-02). **`null` is "no bound"**, never an empty string, and
   * the key is then omitted from the request: an empty value is a 422 on a
   * `.strict()` schema. A company with NO human note matches neither bound.
   *
   * ⚠️ **A DATE, not an id list, so it is not a {@link PartnershipFilterKind}**
   * and has no master and no `FILTER_PARAM_SUFFIX` row — the page reads and
   * writes it through its own pair of URL keys.
   */
  lastNoteAfter: string | null;
  lastNoteBefore: string | null;
}

/** One chosen filter set per card. */
export type PartnershipFilters = Record<PartnershipTableKey, PartnershipFilter>;

/**
 * Nothing narrowed. The state every card starts in.
 *
 * ⚠️ **A shared frozen instance, and every consumer REPLACES rather than
 * mutates** — the same contract `EMPTY_FILTER` in `ColumnFilter` states about
 * itself. `Object.freeze` is what makes a `push` onto one of these arrays a
 * runtime error in development instead of five cards quietly sharing one
 * mutated filter.
 */
export const NO_PARTNERSHIP_FILTER: PartnershipFilter = Object.freeze({
  status: Object.freeze([]),
  country: Object.freeze([]),
  source: Object.freeze([]),
  companyType: Object.freeze([]),
  participatedCycle: Object.freeze([]),
  purchasedCycle: Object.freeze([]),
  stageStatus: Object.freeze([]),
  lastNoteAfter: null,
  lastNoteBefore: null,
});

/**
 * Read a raw query-string value as a `YYYY-MM-DD` bound, or `null`.
 *
 * ⚠️ **A malformed or impossible date reads as ABSENT**, for the reason
 * {@link resolvePartnershipFilterIds} gives for a bad id: sent as-is it is a
 * 422 naming the key, which takes one card out beside four that look perfect.
 * Calendar-checked from the string's own parts — never through a `Date`
 * constructed from it, which would shift a day west of Greenwich.
 */
export function resolvePartnershipDateBound(raw: string | null): string | null {
  if (raw === null) return null;
  const text = raw.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1) return null;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return day <= daysInMonth ? text : null;
}

/**
 * Read a raw query-string value as a filter id.
 *
 * ⚠️ **Anything that is not a positive safe integer reads as ABSENT, never as
 * zero.** `Number('')` is `0` and `Number('abc')` is `NaN`; either sent as an id
 * is a 422 naming the key, which surfaces as one card failing to load beside
 * four that look perfect — so it reads as a flaky endpoint rather than as a
 * typo in a URL somebody shared.
 */
export function resolvePartnershipFilterIds(raw: string | null): number[] {
  if (raw === null || raw.trim() === '') return [];
  const ids = raw.split(',').map((part) => Number(part.trim()));
  /*
   * 🔴 **ONE BAD MEMBER DROPS THE WHOLE VALUE — it is never skipped, and this
   * is the same ruling `CompaniesPage` states for its own `stageId` parser.**
   * A half-applied filter is worse than an unapplied one: `?leadsCountry=82,xx`
   * would otherwise narrow to Italy while the funnel drew two ticks, and the
   * rows would look like the whole answer. Dropping everything renders an
   * unfiltered card with an empty funnel, which is visibly not what the URL
   * said.
   */
  if (ids.some((id) => !Number.isSafeInteger(id) || id <= 0)) return [];
  // De-duplicated, because `?leadsCountry=82,82` must not send `82,82` to a
  // server that would count it twice in an `IN (...)`.
  return Array.from(new Set(ids));
}

/**
 * Whether anything on this card is narrowed — for the "clear" affordance.
 *
 * ⚠️ **EVERY member is tested, including on cards that offer none of them.**
 * Nothing typechecks this against {@link PartnershipFilter}, so a member added
 * without a clause here leaves a card narrowed with no way on screen to clear
 * it — the invisible active filter this app refuses everywhere. **Add the clause
 * in the same edit as the member.**
 */
export function hasPartnershipFilter(filter: PartnershipFilter): boolean {
  return (
    filter.status.length > 0 ||
    filter.country.length > 0 ||
    filter.source.length > 0 ||
    filter.companyType.length > 0 ||
    filter.participatedCycle.length > 0 ||
    filter.purchasedCycle.length > 0 ||
    filter.stageStatus.length > 0 ||
    filter.lastNoteAfter !== null ||
    filter.lastNoteBefore !== null
  );
}

/**
 * Which create modal a card's **+** button opens.
 *
 * ⚠️ **The two `/companies` cards share `'company'`, and the Leads card is the
 * one worth stating out loud: creating from it opens the ordinary company form,
 * whose STAGE field the person chooses.** So a company created there does not
 * necessarily land on the Leads card — it lands on whichever of the two cards
 * its stage puts it on. That is the form's contract and this button does not
 * override it; the alternative would be seeding a stage the person did not pick,
 * on a master whose ids are a measurement with an expiry (see
 * {@link LEAD_COMPANY_STAGE_ID}).
 */
export type PartnershipCreate = 'company' | 'deal' | 'quote' | 'invoice';

/**
 * A quote's stage or an invoice's status, carried to the page UNRESOLVED.
 *
 * ⚠️ **`ref` is the server's own `{id, name}` and carries NO FLAGS** — see the
 * module header. The `kind` says which master to resolve it against; nothing in
 * this module does the resolving, and nothing in this module may match on
 * `ref.name`.
 *
 * ⚠️ **`null` on leads, companies and deals is the ABSENCE of this concept, not
 * a missing value.** A company's pipeline stage already renders in the value
 * column under its own `Stage` heading, and a deal's stage is that column's
 * fallback; neither is a quote stage or an invoice status and neither has the
 * flags these two badges read. Widening this to carry them would put three
 * different masters behind one badge.
 */
export type PartnershipRowStatus =
  | { kind: 'quote-stage'; ref: MasterRef }
  | { kind: 'invoice-status'; ref: MasterRef };

/**
 * A column a card may render between the record column and the value column.
 *
 *  - `status`    — the quote's stage or the invoice's status, from
 *                  {@link PartnershipRow.status}.
 *  - `signature` — the DocuSign envelope state of a quote.
 *  - `overdue`   — how far past {@link PartnershipRow.dueDate} an invoice is.
 *  - `projectOfInterest`
 *                — {@link PartnershipRow.projectsOfInterest}, the projects the
 *                  row's ACCOUNT is being pursued for. ⚠️ **The two COMPANY
 *                  cards, and since 2026-09-14 only those** — see below. It was
 *                  on all five for two days.
 *  - `lineProjects`
 *                — {@link PartnershipRow.lineProjects}, the projects THIS
 *                  DOCUMENT's own line items name. **The three document cards
 *                  only**, where it replaced `projectOfInterest`. ⛔ The two are
 *                  different questions about different records and neither is a
 *                  fallback for the other.
 *  - `upcomingTask`
 *                — {@link PartnershipRow.hasOpenTask}, whether an open task
 *                  stands against this ACCOUNT. On all five cards.
 *  - `note`      — {@link PartnershipRow.latestNote}, the reader's own last note.
 *
 * ⚠️ **The column key is SINGULAR and the row field is PLURAL, and that is not a
 * typo to tidy.** The key is `projectOfInterest` because it is the column's name;
 * the field is `projectsOfInterest` because it is the same word `Company` spells,
 * and this contract has already been burnt once by a field carrying a second
 * spelling of one fact (`countryName` versus `name`, recorded on
 * {@link PartnershipRow.country}). A renderer reaching for `row.projectOfInterest`
 * gets a `tsc` error, which is the loud failure; a second spelling on the wire
 * side would have printed `undefined`.
 *
 * 🔴 **WHICH CARD GETS WHICH IS A PROPERTY OF THE ENDPOINT'S RECORD TYPE, NOT A
 * PREFERENCE, AND IT MUST NOT BE READ AS ONE.** Only a quote and an invoice
 * carry a quote stage or an invoice status **at all** — a company's stage and a
 * deal's stage are two OTHER masters with two other flag sets, which is exactly
 * what {@link PartnershipRowStatus} already refuses to widen for. Only a quote
 * can have a signature envelope; only an invoice has a due date. So a `status`
 * column on the deals card would have nothing to render, and an `overdue`
 * column on any of the other four would be a heading over a permanently empty
 * cell — which reads as missing data rather than as a missing concept.
 *
 * ⚠️ *SUPERSEDED 2026-09-12: "**`note` IS THE FIRST MEMBER THAT IS ON ALL FIVE
 * CARDS, and it is not an exception to the rule above — it satisfies it.** All
 * four endpoints return `latestNote`, so every one of these records really has
 * one, which is exactly the test the other three fail."* **Every word of the
 * TEST it states still binds** — what has moved, twice, is how many members pass
 * it.
 *
 * 🔴 **TWO UNIVERSAL MEMBERS, NOT THREE — 2026-09-14.** ⚠️ *SUPERSEDED, written
 * two days earlier: "**There are THREE universal members now** — `note`,
 * `projectOfInterest` and `upcomingTask` … `projectsOfInterest` reaches all four
 * through the company embed, which is the whole argument and is set out
 * below."* `projectOfInterest` left the three document cards on the owner's
 * decision, so the universal set is `upcomingTask` and `note` again. ⚠️ **The
 * argument that put it there was never wrong — it was answered.** The account's
 * list really does reach a document row through the embed; the owner decided a
 * document card should be answering a different question, which no fact about
 * the wire could have settled either way. **A column passing the
 * does-this-record-carry-the-concept test is NECESSARY and was never
 * sufficient.**
 *
 * 🔴 **THE WIDTH COST IS REAL, IT IS CUMULATIVE, AND THIS MODULE DOES NOT PAY
 * IT.** *SUPERSEDED, as a one-off: "a further width means the THREE cards that
 * had no minimum at all — leads, companies and deals — now declare one and
 * scroll sideways on a narrow viewport, and their action column is pinned for
 * it."* That happened, and it happens again: **two more universal columns widen
 * all five cards at once**, so the three that already scroll sideways scroll
 * further and the two document cards that were widest now carry six extra
 * columns apiece. ⚠️ **The widths are NOT decided here** — `COLUMN_REM`,
 * `COLUMN_LABEL` and `tableMinWidthRem` are the page's, and a member added to
 * this union is a `tsc` error there until the page gives it a width and a
 * heading. That compiler-enforced pairing is the only thing standing between a
 * new column and a table that silently mis-measures itself, so ⛔ **do not add a
 * member here and satisfy the page's maps with a placeholder**: the cost is
 * supposed to be felt at the moment the column is declared.
 *
 * ⚠️ **`country` IS THE COMPANY CARDS' OWN COLUMN AND SATISFIES THE RULE THE
 * SAME WAY — owner's instruction, 2026-09-03.** Only a company carries a country
 * of record: `d_companies.country_id` is a real foreign key and no deal, quote
 * or invoice has one, so it is declared on leads and companies and on neither of
 * the other three. A `country` column on the deals card would be a heading over
 * a permanently empty cell.
 *
 * ⛔ **It is {@link Company.country} — the FOREIGN KEY — and never
 * `billingCountry`, the free-text line inside the postal address.** The two are
 * separate fields that will disagree, and `src/lib/types.ts` says at
 * `CompanyListParams.countryId` that a UI must never hang the country FILTER off
 * a column showing the free-text one: it would narrow on one field while
 * displaying another. The filter and this column read the same field, which is
 * what makes them safe to sit on one card.
 *
 * ⚠️ **`source` IS THE COMPANY CARDS' THIRD OWN COLUMN AND SATISFIES THE RULE
 * THE SAME WAY — owner's instruction, 2026-09-14.** Only a company carries a
 * source: `d_companies.source_id` is a real foreign key into
 * `f_company_sources`, and no deal, quote or invoice has one — not even through
 * the `company` embed, which relays the account's id, name and projects of
 * interest and nothing else. So it is declared on leads and companies and on
 * neither of the other three, exactly as `country` is.
 *
 * ⛔ **IT IS NOT {@link Contact.leadSourceRef}.** That is `d_lead_sources`, a
 * DIFFERENT master on a DIFFERENT record, and both are `{id, name}` — so a
 * renderer handed the wrong one compiles and prints a plausible word. Where a
 * deal came from and where an ACCOUNT came from are two facts, and this column
 * is the second.
 *
 * ⚠️ **BLANK IS THE ORDINARY CELL HERE, not a rarity.** The column is optional
 * and every company that predates it has none, so a card reading as an empty
 * Source column down every row is THE DATA rather than a failed read — the same
 * ruling `travel` on `/client-deliverables` carries. ⛔ Do not write empty-state
 * copy implying the values are on their way.
 *
 * 🔴 **`projectOfInterest` IS BACK TO THE TWO COMPANY CARDS — 2026-09-14, OWNER'S
 * DECISION, AND THE WHOLE SECTION BELOW IS KEPT BECAUSE ITS REASONING IS STILL
 * WHAT LICENSES THE COLUMN THAT REPLACED IT.**
 *
 * *SUPERSEDED, the heading and every clause about which cards render it:
 * "**`projectOfInterest` IS ON ALL FIVE CARDS SINCE 2026-09-12, AND THE RULE
 * ABOVE IS WHAT PERMITS IT RATHER THAN WHAT IT BREAKS.**"* The three document
 * cards render {@link PartnershipRow.lineProjects} instead — the projects THIS
 * DOCUMENT's own line items name — and the reasoning for that is below, after
 * this block.
 *
 * ⚠️ **Read the block that follows as an argument about a COLUMN'S CONCEPT, not
 * as a description of today's cards.** It establishes that the account's list
 * genuinely reaches a document row, that `null` there means *nobody to have any*,
 * and that the test is about the COLUMN rather than a ROW — all three still true
 * and all three still load-bearing. What it cannot establish, and never claimed
 * to, is that the account's list is the question a DOCUMENT card should ask.
 *
 * 🔴 **THE PARAGRAPH THAT TURNED OUT TO BE THE WARNING IS THE ONE READING "⛔ IT
 * IS THE ACCOUNT'S LIST AND NEVER THIS DOCUMENT'S SCOPE".** It named the exact
 * misreading the column invited, judged it acceptable because the cell did not
 * *say* the document was for those projects — and the owner read the cards and
 * decided the invitation was the problem. **A misreading a column can only be
 * defended against in prose is a misreading the column is making.**
 *
 * ⚠️ *SUPERSEDED: "**`projectOfInterest` IS THE COMPANY CARDS' SECOND OWN COLUMN
 * AND SATISFIES THE RULE THE SAME WAY.** Only a company carries projects of
 * interest; no deal, quote or invoice has the field, so it is declared on leads
 * and companies and on neither of the other three."* The first clause was a
 * measurement of the WIRE and it moved: `/deals`, `/quotes` and `/invoices` now
 * carry {@link DocumentCompanyRef} on their `company` embed, which carries the
 * array. ⛔ **Do not read the supersession as the rule being relaxed for a column
 * somebody wanted on more cards** — it is re-derived below, and it would fail if
 * the concept were the DOCUMENT's.
 *
 * **The concept is the ACCOUNT's, and that is the whole re-derivation.** A deal
 * does not have projects of interest; the COMPANY it belongs to does, and the
 * deal reaches them through its company. So on the three document cards this
 * column answers *"what is this account being pursued for"*, which every row that
 * names a company really has — and a row that names NO company has **nobody to
 * have any**, which is {@link PartnershipRow.projectsOfInterest} `=== null` and
 * an empty cell, exactly as this rule already prescribes for an absent concept.
 *
 * ⚠️ **THE TEST IS ABOUT THE COLUMN, NOT ABOUT A ROW, and conflating the two is
 * how this rule gets misapplied in both directions.** What it forbids is a
 * heading whose cell is empty on EVERY row of a card, for ever — `overdue` on a
 * card with no due dates, `country` on a card whose records have no country of
 * record. It has never forbidden a column that is empty on SOME rows: `note` is
 * empty on every record the reader has not written on, and `status` is empty on a
 * document nobody has staged. `company_id` is nullable on all three document
 * tables, so a companyless row is the ordinary sparse cell and not a permanently
 * empty column.
 *
 * ⛔ **IT IS THE ACCOUNT'S LIST AND NEVER THIS DOCUMENT'S SCOPE**, and on the
 * three document cards that misreading is the one the shape invites.
 * {@link DocumentCompanyRef} says so at the field: nothing here claims the deal
 * in front of you is *for* any of these projects — a line item's own project
 * answers that, and the two routinely disagree. Rendering it on a document as
 * though it described the document produces a confident wrong answer rather than
 * a blank, which is the failure this module cannot detect and the page must not
 * write.
 *
 * ⛔ **STILL NOT A SECOND REQUEST, AND NOTHING HERE MAY MAKE IT ONE — the reach
 * got WIDER, so this constraint got MORE load-bearing, not less.** It rides on
 * the `company` embed the three list endpoints already return, exactly as it
 * rides on the company rows the two company cards already fetch. A per-row
 * lookup would now be up to five cards × {@link PER_PAGE} rows, each a round
 * trip for a field the row it sits on already carries.
 *
 * ⚠️ *SUPERSEDED, the arithmetic and NOT the rule: "against a 120-per-60s
 * per-IP budget shared across the office."* The budget is **12,000/60s**
 * (corrected 2026-09-17) — a hundred times what this file assumed, and wide
 * enough that five cards × {@link PER_PAGE} rows would fit inside it.
 * ⛔ **THE PROHIBITION IS UNCHANGED, BECAUSE IT NEVER RESTED ON THE CEILING: a
 * per-row lookup on a PAGINATED list is the wrong SHAPE.** It is N round trips
 * for a field the page was already handed, and it fails a row at a time — so
 * the cell renders blank on whichever rows lost, which is indistinguishable
 * from a company with no projects of interest. A wider budget makes that
 * failure quieter, not rarer. **Do not read the correction as permission.**
 *
 * 🔴 **`lineProjects` IS THE THREE DOCUMENT CARDS' OWN COLUMN AND SATISFIES THE
 * RULE THE SAME WAY — owner's decision, 2026-09-14.** Only a deal, a quote or an
 * invoice has LINE ITEMS, so only those three can name projects through them: a
 * `lineProjects` column on a company card would be a heading over a permanently
 * empty cell, which is exactly what {@link PartnershipRow.lineProjects} is
 * `null` for there.
 *
 * ⛔ **IT IS THIS DOCUMENT'S OWN ANSWER AND NEVER ITS ACCOUNT'S**, which is the
 * whole point of it existing and the one thing to keep straight when reading the
 * superseded block above. *What is this deal for* and *what is this account being
 * chased for* are two questions, they routinely disagree, and the second is the
 * one these three cards used to answer.
 *
 * ⚠️ **THE TWO COLUMNS ARE INTERCHANGEABLE IN CODE AND NOT IN MEANING.** Both
 * fields are `readonly PartnershipRowProject[] | null`, both render through the
 * same label helper, and both sit in the same 12rem slot — so **swapping them
 * compiles and prints a plausible list under the wrong heading.** Nothing
 * typechecks which question a column asks; the field names and the two heading
 * constants in `company-projects.ts` are the only guard, which is why that module
 * now deliberately owns two.
 *
 * ⛔ **NEITHER IS A FALLBACK FOR THE OTHER.** A document whose lines name no
 * project must NOT quietly show its account's projects of interest — that is the
 * superseded rendering restored under a heading that now denies it. `[]` is the
 * honest answer, and it is the MAJORITY one: **measured, 613 of 1,088 documents
 * name no project on any line.** An empty column down most of a card is THE DATA
 * here, the same ruling `travel` on `/client-deliverables` carries, and ⛔ no
 * empty-state copy may imply the values are on their way.
 *
 * ⛔ **STILL NOT A SECOND REQUEST.** It rides on rows the three document cards
 * already fetch, exactly as the account's list did. A per-row lookup would be up
 * to three cards × {@link PER_PAGE} rows against the same shared budget — and
 * there is no per-document projects endpoint to call even if it were affordable.
 *
 * ⚠️ **There is deliberately NO `lineProjects` FILTER and NO `lineProjects`
 * ORDERING, and neither is an omission.** `?projectOfInterestId=` is refused on
 * all four endpoints and an ordering by a SET was refused with it — a set has no
 * single value per row to order by, and that argument is unchanged by the set
 * belonging to the document rather than to the account. ⚠️ **`?lineProjectId=` on
 * `/quotes` and `/invoices` is a real filter and is NOT this**: it narrows which
 * DOCUMENTS come back, where this column reports what each one names. Do not wire
 * one to the other.
 *
 * ⚠️ *SUPERSEDED 2026-10-09, the ORDERING half only:* backend `32057ed` orders
 * `lineProjects` (and `projectsOfInterest`) by the COUNT of distinct members —
 * one value per row — so both columns sort now. The FILTER half stands.
 */
export type PartnershipColumn =
  | 'status'
  | 'signature'
  | 'overdue'
  | 'country'
  | 'source'
  | 'companyType'
  | 'projectOfInterest'
  | 'lineProjects'
  /*
   * The deal's signed quote — the DEALS card only (owner 2026-10-27), from
   * {@link PartnershipRow.quoteSigned}. A deal has linked quotes; no company,
   * quote or invoice row carries this field.
   */
  | 'quoteSigned'
  /*
   * The company's credit balance, one figure per currency — the COMPANIES card
   * only (owner's decision 2026-09-24), declared by `COMPANIES_CARD_COLUMNS`.
   * Read-only; no server filter or sort exists for it.
   */
  | 'creditBalance'
  /*
   * What this company has BOUGHT and the projects it has TAKEN PART IN, across
   * every cycle — the COMPANIES card only, read-only, from
   * {@link PartnershipRow.purchaseHistory}. Neither has a server filter or sort;
   * both funnels are page-scoped over the set, like `projectOfInterest`.
   */
  | 'productsPurchased'
  | 'projectsParticipated'
  /*
   * A SLOT, not a column: where the per-stage status columns go on the two
   * company cards. Expand with `expandStageStatusSlot` — never render it as is.
   */
  | 'stageStatuses'
  | 'upcomingTask'
  | 'note'
  /*
   * 🔴 **The two COMPANY cards' replacement for `note` — 2026-10-02, owner's
   * decision.** {@link PartnershipRow.accountLastNote}: ANYONE's last human
   * note on the account (the company and its deals, quotes and invoices),
   * system notes excluded — not the reader's own. `lastNote` is its author and
   * text, `lastNoteAt` its date; the date is visible to every reader, the text
   * and author only with `notes.read`. ⛔ **The three document cards keep
   * `note` ("Your last note") and must not gain these** — `accountLastNote` is
   * on the `/companies` list row and on no document row.
   */
  | 'lastNote'
  | 'lastNoteAt'
  /*
   * 2026-10-27 (owner): the CURRENT RESOLVED account lead — the record's own
   * override, else its company's lead today — on all five cards, from
   * {@link PartnershipRow.accountLead}. The same lead the page's lead filter
   * matches. Id `accountLead` is stored in saved layouts: never rename it.
   */
  | 'accountLead';

/**
 * One project as a partnership row carries it — `{id, name}`.
 *
 * ⚠️ *SUPERSEDED 2026-09-14: "One project a **company** is being pursued for, as
 * {@link PartnershipRow.projectsOfInterest} carries it."* **It now describes two
 * different facts** — a company's projects of interest AND, on the three
 * document cards, {@link PartnershipRow.lineProjects}, the projects a document's
 * own line items name. ⛔ **That is a shared SHAPE and emphatically not a shared
 * meaning**; the two fields are what keep them apart, and a renderer handed the
 * wrong one compiles and prints a plausible list.
 *
 * Named so the page can type a cell renderer without restating the shape. It is
 * assigned straight from the wire in all four mappers — ⚠️ *SUPERSEDED
 * 2026-09-12: "from `Company.projectsOfInterest` in `companyRow`"* — so a change
 * to either field's shape fails at an assignment rather than printing
 * `undefined` in a cell.
 *
 * ⚠️ **THREE wire sources now, declared separately.** ⚠️ *SUPERSEDED 2026-09-14:
 * "**TWO wire sources now**".* `Company.projectsOfInterest` on the two company
 * cards; {@link DocumentCompanyRef.projectsOfInterest} through the `company`
 * embed, which **no card renders any more** but which the row still carries; and
 * `Deal.lineProjects` / `Quote.lineProjects` / `Invoice.lineProjects`, normalised
 * by {@link lineProjectsOf}. All three are structurally identical today and are
 * NOT one type; a cell renderer sees only this one, which is the point of it
 * existing.
 *
 * ⚠️ **Structurally identical to `CompanyProject` in `company-projects.ts` and
 * DELIBERATELY NOT THE SAME NAME.** That module's header says in terms that it
 * will not name a wire type, because it renders and has no business knowing the
 * response shape; this module is the wire side and has no business depending on
 * a render helper. The two agree structurally, so a row's projects pass straight
 * into `companyProjectsLabel` — and the day either side's shape moves, the
 * mismatch is a `tsc` error at the boundary rather than a silent one. ⛔ Do not
 * "unify" them into one import; the decoupling is the decision.
 *
 * ⚠️ **`name` is emitted as the server sent it and this module does not word,
 * join, truncate or count it** — the page owns every sentence on the screen, and
 * a list joined here would be a second copy of a decision the page has to make
 * anyway for the narrow layout.
 */
export interface PartnershipRowProject {
  id: number;
  name: string;
}

export interface PartnershipRow {
  id: number;
  /** Main label — company name, deal name, quote subject. */
  primary: string;
  /** Supporting line, e.g. the company name under a deal. */
  secondary: string | null;
  /**
   * The SEED EMERGE document number (`EQ-00001`) on quote rows only, for the
   * PDF preview's title (2026-10-25). Absent on every other table.
   */
  documentNumber?: string | null;
  /** Money with its currency code, or a stage/status name. */
  meta: string | null;
  /**
   * The quote's stage or the invoice's status. `null` on the three tables that
   * have neither — see {@link PartnershipRowStatus}.
   *
   * ⚠️ *SUPERSEDED 2026-09-03, the PLACEMENT only: "for the badge beside the
   * record's name".* It is its own column now, declared per card in
   * {@link PartnershipTableState.extraColumns}. Nothing about the VALUE moved:
   * it is still the server's `{id, name}` with no flags on it, and it is still
   * the page that resolves it against the master.
   */
  status: PartnershipRowStatus | null;
  /**
   * 2026-10-27: the CURRENT RESOLVED account lead, for the "Account lead" column
   * on all five cards — the wire's `accountLead` on `/companies`, `/deals`,
   * `/quotes` and `/invoices`, which every one of those repositories already
   * joins as `COALESCE(record.account_lead_id, company.account_lead_id)`.
   * `null` = nobody (the "Unassigned" the lead filter matches); `undefined` =
   * an older API that did not send the field.
   */
  accountLead?: { id: number; name: string } | null;
  /**
   * The invoice's own due date, plain `YYYY-MM-DD` — for the "Overdue by"
   * column. `null` on every other table, which have no such field, and null on
   * an invoice that carries none.
   *
   * 🔴 **THIS MODULE EMITS THE DATE AND NOTHING ELSE. It computes nothing about
   * overdueness and must never start.** Whether an invoice is overdue depends on
   * `is_settled`, which lives on the `/invoice-statuses` MASTER — and this
   * module deliberately never resolves that master, for the same reason it
   * never resolves a quote stage: a second copy of the flag lookup would agree
   * the day it was written and drift on the first correction, and what would
   * drift is whether a document reads as settled. The page owns that, through
   * `isStatusSettled`, which is also what tones the status badge — so the badge
   * and any overdue claim beside it cannot end up contradicting each other.
   *
   * ⛔ And never derive it from a NAME. `"Unpaid"` contains `"paid"`.
   */
  dueDate: string | null;
  /**
   * The company's country OF RECORD, already resolved to its name — for the
   * "Country" column on the two `/companies` cards. `null` on the other three,
   * which carry no such field, and null on a company nobody has classified.
   *
   * ⚠️ **`company.country.name`, and the spelling is a trap this contract has
   * already sprung once.** The `/countries` MASTER row spells its label
   * `countryName` ({@link Country}); the embed on a company spells it `name`
   * (`toCompanyCountry` in the deployed `companies.repository.js`, verified
   * against the served release 2026-09-03). A renderer that reaches for
   * `countryName` here compiles and prints `undefined` — the `LineCity`-versus-
   * project-`cities` trap, and `src/lib/types.ts` records that this exact field
   * was once typed as `countryName` and was wrong for days because nothing
   * dereferenced it.
   *
   * ⚠️ **`null` is UNCLASSIFIED, not "no country exists".** The country filter
   * cannot ask for these rows at all — `country_id IN (...)` is UNKNOWN for a
   * NULL — so a company with none is unreachable by any value of it. The page
   * says so rather than leaving an empty cell to mean two things.
   */
  country: string | null;
  /**
   * Where the account came from, already resolved to its name — for the "Source"
   * column on the two `/companies` cards. `null` on the other three, which carry
   * no such field, and null on a company nobody has recorded one for.
   *
   * ⚠️ **`company.source.name`, and unlike `country` the master and the embed
   * AGREE on that spelling** — `f_company_sources` is an ordinary
   * `ReferenceRecord`, so its label is `name` on both sides. That is a
   * convenience and not a licence: the ID is still the half a write addresses,
   * for the reasons the WRITE HANDLES block below states in full.
   *
   * 🔴 **`null` IS "NOBODY HAS RECORDED ONE", AND IT IS THE MAJORITY STATE.**
   * The column is optional and blank on every company that predates it, so an
   * empty cell is the ordinary answer here rather than the exception it is on
   * `country`. ⛔ It must never be coalesced to a word — "Unknown", "Direct" or
   * "Organic" in place of a blank would be this client inventing a provenance
   * for an account, which is a claim somebody would act on.
   */
  source: string | null;
  /**
   * What KIND of institution the account is, already resolved to its name — for
   * the "Company type" column on the two `/companies` cards. `null` on the other
   * three, which carry no such field, and null on a company nobody has typed.
   *
   * ⚠️ **`company.companyType.name`.** The FIELD is the long word
   * (`companyType`, never `type` — `src/lib/types.ts` states why at
   * {@link Company.companyType}) and the LABEL inside it is the short one
   * (`name`, not `companyTypeName`) — `f_company_types` is an ordinary
   * `ReferenceRecord`, so it agrees with `source` beside it and disagrees with
   * `country`, whose master spells its label `countryName`. **Three masters,
   * three spellings, one column apart.**
   *
   * 🔴 **`null` IS "NOBODY HAS TYPED THIS ACCOUNT", AND IT IS THE MAJORITY
   * STATE** — the column is optional and blank on every company that predates
   * it, exactly as `source` beside it is. ⛔ It must never be coalesced to a
   * word: "Unknown", "Other" or "University" in place of a blank would be this
   * client asserting what an institution IS, which is a claim somebody would act
   * on and a claim the record does not make.
   */
  companyType: string | null;
  /**
   * The projects this row's ACCOUNT is being pursued for, for the "Projects of
   * interest" column.
   *
   * ⚠️ *SUPERSEDED 2026-09-12: "for the … column on the two `/companies` cards.
   * `null` on the other three, which carry no such field."* **Populated on all
   * five now.** The two company cards read it off the company itself; the three
   * document cards read it off their `company` embed, which carries the array
   * since {@link DocumentCompanyRef}. The reasoning for why a document card may
   * render it at all is on {@link PartnershipColumn} and is re-derived there
   * rather than assumed — ⛔ the concept is the ACCOUNT's, never the document's.
   *
   * 🔴 **`[]` AND `null` ARE DIFFERENT FACTS AND MUST NEVER BE COALESCED.** `[]`
   * is *an account exists and nobody has recorded a project against it* — a real,
   * ordinary state that the empty cell honestly reports.
   *
   * **`null` is every case where there was no such list to read**, and on the
   * three document cards it now covers TWO of them:
   *
   *  - **the record names no company** — `company_id` is nullable on `d_deals`,
   *    `d_quotes` and `d_invoices`, so a companyless document has **nobody to
   *    have any**; and
   *  - **the response carried no such key**, which happens when this app is
   *    deployed AHEAD of the API, exactly as `latestNote` can be.
   *
   * ⚠️ **Those two are conflated here deliberately and the conflation is safe,
   * because neither is a CLAIM.** Both render the same empty cell and neither
   * says the account is interested in nothing — which is the one reading that
   * would be wrong. What must never join them is `[]`, which IS a claim: a
   * `?? []` anywhere between the wire and the screen turns "no account" and "not
   * read" alike into *this account is interested in nothing*, indistinguishable
   * from the real empty state, on the column whose whole job is to say what is
   * being pursued. The same distinction `0`-versus-`null` draws on campaign
   * inclusions, and {@link DocumentCompanyRef} refuses the identical
   * `company?.projectsOfInterest ?? []` at the wire.
   *
   * ⚠️ **It rides on rows all five cards already fetch.** ⛔ Nothing here may add
   * a request, a query param or a wider `perPage` to obtain it; if it ever stops
   * arriving, the honest answer is `null` and an empty cell, not a second call
   * per row.
   */
  projectsOfInterest: readonly PartnershipRowProject[] | null;
  /**
   * The projects **THIS DOCUMENT'S OWN LINE ITEMS** name — for the "Projects on
   * this document" column. Owner's decision, 2026-09-14.
   *
   * **The three DOCUMENT cards only.** `null` on leads and companies, where the
   * concept is absent: a company IS the account and has no line items at all.
   *
   * 🔴 **IT IS NOT {@link PartnershipRow.projectsOfInterest} AND NEITHER IS A
   * FALLBACK FOR THE OTHER.** Both fields ride on the same document row, both are
   * `readonly PartnershipRowProject[] | null`, and both render through the same
   * label helper — so **the swap COMPILES and prints a perfectly plausible list
   * under the wrong heading.** Only the two field names and the two heading
   * constants in `company-projects.ts` keep them apart:
   *
   *  - **`projectsOfInterest`** is the ACCOUNT's INTENT — what this school is
   *    being chased for. It is a company's column and is editable there.
   *  - **`lineProjects`** is THIS DOCUMENT's SCOPE — what this deal, quote or
   *    invoice actually sells. It is a consequence of the line items and is
   *    **not editable from a cell at all**, here or anywhere.
   *
   * They routinely disagree, which is the entire reason the three document cards
   * stopped rendering the first one. ⛔ Never show one where the other is empty.
   *
   * 🔴 **`[]` IS THE MAJORITY ANSWER AND IT IS A REAL, ORDINARY STATE** —
   * measured, **613 of 1,088 documents name no project on any line**. It says
   * *this document's lines name no project*, which is honest and common; an empty
   * column down most of a card is THE DATA, the same ruling `travel` on
   * `/client-deliverables` carries. ⛔ It must never be read as a failure and no
   * empty-state copy may imply the values are on their way.
   *
   * ⚠️ **`null` IS "THERE WAS NO SUCH LIST TO READ", and it covers TWO cases that
   * are safe to conflate because neither is a CLAIM**: the card is a company card
   * and the concept is absent, or the response carried no such key — which happens
   * when this app is deployed AHEAD of the API, exactly as `latestNote` can be.
   * Both render an empty cell and neither says the document sells nothing.
   *
   * ⛔ **What must never join them is `[]`, which IS a claim.** A `?? []` anywhere
   * between the wire and the screen turns *not read* and *no lines concept* alike
   * into *this document names no project* — indistinguishable from the real and
   * very common empty state, on the column whose whole job is to say what a
   * document covers. {@link lineProjectsOf} is the one place that distinction is
   * made, and it is made ONCE rather than at three call sites.
   *
   * ⚠️ **It rides on rows the three document cards already fetch.** ⛔ Nothing here
   * may add a request, a query param or a wider `perPage` to obtain it — and there
   * is no per-document projects endpoint to call even if it were affordable.
   *
   * ⚠️ **`?lineProjectId=` on `/quotes` and `/invoices` is a different thing
   * entirely** — a filter narrowing WHICH DOCUMENTS come back, where this field
   * reports what each one names. One word apart, and wiring one to the other would
   * narrow a card while its column went on describing the rows that survived.
   */
  lineProjects: readonly PartnershipRowProject[] | null;
  /**
   * The deal's signed quote — for the "Quote signed" column on the **Deals card
   * only** (owner 2026-10-27). Assigned straight from `Deal.quoteSigned`.
   *
   * ⛔ **THREE states, never collapsed**: `undefined` is an older server (or a
   * row that is not a deal), `null` is a deal with no signed quote, an object is
   * the quote. Both of the first two render `—`; never `?? null`.
   */
  quoteSigned?: DealQuoteSigned | null;
  /**
   * The credit this company holds, one entry per currency — for the "Credit
   * balance" column on the **Companies card only** (owner's decision 2026-09-24;
   * not Leads, not the document cards). Assigned from `Company.creditBalances`.
   *
   * `null` is EITHER no `credit-notes.read` grant, an older server that sends no
   * key, or a row that is not a company — every one of which reads as *not
   * available*, never as *no credit*. `[]` is no credit. Read through
   * `creditHeldOf` in `@/lib/credit-notes`, which is where zero-balance entries
   * drop out. ⛔ Never `?? []`, never summed, never converted.
   */
  creditBalances: readonly CompanyCreditBalance[] | null;
  /**
   * The company's purchase history, one entry per cycle — for the "Products
   * purchased" and "Projects participated in" columns on the **Companies card
   * only**. Assigned through `purchaseHistoryOf(company)`.
   *
   * `null` is NOT REPORTED (an older server, or a row that is not a company) and
   * must never be read as *bought nothing*; `[]` is a company with no purchases.
   * ⛔ Never `?? []`.
   */
  purchaseHistory: readonly CompanyPurchaseCycle[] | null;
  /**
   * An open task stands against this ACCOUNT — for the "Upcoming task" column.
   * Present on all five tables; all four record types carry it, server-computed
   * and never null.
   *
   * 🔴 **`false` IS "NO OPEN TASK ON THIS ACCOUNT", NEVER "NOT READ", and this is
   * the one field here where those two could be collapsed by a single character.**
   * ⛔ **Never `?? false`.** The wire declares it always present, so there is
   * nothing to default — and a coalesce at the wire boundary is the LAST place
   * the difference is still observable, which is what makes it the worst place to
   * erase it. An unread key would survive an assignment as `undefined`, findable
   * by anyone who later looks; laundered through `?? false` it becomes a stored,
   * confident, permanently indistinguishable *no*. Same defect class as the
   * `?? []` refused on {@link PartnershipRow.projectsOfInterest} directly above.
   *
   * ⚠️ **It is `boolean` and NOT `boolean | null`, unlike its neighbour, and the
   * asymmetry is the contract rather than an inconsistency.** `projectsOfInterest`
   * needs the third state because a companyless document really has no list to
   * read; a task flag has two states the server can always answer, on a record
   * with a company and without. Widening it would invite a three-way render for a
   * state the server cannot express.
   *
   * ⚠️ **VIEWER-INDEPENDENT — the opposite of {@link PartnershipRow.latestNote},
   * which it renders next to.** `/tasks` is permission-scoped (`tasks.read`
   * defaults to scope `own`), so a client-side equivalent would flag rows for the
   * reader's own tasks and nobody else's — which is precisely the per-viewer
   * figure presented as an account fact that this page was repaired for. This one
   * discloses only THAT a task exists: never whose, what it says, or when it is
   * due. It may therefore be worded as a fact about the account.
   *
   * ⚠️ **ACCOUNT-LEVEL IN BOTH DIRECTIONS.** A task on a company flags its deals,
   * quotes and invoices; a task on any of those flags the company and its
   * siblings. A record with no company still flags for a task on itself. ⛔ So do
   * not re-group by company in the client, and do not read a flagged deal as
   * meaning the task is *on the deal* — it is on the account.
   *
   * ⚠️ **IT SAYS NOTHING ABOUT ROW ORDER.** The flag pairs with `tasksFirst` on
   * the list request to sort flagged rows first, and **none of the five loaders
   * sends it** — deliberately: `tasksFirst` PARTITIONS where `activityFirst`
   * RANKS, and the two are mutually exclusive here for the same reason `sort` and
   * `activityFirst` are (see `orderParamsFor`). Reading the flag without asking
   * for the sort is legitimate; it just tells you nothing about where the row sat.
   */
  hasOpenTask: boolean;
  /**
   * The newest note on this record **that the reader may see**, for the
   * "Your last note" column. Present on all five tables — every one of the four
   * endpoints returns it.
   *
   * 🔴 **VIEWER-SCOPED, AND `null` IS "NO NOTE YOU MAY SEE".** See
   * {@link ViewerLatestNote} and the section in this module's header. For a
   * reader without the notes audit grant this is the newest note THEY wrote, so
   * `null` says *you have not written one here* and says nothing at all about
   * whether a colleague has. ⛔ It may not be rendered, worded or summarised as
   * "no activity", and it may not order anything.
   *
   * `body` is already TRIMMED here; see `latestNoteOf` for what a blank one
   * does and why.
   */
  latestNote: ViewerLatestNote | null;
  /**
   * The ACCOUNT's last human note, by anyone — the two company cards only
   * (2026-10-02). Assigned straight from `Company.accountLastNote`.
   *
   * ⛔ **THREE states, never collapsed**: `undefined` is an older server that
   * does not send the key (render a dash, claim nothing); `null` is an account
   * with no human note anywhere; an object is the note. A `?? null` here would
   * turn *the server did not say* into *nobody has written on this account*.
   * Always `undefined` on deal, quote and invoice rows, whose cards do not
   * declare the columns.
   */
  accountLastNote: AccountLastNote | null | undefined;
  /** Route to the record, e.g. `/deals/12`. */
  href: string;
  /** What a task or note raised from this row attaches to. */
  activityTarget: { type: TaskTargetType; id: number };
  /**
   * The record's own audit timestamp — when the row was last written, the same
   * answer for every viewer.
   *
   * ⚠️ It is **not** "last activity" and must never be labelled as one. A note,
   * a task and a call leave it untouched; on a quote or an invoice a Xero push
   * moves it with no person involved (see `updatedAtAlsoWrittenByXero`).
   *
   * `null` only where the response carried no usable timestamp, which the
   * contract says cannot happen — it is here so a blank cell has one meaning
   * rather than two, not because it is expected.
   */
  updatedAt: string | null;
  /**
   * How many contacts the company has — the figure on the row's contacts
   * button (Leads and Companies cards).
   *
   * ⚠️ `null` on deal, quote and invoice rows (the concept is the company's,
   * not the document's) and on a company row from a server that did not send
   * `contactCount`. A `null` renders the button with no figure, never "0".
   */
  contactCount: number | null;

  /*
   * ── WRITE HANDLES ────────────────────────────────────────────────────────
   *
   * ✅ Added 2026-09-12, when this dashboard became EDITABLE. Everything above
   * this line is read TO A SCREEN; everything below it is read INTO A REQUEST.
   *
   * 🔴 **A NAME IS NOT A HANDLE, AND EVERY FIELD HERE EXISTS BECAUSE THE FIELD
   * ABOVE IT IS A LABEL.** `secondary` carries a company's name, `meta` carries a
   * company's stage name, `country` carries a country's name — each is what a
   * reader sees, and NONE of them can address the record it names:
   *
   *  - **it is ambiguous.** Company names are not unique in this tenant and are
   *    not meant to be: `Bocconi University` and `Gud Bocconi` are two real
   *    accounts, and the school-level Xero contacts share long prefixes by
   *    design. A resolver keyed on the label picks the wrong customer, and the
   *    write succeeds.
   *  - **it is lossy.** These labels go through `text()`, which returns `null`
   *    for a blank — so a record whose name is whitespace has no label at all,
   *    and `primary` has already fallen back to `#id` for exactly that case.
   *  - **it is admin-editable free text.** Reading one as an IDENTITY is the
   *    failure this codebase records repeatedly, one step past reading one as a
   *    FLAG — and it is the step where the wrong answer stops being a badge and
   *    starts being a write.
   *
   * ⚠️ **Each handle is assigned in the SAME mapper, from the SAME wire object,
   * as the label it belongs to.** A label and its id are ONE fact split in two,
   * and the split is where they come to describe different records — so
   * `country` and `countryId` are two lines apart in `companyRow` and must stay
   * that way. ⛔ Never recover one from the other at a call site.
   *
   * ⚠️ **`null` here is almost always THE CONCEPT BEING ABSENT rather than a
   * value that is unknown — and `countryId` on a company row is the ONE
   * EXCEPTION, so read each field rather than this paragraph.** There it is a
   * company nobody has classified: a real, editable state, and the one this
   * dashboard gained a picker to close. Everywhere else a `null` handle means
   * the row has no such thing to address.
   *
   * ⚠️ **What tells the page which it is looking at is the CARD, structurally.**
   * `extraColumns` declares the matching column only where the concept exists,
   * so the only rows that can render or edit a handle are the ones where its
   * `null` has a single meaning. ⛔ That is why a page must not render one of
   * these off `row !== null` alone — the card's column list is the test.
   */

  /**
   * The company an edit made from this row WRITES TO.
   *
   * **Populated on all five cards.** On leads and companies it is the row's own
   * `id` — the company IS the record. On deals, quotes and invoices it is the
   * `company` embed's id, which is what makes the projects-of-interest column on
   * those three editable at all: the array is rendered on a deal and written to
   * the ACCOUNT, so a modal opened from a deal row needs the company to `PATCH`
   * and `row.id` is the deal's.
   *
   * 🔴 **`null` MEANS THIS RECORD NAMES NO COMPANY — THERE IS NOTHING TO WRITE
   * TO, not "we could not tell which".** `company_id` is nullable on all three
   * document tables. It is the same `null` that puts
   * {@link PartnershipRow.projectsOfInterest} at `null` on that row, from the
   * same embed, in the same expression — so the cell is empty and the edit is
   * unavailable for one reason, and the two cannot disagree.
   *
   * ⛔ **An edit affordance must be WITHHELD on a `null`, never offered and then
   * refused.** There is no id to send, so the only reachable outcome is a request
   * that cannot be built or a `PATCH` to whatever a fallback invented — and this
   * module's own rule against a client resolving a company by name is what closes
   * the second of those.
   */
  companyId: number | null;
  /**
   * The company's pipeline stage as a WRITABLE reference — for the stage picker.
   *
   * **The two `/companies` cards only.** {@link PartnershipTableState.valueLabel}
   * renders {@link PartnershipRow.meta} for it, and `meta` is a STRING: it is
   * `text(company.stage?.name)`, so it carries the label and cannot carry the id
   * a picker has to send. ⚠️ Note also that the Leads card renders no value
   * column at all, so on that card the label is not even on screen while the
   * handle still is — which is the clearest possible statement that these two
   * fields are not each other.
   *
   * 🔴 **IT IS AN `f_company_stages` ROW AND MUST NEVER BE HANDED TO
   * `StageBadge` OR `StatusBadge`.** Those two resolve `/quote-stages` and
   * `/invoice-statuses` — three masters, three different flag sets — and all
   * three are `{id, name}`, so the substitution COMPILES and paints a company
   * stage with quote semantics. {@link PartnershipRowStatus} is a TAGGED union
   * for precisely this reason and this field is deliberately NOT one, because it
   * is not a member of that union's question; the tag cannot guard it, so the
   * warning has to. ⛔ Do not widen `PartnershipRowStatus` to hold it either —
   * that is the widening that type already refuses.
   *
   * **`null` on the other three cards is the concept being absent**, exactly as
   * {@link PartnershipRow.status} is `null` on these two: a deal's stage is a
   * third master and a quote's stage a fourth, and neither is this one.
   */
  companyStage: MasterRef | null;
  /**
   * The company's stage STATUS, off the same `/companies` row as
   * {@link PartnershipRow.companyStage}. Three states: `undefined` = not read
   * (an older API), `null` = no status, an object = the status. `null` on the
   * three document cards, where the concept is absent. Never coalesce.
   */
  stageStatus?: CompanyStageStatusRef | null;
  /**
   * The company's follow-up date for a resurface status, bare `YYYY-MM-DD`,
   * off the same `/companies` row as {@link PartnershipRow.stageStatus}
   * (2026-10-25). Three states: `undefined` = not read (an older API), `null` =
   * none, a string = the date. `null` on the three document cards, where the
   * concept is absent. Never coalesce.
   */
  followUpOn?: string | null;
  /**
   * The company's country of record as a WRITABLE id — for the country picker.
   *
   * **The two `/companies` cards only**, beside {@link PartnershipRow.country},
   * which carries the same fact as a NAME for the column to print. The write key
   * on `PATCH /companies/:id` is `countryId`; the label is `country.name`, and
   * ⚠️ the `/countries` MASTER spells that label `countryName` while this embed
   * spells it `name` — a trap this contract has already sprung once, recorded on
   * {@link PartnershipRow.country}. The id is the half that is spelled the same
   * everywhere and is the half to address a record by.
   *
   * ⚠️ **`?sort=country` and `?countryId=` are ONE WORD APART and are different
   * things** — the ordering (see {@link COUNTRY_SORT_KEY}) and the filter. This
   * field is neither: it is what an EDIT sends. Three uses of one word, and the
   * schemas are `.strict()`, so the wrong one is a 422 rather than a no-op.
   *
   * 🔴 **`null` MEANS TWO DIFFERENT THINGS AND THE CARD IS WHAT SEPARATES
   * THEM.** On a company card it is a company nobody has classified — a real,
   * editable state, and the one this picker exists to close. On the other three
   * it is the concept being absent. Nothing on the row distinguishes them and
   * nothing needs to: `extraColumns` declares `country` on the two company cards
   * alone, so the only rows that can render or edit it are the ones where `null`
   * has the first meaning.
   */
  countryId: number | null;
  /**
   * The account's source as a WRITABLE id — for the source picker.
   *
   * **The two `/companies` cards only**, beside {@link PartnershipRow.source},
   * which carries the same fact as a NAME for the column to print. The write key
   * on `PATCH /companies/:id` is `sourceId`; the label is `source.name`.
   *
   * ⚠️ **`?sort=source` and `?sourceId=` are ONE WORD APART and are different
   * things** — the ordering and the filter — and this field is a THIRD use of
   * the word: what an EDIT sends. `src/lib/types.ts` states the first two at
   * {@link CompanyListSort}, and the schemas are `.strict()`, so the wrong one
   * is a 422 rather than a no-op.
   *
   * 🔴 **`null` MEANS TWO DIFFERENT THINGS AND THE CARD IS WHAT SEPARATES
   * THEM** — the same arrangement {@link PartnershipRow.countryId} documents. On
   * a company card it is a company nobody has recorded a source for, which is a
   * real, editable state and the one this picker exists to close; on the other
   * three it is the concept being absent. `extraColumns` declares `source` on the
   * two company cards alone, so the only rows that can render or edit it are the
   * ones where `null` has the first meaning.
   */
  sourceId: number | null;
  /**
   * The account's type as a WRITABLE id — for the type picker.
   *
   * **The two `/companies` cards only**, beside {@link PartnershipRow.companyType},
   * which carries the same fact as a NAME for the column to print. The write key
   * on `PATCH /companies/:id` is `companyTypeId`; the label is `companyType.name`.
   *
   * ⚠️ **THREE USES OF ONE WORD AND THE WIRE SPELLS THEM DIFFERENTLY.** The SORT
   * is `?sort=companyType`, the FILTER is `?companyTypeId=`, and this field is
   * what an EDIT sends — which is the filter's spelling and not the sort's.
   * `src/lib/types.ts` states the first two at {@link CompanyListSort}, and the
   * schemas are `.strict()`, so the wrong one is a 422 rather than a no-op.
   *
   * 🔴 **`null` MEANS TWO DIFFERENT THINGS AND THE CARD IS WHAT SEPARATES
   * THEM** — the arrangement {@link PartnershipRow.countryId} and
   * {@link PartnershipRow.sourceId} both document. On a company card it is a
   * company nobody has typed, which is a real, editable state and the one this
   * picker exists to close; on the other three it is the concept being absent.
   * `extraColumns` declares `companyType` on the two company cards alone, so the
   * only rows that can render or edit it are the ones where `null` has the first
   * meaning.
   */
  companyTypeId: number | null;
}

export interface PartnershipTableState {
  key: PartnershipTableKey;
  title: string;
  /**
   * The module list this card summarises, scoped to the same person.
   *
   * Where **Show all** goes. It exists because {@link PER_PAGE} is the
   * endpoints' ceiling and not a promise: a card whose `total` exceeds 100 can
   * never show every row, and the honest answer to that is the list page, which
   * pages.
   *
   * ✅ **All four list pages read `accountLeadId` OUT OF THE URL — verified in
   * source, not assumed.** It is in `SERVER_FILTER_PARAMS` on `CompaniesPage`,
   * `QuotesPage` and `InvoicesPage`, and on `DealsPage` the Account lead column
   * funnel owns the same param at `scope: 'server'`. So the link lands on the
   * SAME scope the card shows and the two counts agree. That is the whole
   * reason it was checked rather than presumed: a link carrying a param the
   * destination ignored would open the whole company's pipeline under a heading
   * the reader believes is theirs — a 200, a full page, and the "it looks like
   * it worked" failure this codebase records repeatedly.
   *
   * 🔴 **THE COMPANIES CARD IS THE EXCEPTION AND ITS COUNTS WILL NOT AGREE.**
   * That card's own filter is a derived CSV of every ACTIVE NON-LEAD stage
   * (see `nonLeadStageFilter`), which is computed inside `load`, from a request
   * that has not been made when this link is built — so `/companies?accountLeadId=N`
   * includes leads and returns more rows than the card counted.
   *
   * ⚠️ **Accepted deliberately.** "Show all" means the MODULE, which is what a
   * reader hitting the 100-row ceiling actually wants; and the alternative is
   * worse than the discrepancy. Reproducing that CSV here would be a second
   * copy of the one thing `nonLeadStageFilter` exists to have only one of, built
   * from a master this function does not hold — and a stale second copy silently
   * omits every company on a stage somebody added. ⛔ Do not "fix" this by
   * inlining a stage list.
   */
  listHref: string;
  /**
   * The columns this card renders BETWEEN the record column and the value
   * column, in this order.
   *
   * ⚠️ **A property of the ENDPOINT's record type, not a preference** — see
   * {@link PartnershipColumn}, which carries the reasoning. ⚠️ *SUPERSEDED:
   * "Empty on leads, companies and deals because none of those records has a
   * quote stage, an invoice status, a signature envelope or a due date to
   * render."* No card's list is empty now — but the REASON is untouched and is
   * the half that matters: each card carries the columns its own record type
   * really has, which is why leads and companies still carry no quote stage, no
   * invoice status, no signature and no due date.
   *
   * ⚠️ *SUPERSEDED IN TURN, 2026-09-12, the tail of that same sentence: "`note`
   * went on all five, and `country` and `projectOfInterest` went on the two
   * company cards … and why deals carries **neither** company column."* **One of
   * the two, not neither.** `projectOfInterest` is on all five since
   * {@link DocumentCompanyRef} put the account's list on the `company` embed;
   * `country` is still the two company cards' own. ⚠️ **Note what kind of
   * sentence went wrong: a count of a set somebody can add to, in a clause that
   * was only decoration on a rule that never moved.** The rule is unchanged and
   * still decides every entry here.
   *
   * 🔴 **"THE RECORD TYPE" INCLUDES WHAT THE RECORD EMBEDS, and that is the
   * clarification the change forced rather than a loophole it opened.** A deal
   * does not have projects of interest; the account it embeds does, and the
   * column answers a question about the ACCOUNT on a row that names one. ⛔ It is
   * still not a licence to render a concept the record cannot reach — `country`
   * is on the same embed's table and is NOT on that embed, so the deals card
   * cannot have it at any price.
   *
   * ⚠️ Declared on the spec beside `sortOptions` and for the same reason: a
   * column listed here that the page cannot render is a heading over nothing,
   * and a column the page renders unconditionally would appear on cards whose
   * rows carry no such field.
   */
  extraColumns: readonly PartnershipColumn[];
  /**
   * Heading for the column that renders {@link PartnershipRow.meta}.
   *
   * Per table, because the same field means different things: money on a deal,
   * quote or invoice, and the pipeline stage on a company. One heading of
   * "Detail" described none of them.
   *
   * ⚠️ **`null` means the card renders NO value column at all** — added
   * 2026-09-12 for Leads, where every row is by definition at the one stage the
   * card filters on, so the column repeated a constant down the page.
   *
   * 🔴 **It is not enough to drop the `<th>`.** The table is `table-fixed` with
   * an explicit `<colgroup>`, and the width minimum is computed from the same
   * column list — so a card that renders one fewer CELL than it declares a
   * `<col>` does not squeeze, it shifts every column along and the browser pads
   * the shortfall at the end. `PartnershipColgroup`, `tableMinWidthRem`,
   * `PartnershipHead`, `RowsSkeleton` and the loaded row all take the same
   * `hasValueColumn` boolean, derived from this field at one place per call
   * site. Two derivations of one layout is what this page's own comments
   * describe as the thing that drifts.
   */
  valueLabel: string | null;
  rows: PartnershipRow[];
  total: number;
  loading: boolean;
  /**
   * A failed fetch, as a sentence. `rows` is then empty — but "failed" and
   * "empty" are different facts and the page renders them differently, so
   * never read an empty `rows` as a statement about the data.
   */
  error: string | null;
  /**
   * WHICH relationship scoped this table.
   *
   * ⚠️ All five tables are `'accountLeadId'` today, so the union has exactly
   * one inhabitant in use — and `'ownerId'` is KEPT DELIBERATELY. The whole
   * reason this field exists is that the keys were NOT uniform: `/quotes` and
   * `/invoices` were scoped by `ownerId` until `accountLeadId` landed on them
   * (migration 082), and a dashboard showing five tables
   * under one heading while scoping them two different ways quietly claims a
   * consistency the API does not offer. That fact is worth keeping visible, a
   * further table may well need the other key, and the page's lookups still
   * handle both — so do not collapse the union or delete the field.
   */
  scopedBy: 'accountLeadId' | 'ownerId';
  /**
   * The ordering these rows were REQUESTED with — always one of
   * {@link sortOptions}, never a raw URL value.
   *
   * ⚠️ **This replaced an `ordering` field, and it was DELETED rather than
   * renamed.** That field carried `'updated' | 'server-default'` and asserted
   * that `/companies` had been sorted by `updatedAt` descending — true while
   * the request was hardcoded, and an outright false claim the moment a person
   * could choose `name` instead. Nothing consumed it, so redefining it in place
   * would have left a field that reads plausibly and answers a question nobody
   * asked any more; an absent key is a `tsc` error you can find, a redefined one
   * is a wrong answer you cannot.
   */
  sort: PartnershipSort;
  /**
   * The orderings THIS card can honour, in the order they are offered.
   *
   * ⚠️ **A property of the endpoint, not a preference.** All five can now be
   * ordered alphabetically, but only the three document cards can offer
   * `newest` — `/companies` declares no `id` member on its sort enum, and its
   * nearest neighbour is a measurably different sequence. The rule the earlier
   * asymmetry established still holds and is the reason this field exists at
   * all: an option that silently does nothing is worse than an absent one,
   * because the reader concludes the data is wrong rather than that the control
   * is.
   */
  sortOptions: readonly StaticPartnershipSort[];
  /**
   * What this card is narrowed to right now — always the RESOLVED value, never
   * a raw URL param.
   *
   * ⚠️ Read back by the control, so the option on screen is always the one the
   * request was made with. A URL naming an id that is not in the master still
   * resolves to that id here and simply returns no rows: the server is the only
   * thing that can say an id does not exist, and a client that silently dropped
   * an unrecognised value would show an unfiltered table under a filtered
   * control.
   */
  filter: PartnershipFilter;
  /**
   * The filters THIS card can honour. A property of the endpoint — see
   * {@link PartnershipFilterKind}.
   */
  filterKinds: readonly PartnershipFilterKind[];
  /**
   * The company stage ids whose status columns this card renders — see the
   * STAGE STATUS COLUMNS block. `null` = none (document cards). Referentially
   * stable while the ids are unchanged.
   */
  stageStatusStageIds: ReadonlySet<number> | null;
  /** Whether this card honours the per-stage `stage-status-*` orderings. */
  stageStatusSortable: boolean;
  /** Which create modal this card's **+** opens. */
  create: PartnershipCreate;
  /**
   * Whether a Xero push also stamps this table's `updatedAt`.
   *
   * `true` on quotes and invoices: a successful push writes the Xero document
   * number and the currency rate back onto the record, so the timestamp climbs
   * on a background integration with nobody at a keyboard. `d_companies` has no
   * such writer — only the human CRM edit path writes its `updated_at`.
   *
   * `false` is the ABSENCE of that caveat, not a claim that only people write
   * the column. The page prints a sentence for `true` and none for `false`, so
   * a table nobody has audited says nothing either way rather than promising
   * something unmeasured.
   */
  updatedAtAlsoWrittenByXero: boolean;
}

/**
 * `Lead` on `/company-stages`.
 *
 * MEASURED against production: the master returns exactly four rows — 1 Lead,
 * 2 Prospect, 3 Client, 4 Past Client — and the id is the only stable handle on
 * the row. The NAME is admin-editable free text and is deliberately not matched
 * here, not even case-folded: this codebase has been bitten three times by a
 * client deciding what a record means from a name an administrator can change
 * ("Unpaid" read as paid, "Not Accepted" painted as accepted).
 *
 * The real answer is a boolean on the row — an `isLead` beside the deal stages'
 * `isWon` / `isLost` — which does not exist yet. Until it does, this constant is
 * a measurement with an expiry: it is wrong the day somebody reorders or
 * reseeds the master, and nothing will fail when it does.
 *
 * ⚠️ It is now a PARTITION, not just a filter: the Leads table asks for this id
 * and the Companies table asks for every other active one, so the two cards are
 * defined against each other. A wrong value here no longer just mis-scopes one
 * card — it puts the same company on both, which is the duplication the split
 * exists to remove.
 */
const LEAD_COMPANY_STAGE_ID = 1;

/** The Leads card's stage status scope — a module constant, so it is stable. */
const LEAD_STAGE_IDS: ReadonlySet<number> = new Set([LEAD_COMPANY_STAGE_ID]);

/** Before the Companies card's first load: no columns yet. Stable. */
const NO_STAGE_IDS: ReadonlySet<number> = new Set<number>();

/**
 * The Companies card's stage ids, from the `stageId` CSV its request sent —
 * cached by value so the same ids are always the same Set instance.
 */
const stageIdSetCache = new Map<string, ReadonlySet<number>>();
function stageIdSetOf(csv: string): ReadonlySet<number> {
  const cached = stageIdSetCache.get(csv);
  if (cached !== undefined) return cached;
  const set: ReadonlySet<number> = new Set(
    csv
      .split(',')
      .map((part) => Number(part))
      .filter((id) => Number.isSafeInteger(id) && id > 0),
  );
  stageIdSetCache.set(csv, set);
  return set;
}

/**
 * The company stage master, read to derive the Companies table's filter.
 *
 * `perPage` is the endpoint's ceiling — 201 is a 422 and the default of 50 would
 * silently page a master that outgrew it, which here would quietly drop a stage
 * out of the filter and hide every company sitting on it. There is no `sort` or
 * `order` on this query schema: it is `.strict()`, ordering is always
 * `sortOrder, name`, and sending one is a 422.
 *
 * ⚠️ **No `isActive` on the QUERY, deliberately.** The filter is opt-in with no
 * default, so asking for it would work — but the split below has to know which
 * rows are retired, and a server-side narrow throws that away. Fetch everything
 * and split here, the same rule `use-company-stages.ts` follows.
 */
const COMPANY_STAGE_QUERY = { page: 1, perPage: 200 } as const;

/*
 * How many rows one card FETCHES. Five are visible above the fold; the rest are
 * reached by scrolling inside the card.
 *
 * ⚠️ *SUPERSEDED 2026-09-03: `const PER_PAGE = 15;`* — owner's instruction,
 * *"in each card on scroll i want to show all the records and not restricting
 * them"*. Fifteen was an arbitrary depth chosen when the scroll area was new;
 * 100 is not arbitrary and is not a preference. It is the **endpoints' own
 * ceiling** — `perPage` maxes at 100 on `/companies`, `/deals`, `/quotes` and
 * `/invoices`, and 101 is a 422 — so it is as far as ONE request goes and there
 * is no larger number to raise this to.
 *
 * Five rows are still what stands above the fold; only the depth behind them
 * changed. Same number of requests as before, one larger page each.
 *
 * 🔴 **THIS IS NOT "SHOW ALL", AND MUST NOT BE DESCRIBED AS ONE.** A card whose
 * `total` exceeds 100 still cannot show all of its rows — this module fetches
 * one page and there is deliberately no second-page fetch, no cursor and no
 * infinite scroll here. The cap moved; it did not go away, and a card that
 * silently stops at row 100 looks exactly like a complete card. What closes
 * that gap is the **Show all** link each card now carries — see
 * {@link PartnershipTableState.listHref} — which hands the reader the module's
 * own list page, where paging exists.
 */
const PER_PAGE = 100;

/* --- row builders --------------------------------------------------------- */

/** Trimmed, or null. A blank string is an absent value dressed as a present one. */
function text(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

/** `primary` is not nullable, so a record with no usable label falls back to its id. */
function primaryOf(label: string, id: number): string {
  return text(label) ?? `#${id}`;
}

/**
 * Money for the `meta` slot, or `null` when it cannot be stated honestly.
 *
 * Two rules from this codebase meet here:
 *
 *  - **A decimal string never sees a float.** `formatDecimalString` groups it
 *    as text; `Number()` on it loses the exactness the column was chosen for.
 *  - **A figure is never shown without its currency code.** Currency is
 *    per-record here and nothing converts, so a bare number in a list that can
 *    hold four currencies reads as the reader's own — which is how a figure
 *    that is wrong by construction gets believed.
 *
 * So a record carrying an amount but no `currency` returns `null` and the
 * caller falls back to the stage or status name. That loses information, and it
 * is the lesser loss: the amount is still on the record's own page, beside the
 * currency field that explains why it could not be shown here.
 */
/**
 * The row's viewer-scoped last note, normalised.
 *
 * ⚠️ **The parameter accepts `undefined` and the contract does not, on purpose.**
 * `latestNote` is declared as always present on all four record types, which is
 * the contract — but this app deploys separately from the API and can be NEWER
 * than it, and a key an older server does not send reads back `undefined`, not
 * `null`. That is the failure this codebase already records twice by name
 * (`leadSourceText`, `vatNumber`): the row renders blank on every record and
 * looks exactly like a reader who has written no notes. Handled ONCE, here,
 * rather than at four call sites.
 *
 * ⚠️ **A blank body is treated as no note, and that is a choice rather than a
 * tidy-up.** This column shows what a note SAYS; a note that says nothing leaves
 * only its timestamp, and a per-viewer timestamp presented as a row's activity
 * is precisely the figure this page was repaired for removing. The empty cell it
 * falls to is not a claim that nothing happened, and the drawer behind that cell
 * still shows the real note. (The API requires a body, so this is a guard rather
 * than an expected state.)
 */
function latestNoteOf(note: ViewerLatestNote | null | undefined): ViewerLatestNote | null {
  const body = text(note?.body);
  if (note == null || body === null) return null;
  return { body, createdAt: note.createdAt };
}

/**
 * A document's own line projects, normalised — for
 * {@link PartnershipRow.lineProjects}.
 *
 * ⚠️ **The parameter accepts `undefined` and the contract does not, on purpose —
 * the same arrangement {@link latestNoteOf} above it documents, and for the same
 * reason.** `lineProjects` is declared always present on `Deal`, `Quote` and
 * `Invoice`, which is the contract; but this app deploys separately from the API
 * and can be NEWER than it, and a key an older server does not send reads back
 * `undefined`, not `[]`. Handled ONCE, here, rather than at three call sites.
 *
 * 🔴 **`?? null`, AND NEVER `?? []` — this is the whole function.** `[]` is a
 * CLAIM on this field (*this document's lines name no project*) and it is the
 * MAJORITY answer: measured, **613 of 1,088 documents**. So an unread response
 * coalesced to `[]` would be indistinguishable from the commonest real state, on
 * every row of three cards at once — a column that looks exactly like working
 * data and is not. `null` renders the same empty cell and asserts nothing, which
 * is the honest degradation.
 *
 * ⚠️ **The array is passed through UNTOUCHED — not copied, not sorted, not
 * de-duplicated, not truncated.** The server sends it in its own order and this
 * module words nothing; the cap, the `+N` marker and the `title` all belong to
 * `companyProjectsLabel` / `companyProjectsTitle`, which the page calls. A sort
 * here would be a second opinion about an ordering the server already has one of.
 *
 * ⚠️ **An EMPTY array is returned as an empty array, deliberately**, where
 * {@link latestNoteOf} turns a blank note into `null`. The two look like
 * inconsistent treatments of emptiness and are not: a blank note body has nothing
 * to SAY, so there is no note to show; an empty project list is a complete and
 * useful answer about a document. ⛔ Do not "harmonise" this into a `length === 0
 * ? null` — that would relabel the commonest real state as *not read*.
 */
function lineProjectsOf(
  projects: readonly PartnershipRowProject[] | null | undefined,
): readonly PartnershipRowProject[] | null {
  return projects ?? null;
}

function moneyMeta(amount: string | null, currency: string | null): string | null {
  const figure = text(amount);
  const code = text(currency);
  if (figure === null || code === null) return null;
  return `${formatDecimalString(figure)} ${code}`;
}

/*
 * `meta` is ONE slot and the page prints one line in it, so the fallback on the
 * DEAL row below is a deliberate choice between two true facts rather than an
 * accidental coalesce: money is the more useful of the two on a revenue
 * document, and the stage name is what remains when there is no statable figure.
 * Both are on the record's own page.
 *
 * 🔴 **QUOTES AND INVOICES NO LONGER TAKE THAT FALLBACK — 2026-09-03, and
 * removing it was part of ADDING the status badge rather than a tidy-up.**
 * *SUPERSEDED: `moneyMeta(…) ?? text(quote.stage?.name)` on both.* Those two
 * rows now carry their stage or status in `status`, which the page renders as
 * its own column (⚠️ *SUPERSEDED later the same day: "beside the record's
 * name"* — see the module header; the fallback's fate is unaffected either
 * way) — so keeping the fallback would print the SAME NAME
 * TWICE on any row whose money cannot be stated, once as a badge and once in the
 * value column under a heading reading "Quote value". Nothing would fail; the
 * row would simply say one thing in two places, one of them under a label that
 * denies it is a stage.
 *
 * The fallback's own reason is spent rather than overruled: it existed because
 * the stage had nowhere else to go, and now it has somewhere better. A quote
 * with no statable figure shows an em dash in the value column, which is what
 * that column is for, with the stage still on the row.
 *
 * ⚠️ **The DEAL row keeps its fallback**, because a deal has no `status` slot —
 * `d_deals` has no quote stage or invoice status, and its own pipeline stage is
 * a third master with a third flag set (`isWon`/`isLost`). That column is still
 * the only place a deal's stage appears.
 */

/**
 * The resolved lead as the row carries it — `{id, name}`, three states kept
 * apart: `undefined` (older API), `null` (unassigned), a person.
 */
function accountLeadOf(
  lead: { id: number; name: string } | null | undefined,
): PartnershipRow['accountLead'] {
  if (lead === undefined) return undefined;
  if (lead === null) return null;
  return { id: lead.id, name: lead.name };
}

function companyRow(company: Company): PartnershipRow {
  return {
    id: company.id,
    accountLead: accountLeadOf(company.accountLead),
    /*
     * ⚠️ **The row id and the company id are THE SAME NUMBER on these two cards
     * and on no others, and that coincidence is exactly why the field is carried
     * rather than derived.** A page that wrote `row.id` as the company would be
     * correct here and would silently address a DEAL as a company one card down.
     * Both cards that map this function are `/companies` reads, so the company
     * IS the record — see the field.
     */
    companyId: company.id,
    primary: primaryOf(company.name, company.id),
    /*
     * ⚠️ Deliberately EMPTY, where this used to carry the account lead's name.
     * Every table on this page is scoped to one person, so that line printed
     * the same name on every row of every card — noise that pushed the only
     * distinguishing fact, the company name, into second place.
     */
    secondary: null,
    meta: text(company.stage?.name),
    /*
     * ⚠️ **The handle for the label on the line above, assigned from the same
     * `company.stage` object so the two cannot come to describe different rows.**
     * `meta` is a string and a picker needs an id; see the field, including why
     * this must never be handed to `StageBadge`.
     *
     * Assigned rather than rebuilt as `{id, name}`: `Company.stage` is a
     * `MasterRef`, so a change to that wire shape fails HERE rather than
     * printing `undefined` in a picker.
     */
    companyStage: company.stage,
    // Assigned, never coalesced: `undefined` (not sent) must stay distinct from `null`.
    stageStatus: company.stageStatus,
    // Assigned, never coalesced — same three states as `stageStatus`.
    followUpOn: company.followUpOn,
    /*
     * ⚠️ A company's `stage` is `f_company_stages`, which is neither a quote
     * stage nor an invoice status and carries neither badge's flags. It already
     * renders in the value column above, under its own `Stage` heading.
     */
    status: null,
    /* A company has no due date — the concept does not exist on this record. */
    dueDate: null,
    /*
     * ⚠️ `.name`, NOT `.countryName` — see the field on `PartnershipRow`. The
     * master spells it the other way and this embed does not.
     */
    country: text(company.country?.name),
    /*
     * ⚠️ **The handle for the label on the line above, and the ONE `null` in this
     * section that is a real editable state rather than an absent concept** — a
     * company nobody has classified. Both halves come off the same
     * `company.country` object, so the name on screen and the id a picker sends
     * cannot drift apart.
     *
     * ⚠️ `.id` is spelled the same on the embed and on the `/countries` master;
     * `.name` is NOT (`countryName` there). That asymmetry is why the id is the
     * safe half to address a record by — see the two fields.
     */
    countryId: company.country?.id ?? null,
    /*
     * ⚠️ **`.name` on BOTH sides here**, unlike `country` two lines up, where the
     * `/countries` master spells its label `countryName` and only the embed says
     * `name`. `f_company_sources` is an ordinary `ReferenceRecord`, so there is
     * no asymmetry to trip over — which is worth saying precisely because the
     * field beside it has one.
     */
    source: text(company.source?.name),
    /*
     * ⚠️ **The handle for the label on the line above, off the SAME
     * `company.source` object**, so the name on screen and the id a picker sends
     * cannot come to describe different rows. The second `null` in this section
     * that is a real editable state rather than an absent concept — and unlike
     * `countryId` beside it, it is the state MOST companies are in.
     */
    sourceId: company.source?.id ?? null,
    /*
     * ⚠️ **`.companyType`, never `.type`, and `.name` inside it, never
     * `.companyTypeName`.** The long word is the FIELD and the short one is the
     * label — the opposite pairing to `country` ten lines up, whose master says
     * `countryName`. `f_company_types` is an ordinary `ReferenceRecord`, so this
     * one matches `source` directly above it.
     */
    companyType: text(company.companyType?.name),
    /*
     * ⚠️ **The handle for the label on the line above, off the SAME
     * `company.companyType` object**, so the name on screen and the id a picker
     * sends cannot come to describe different rows. The third `null` in this
     * section that is a real editable state rather than an absent concept — and
     * like `sourceId` beside it, it is the state MOST companies are in.
     */
    companyTypeId: company.companyType?.id ?? null,
    /*
     * ⛔ **ASSIGNED, NEVER COALESCED.** `Company.projectsOfInterest` is declared
     * always present and `[]` when empty, so there is nothing to default — and a
     * `?? []` here would be the one edit that makes an unread response say "this
     * company is interested in nothing". See the field on `PartnershipRow`: an
     * older server that does not send the key reads back `undefined`, which is
     * `null` on the row and an empty cell on the screen, which is honest. It is
     * `?? null`, deliberately, and not `?? []`.
     *
     * ⚠️ It costs no request: it is already on the rows both company cards fetch.
     */
    projectsOfInterest: company.projectsOfInterest ?? null,
    /*
     * 🔴 **`null`, AND EMPHATICALLY NOT `[]` — a company has no LINE ITEMS.**
     * The concept is absent from this record, exactly as `dueDate` and `country`
     * are absent from a deal: there is nothing here for a line to name a project
     * through, so there is no answer rather than an empty one.
     *
     * ⛔ **`[]` would be a CLAIM about the wrong subject** — *this company names
     * no projects* — on a card whose OTHER project column, two lines up, is the
     * account's real projects of interest. Two project columns on one row, one of them
     * asserting an emptiness the record cannot even have, is the confident wrong
     * answer this split was made to remove.
     *
     * ⚠️ **The two company cards do not declare the column at all** (see
     * `COMPANY_CARD_COLUMNS`), so this `null` should never reach a cell. It is
     * written down rather than left to the column list because a row is one
     * shared type across five cards and the next reader filling in a missing
     * field reaches for the neighbouring literal, which here is an array.
     */
    lineProjects: null,
    /*
     * ⛔ **`?? null`, NEVER `?? []`.** An absent key is an older server; folding
     * it to `[]` would print "no credit" on every account for a reader nobody
     * showed a balance to. Carried on the Leads row too (one shared mapper) but
     * declared only on the Companies card.
     */
    creditBalances: company.creditBalances ?? null,
    // `undefined` (an older server) folds to `null` inside the helper — never `[]`.
    purchaseHistory: purchaseHistoryOf(company),
    /*
     * ⛔ **ASSIGNED, NEVER `?? false`** — see the field. The flag is
     * server-computed and always present; defaulting it would launder an unread
     * response into a confident "no open task", at the one boundary where the
     * difference is still observable.
     */
    hasOpenTask: company.hasOpenTask,
    latestNote: latestNoteOf(company.latestNote),
    // ⛔ Assigned, never coalesced — `undefined` (older server) and `null` (no
    // note) are different facts. See the field.
    accountLastNote: company.accountLastNote,
    href: `/companies/${company.id}`,
    activityTarget: { type: 'company', id: company.id },
    updatedAt: text(company.updatedAt),
    // `?? null`, never `?? 0`: an absent key is an older server, not "no contacts".
    contactCount: company.contactCount ?? null,
  };
}

function dealRow(deal: Deal): PartnershipRow {
  return {
    id: deal.id,
    accountLead: accountLeadOf(deal.accountLead),
    /*
     * ⚠️ **NOT `deal.id`** — the row is a deal and the handle is its ACCOUNT.
     * `null` when the deal names no company, which is a legal state on
     * `d_deals`; see the field for why the edit is then withheld rather than
     * offered.
     */
    companyId: deal.company?.id ?? null,
    primary: primaryOf(deal.dealName, deal.id),
    /*
     * ⚠️ The company's NAME, for the reader. It is a label and never a handle —
     * `companyId` above is the half an edit sends, and the two are assigned from
     * the same embed for that reason.
     */
    secondary: text(deal.company?.name),
    // Verbatim: `undefined` (older API) and `null` (not signed) stay apart.
    quoteSigned: deal.quoteSigned,
    /*
     * `dealValue`, not `deal.amount` — owner's ruling, 10 September 2026: a
     * deal's value is its LINE TOTAL whenever it has lines, and the typed
     * `amount` only when it has none. This row is the FOURTH surface reading
     * that figure, after the deals list, the deal detail page and the deal
     * form; a dashboard quoting a different number from the list it links to
     * is the disagreement the ruling exists to remove.
     *
     * ⚠️ `GET /deals` already carries `linesTotal`, so this costs no request.
     */
    meta: moneyMeta(dealValue(deal), deal.currency) ?? text(deal.stage?.name),
    /* A deal stage is `f_deal_stages` — `isWon`/`isLost`, a third master. See above. */
    status: null,
    /*
     * ⚠️ A deal's own stage is that third master, and `companyStage` is a FOURTH
     * — `f_company_stages`, on the account. The deals card renders neither as a
     * company stage, so this is the concept being absent rather than a value
     * withheld. ⛔ Do not populate it from `deal.stage`: the shapes are
     * compatible and the masters are not.
     */
    companyStage: null,
    stageStatus: null,
    followUpOn: null,
    /*
     * A deal has no due date. `closingDate` is a FORECAST somebody moves, not a
     * date money is owed on, so it is not this field under another name.
     */
    dueDate: null,
    /*
     * Only a company carries a country of record — the label and the handle
     * alike. The embed on a deal carries the account's id, name and projects of
     * interest, and no country, so there is nothing to read even through it.
     */
    country: null,
    countryId: null,
    /*
     * Only a company carries a source — the label and the handle alike, and the
     * embed on a deal does not relay one. ⛔ Never fill this from
     * `deal.leadSource` or a contact's `leadSourceRef`: those are
     * `d_lead_sources`, a different master answering where a DEAL came from, and
     * both are `{id, name}`, so the substitution would compile and print a
     * plausible word under a heading about the account.
     */
    source: null,
    sourceId: null,
    /*
     * Only a company carries a type — the label and the handle alike, and the
     * embed on a deal does not relay one. ⛔ Never fill this from a deal's own
     * `dealType`: that is `d_deal_types`, a different master answering what kind
     * of DEAL this is, and it is `{id, name}` too — so the substitution would
     * compile and print a plausible word under a heading about the institution.
     * The same trap `source` directly above states against `d_lead_sources`.
     */
    companyType: null,
    companyTypeId: null,
    /*
     * 🔴 **POPULATED SINCE 2026-09-12, THROUGH THE COMPANY EMBED.**
     *
     * ⚠️ *SUPERSEDED: "Only a company is pursued for projects. ⚠️ `null` because
     * the CONCEPT is absent from this record, not because the list happens to be
     * empty — `[]` here would claim a deal had been checked for projects of
     * interest and had none. The two cards that render the column never map a
     * deal."* **The first sentence is still true and is the whole reason this
     * expression is shaped the way it is; the last one is not — the deals card
     * renders the column now.**
     *
     * **A deal is still not pursued for projects. Its ACCOUNT is**, and the deal
     * reaches the account's list through {@link DocumentCompanyRef}. So the
     * concept was never the deal's and has not become the deal's; what changed is
     * that the wire now carries the account's answer on the deal's row.
     *
     * ⛔ **`?? null`, and NEVER `?? []`.** The optional chain yields `undefined`
     * exactly when the deal names no company — which is *nobody to have any*, not
     * *an account with none*. `[]` there would make the reasoning the superseded
     * text gives come true after all: a claim that this account was checked and
     * had none. The two states render the same empty cell and only one of them is
     * a statement.
     */
    projectsOfInterest: deal.company?.projectsOfInterest ?? null,
    /*
     * 🔴 **THE DEAL'S OWN ANSWER, and the field directly above is the ACCOUNT's —
     * both are carried, only this one is rendered here since 2026-09-14.**
     *
     * ⚠️ **Kept side by side deliberately, and they are one keystroke apart.**
     * `projectsOfInterest` stays populated because it is still correct and still
     * free — it rides on an embed this row already has — and because nulling it
     * would give its own `null` a THIRD meaning ("the card does not ask"), on a
     * field whose contract says `null` is *nobody to have any*. What stopped is
     * the deals card DECLARING that column, which is the only gate either field
     * has ever had; see `DOCUMENT_CARD_COLUMNS`.
     *
     * ⛔ **So a page must not render `projectsOfInterest` on this card, and the
     * compiler will not say so.** Both are `{id, name}[]`, both go through
     * `companyProjectsLabel`, and the wrong one under *"Projects on this
     * document"* is a confident wrong answer about what a deal sells.
     *
     * ⚠️ **`lineProjectsOf`, not a bare read** — `?? null` and never `?? []`, for
     * the reason on that function: `[]` is the commonest true answer here, so an
     * unread response defaulted to it is invisible.
     */
    lineProjects: lineProjectsOf(deal.lineProjects),
    // Not a company: the concept is absent, and no document card declares it.
    creditBalances: null,
    purchaseHistory: null,
    /* ⛔ Assigned, never `?? false` — see the field. */
    hasOpenTask: deal.hasOpenTask,
    latestNote: latestNoteOf(deal.latestNote),
    // Not on a deal row — the deals card keeps "Your last note". See the field.
    accountLastNote: undefined,
    href: `/deals/${deal.id}`,
    activityTarget: { type: 'deal', id: deal.id },
    updatedAt: text(deal.updatedAt),
    contactCount: null,
  };
}

function quoteRow(quote: Quote): PartnershipRow {
  return {
    id: quote.id,
    accountLead: accountLeadOf(quote.accountLead),
    // The house title for a quote: its subject, plus Xero's number once Xero
    // has issued one. Composed at render time, never stored — the reasoning is
    // on `documentDisplayTitle`.
    /* ⚠️ The ACCOUNT's id, not the quote's — see `dealRow` and the field. */
    companyId: quote.company?.id ?? null,
    primary: primaryOf(documentDisplayTitle(quote.subject, quote.xeroQuoteNumber), quote.id),
    secondary: text(quote.company ? documentCustomerName(quote.company) : null),
    documentNumber: quote.quoteNumber,
    meta: moneyMeta(quote.grandTotal, quote.currency),
    /*
     * Emitted verbatim and UNRESOLVED — `quote.stage` is `{id, name}` with no
     * `isAccepted` / `isDeclined` on it. The page resolves it against the
     * `/quote-stages` master through `StageBadge`.
     */
    status: quote.stage === null ? null : { kind: 'quote-stage', ref: quote.stage },
    /*
     * ⚠️ `quote.stage` is a `/quote-stages` row and went into `status` above;
     * this field is an `f_company_stages` row, on the account. Two masters, two
     * fields, and the shapes are identical — which is why the swap would compile.
     */
    companyStage: null,
    stageStatus: null,
    followUpOn: null,
    /*
     * A quote has no due date — `d_quotes` carries `issue_date` and
     * `valid_till`, and neither is a date payment falls due on. Expiry is not
     * overdueness.
     */
    dueDate: null,
    /* Only a company carries a country of record; the embed does not relay one. */
    country: null,
    countryId: null,
    /* Only a company carries a source; the embed does not relay one. See `dealRow`. */
    source: null,
    sourceId: null,
    /* Only a company carries a type; the embed does not relay one. See `dealRow`. */
    companyType: null,
    companyTypeId: null,
    /*
     * The ACCOUNT's projects, reached through the embed — populated since
     * 2026-09-12. ⛔ `?? null` and never `?? []`: `undefined` here is a quote
     * naming no company, which is *nobody to have any*. The full reasoning, and
     * the sentence it supersedes, are on `dealRow`.
     */
    projectsOfInterest: quote.company?.projectsOfInterest ?? null,
    /*
     * The QUOTE's own line projects — what this document sells, where the field
     * above is what its account is being chased for. Both carried, only this one
     * rendered on this card since 2026-09-14. The full reasoning, including why
     * the other is kept rather than nulled, is on `dealRow`.
     *
     * ⛔ `lineProjectsOf`, so `?? null` and never `?? []`: on quotes `[]` is not
     * merely common, it is the answer for most of them.
     */
    lineProjects: lineProjectsOf(quote.lineProjects),
    creditBalances: null,
    purchaseHistory: null,
    /* ⛔ Assigned, never `?? false` — see the field. */
    hasOpenTask: quote.hasOpenTask,
    latestNote: latestNoteOf(quote.latestNote),
    accountLastNote: undefined,
    href: `/quotes/${quote.id}`,
    activityTarget: { type: 'quote', id: quote.id },
    updatedAt: text(quote.updatedAt),
    contactCount: null,
  };
}

function invoiceRow(invoice: Invoice): PartnershipRow {
  return {
    id: invoice.id,
    accountLead: accountLeadOf(invoice.accountLead),
    /* ⚠️ The ACCOUNT's id, not the invoice's — see `dealRow` and the field. */
    companyId: invoice.company?.id ?? null,
    primary: primaryOf(
      documentDisplayTitle(invoice.subject, invoice.xeroInvoiceNumber),
      invoice.id,
    ),
    secondary: text(invoice.company ? documentCustomerName(invoice.company) : null),
    meta: moneyMeta(invoice.grandTotal, invoice.currency),
    /*
     * Unresolved, as on the quote row: `invoice.status` is `{id, name}` with no
     * `isSettled` / `isPaid`. `StatusBadge` resolves it against
     * `/invoice-statuses`, which is also what `isOverdue()` reads — so the tone
     * and any overdue claim cannot end up contradicting each other.
     */
    status: invoice.status === null ? null : { kind: 'invoice-status', ref: invoice.status },
    /*
     * The only table that has one. Emitted RAW — a plain `YYYY-MM-DD`, never a
     * `Date` (`new Date('2026-05-31')` is midnight UTC and renders as the 30th
     * west of Greenwich) and never an overdue verdict, which needs the
     * `/invoice-statuses` master this module does not read. See the field.
     */
    dueDate: text(invoice.dueDate),
    /*
     * ⚠️ `invoice.status` is an `/invoice-statuses` row and went into `status`
     * above; this field is an `f_company_stages` row, on the account. Both are
     * `{id, name}`, so only the two field names keep them apart.
     */
    companyStage: null,
    stageStatus: null,
    followUpOn: null,
    /* Only a company carries a country of record; the embed does not relay one. */
    country: null,
    countryId: null,
    /* Only a company carries a source; the embed does not relay one. See `dealRow`. */
    source: null,
    sourceId: null,
    /* Only a company carries a type; the embed does not relay one. See `dealRow`. */
    companyType: null,
    companyTypeId: null,
    /*
     * The ACCOUNT's projects, reached through the embed — populated since
     * 2026-09-12. ⛔ `?? null` and never `?? []`: `undefined` here is an invoice
     * naming no company, which is *nobody to have any*. The full reasoning, and
     * the sentence it supersedes, are on `dealRow`.
     */
    projectsOfInterest: invoice.company?.projectsOfInterest ?? null,
    /*
     * The INVOICE's own line projects — what this document bills for, where the
     * field above is what its account is being chased for. Both carried, only
     * this one rendered on this card since 2026-09-14; see `dealRow`.
     *
     * ⛔ `lineProjectsOf`, so `?? null` and never `?? []`.
     */
    lineProjects: lineProjectsOf(invoice.lineProjects),
    creditBalances: null,
    purchaseHistory: null,
    /* ⛔ Assigned, never `?? false` — see the field. */
    hasOpenTask: invoice.hasOpenTask,
    latestNote: latestNoteOf(invoice.latestNote),
    accountLastNote: undefined,
    href: `/invoices/${invoice.id}`,
    activityTarget: { type: 'invoice', id: invoice.id },
    updatedAt: text(invoice.updatedAt),
    contactCount: null,
  };
}


/* --- the five tables ------------------------------------------------------ */

interface TablePage {
  rows: PartnershipRow[];
  /** The server's own count for the whole filtered table, not `rows.length`. */
  total: number;
  /** The Companies card only: the stage ids its request was scoped to. */
  stageStatusStageIds?: ReadonlySet<number>;
}

function pageOf<T>(result: Paginated<T>, toRow: (record: T) => PartnershipRow): TablePage {
  return { rows: result.data.map(toRow), total: result.meta.total };
}

/**
 * `stageId` for the Companies table: every ACTIVE stage that is not the lead
 * stage, comma-separated.
 *
 * ## Why it is derived rather than written down
 *
 * `f_company_stages` is an admin-editable master. It holds 1 Lead, 2 Prospect,
 * 3 Client, 4 Past Client today, and a hard-coded `'2,3,4'` would be a client
 * asserting what production's id counter happened to hold on the day it was
 * typed. A fifth stage was explicitly designed to be a row somebody adds rather
 * than a migration — so the moment one is added, a written-down list silently
 * omits every company in it, with a full-looking table and nothing failing.
 *
 * The complement is derived from the master for that reason, and it stays
 * anchored to the ONE id this module already depends on.
 *
 * ## Every member is validated before it reaches the CSV
 *
 * A comma-separated `stageId` is parsed by `/companies` and by nothing else on
 * this API — legal here, a 422 "expected number, received NaN" everywhere else —
 * and a single bad member fails the WHOLE request, not just its own term. So
 * each id is checked to be a positive safe integer before it is joined. That is
 * the same class of bug as `Number("1,2")` being `NaN`: it reads as an active
 * filter in the code and is absent from, or fatal to, the request.
 *
 * ## ⚠️ It REFUSES rather than degrading, and that is the whole point
 *
 * Two ways this can fail to produce a filter, and both `throw`:
 *
 *  - the master could not be read — the rejection propagates untouched, so the
 *    server's own sentence is what the card reports;
 *  - the master was read and every non-lead stage is retired, so there is no
 *    filter to express.
 *
 * Neither may fall back to an unfiltered fetch. An unfiltered Companies table
 * is not a degraded version of this one: it is the exact bug this change
 * removes — every lead listed twice on one page — and it looks completely
 * normal, so nobody would report it. Sending an empty `stageId=` is no better;
 * it is a 422 on a strict schema, or "no filter" dressed as one.
 *
 * The card therefore takes the table's existing error state, which is rendered
 * as its own danger-toned block above the rows and never substituted for the
 * empty state. A failed derivation shows a failure; it never shows a table
 * quietly claiming a scope it does not have.
 *
 * ## The gap this leaves, named rather than papered over
 *
 * Reads never filter on `isActive`, so a company can sit on a RETIRED non-lead
 * stage. Such a company is on neither card: the Leads table asks for the lead
 * id alone, and this one asks for active ids alone. Retired ids are not sent
 * because their acceptance as a filter VALUE is unmeasured, and one refused
 * member takes the whole request with it. It is a real hole and it is small;
 * `/companies` is the unfiltered list and shows those rows.
 */
async function nonLeadStageFilter(signal: AbortSignal): Promise<string> {
  const master = await api.listCompanyStages(COMPANY_STAGE_QUERY, signal);

  const ids = master.data
    .filter((stage) => stage.isActive && stage.id !== LEAD_COMPANY_STAGE_ID)
    .map((stage) => stage.id)
    .filter((id) => Number.isSafeInteger(id) && id > 0);

  if (ids.length === 0) {
    throw new Error(
      'Every company stage other than the lead stage is retired, so this table cannot be scoped.',
    );
  }

  return ids.join(',');
}

/**
 * 🔴 **WHICH COLUMN EACH CARD SORTS ALPHABETICALLY BY. The one place this is
 * decided.**
 *
 * Four endpoints answer the same question and spell it three ways —
 * `/companies` takes `name`, `/deals` takes `dealName`, and `/quotes` and
 * `/invoices` take `subject`. **There is no shared `name` alias and asking for
 * one is a 422 on three of the four.** The backend refused the alias
 * deliberately: on an invoice it would have to mean `subject`, and nothing on
 * the wire would then say which column had actually been sorted.
 *
 * ⚠️ **Written down ONCE, because five copies would agree today and drift on
 * the first correction** — and the drift is silent in the worst direction. A
 * card sending the wrong spelling gets a 422 naming `sort`, which surfaces as
 * that one card failing to load while the other four look perfect, so it reads
 * as a flaky endpoint rather than as a wrong constant.
 *
 * `as const` is load-bearing: it makes each entry a literal type rather than
 * `string`, so `AZ_SORT_KEY.deals` is `'dealName'` and passing it where a
 * `QuoteListSort` is wanted is a `tsc` error. That is what stops a card
 * reaching into the wrong row of this table.
 *
 * ⚠️ **The one substitution `tsc` CANNOT catch is quotes ↔ invoices**, whose
 * unions are member-for-member identical, so `AZ_SORT_KEY.quotes` inside the
 * invoices loader compiles and behaves correctly today. It is recorded rather
 * than guarded because the guard would be a second copy of the thing this
 * constant exists to have only one of — and if the two endpoints ever diverge,
 * the substitution becomes a `tsc` error on its own.
 */
const AZ_SORT_KEY = {
  leads: 'name',
  companies: 'name',
  deals: 'dealName',
  quotes: 'subject',
  invoices: 'subject',
} as const satisfies Record<PartnershipTableKey, string>;

/**
 * 🔴 **WHICH COLUMN THE STATUS HEADER SORTS BY — and it is a DIFFERENT WORD on
 * the two endpoints that have one.**
 *
 * `/quotes` takes `stage`; `/invoices` takes `status`. Same idea, two columns
 * (`d_quotes.stage_id`, `d_invoices.status_id`), so the backend gave them two
 * keys deliberately — `src/lib/types.ts` states the rule at the two unions:
 * *same idea + same column ⇒ same key; same idea + different columns ⇒ different
 * keys.* Sending one to the other endpoint is a **422 naming the unrecognised
 * key** on a `.strict()` schema, which shows up as one card failing to load
 * beside four that look perfect.
 *
 * ⚠️ **Only two entries, and that is the contract rather than an omission.**
 * There is no such key on `/companies` or `/deals`: a company's stage and a
 * deal's stage are two other masters, and neither card renders a `status`
 * column to click. A `Partial` record is what makes that a `tsc` question at
 * `orderParamsFor` instead of a runtime one.
 *
 * ⚠️ **Two properties of this ordering that a header must not misdescribe**,
 * both from `types.ts`:
 *
 *  - it orders by the reference row's own `sortOrder`, **not alphabetically** —
 *    see {@link PARTNERSHIP_SORT_LABEL};
 *  - both columns are **NULLABLE and the server coalesces no sentinel**, so
 *    MariaDB puts NULLs first ascending and last descending. `Status order` on
 *    a card holding unstatused documents therefore OPENS on them. That is the
 *    ordering, not a fault, and it is the same contract `?sort=startDate` on
 *    `/projects` has.
 */
const STATUS_SORT_KEY = {
  quotes: 'stage',
  invoices: 'status',
} as const satisfies Partial<Record<PartnershipTableKey, string>>;

/**
 * 🔴 **WHICH COLUMN THE COUNTRY HEADER SORTS BY — added 2026-09-12, backend
 * `0e30700`, after `/companies` gained a fourth `sort` member.**
 *
 * ⚠️ **A BARE STRING RATHER THAN A PER-CARD MAP, and the difference from its two
 * neighbours is the whole point.** `AZ_SORT_KEY` and {@link STATUS_SORT_KEY} are
 * maps because the same idea is a DIFFERENT COLUMN on each endpoint — `name` vs
 * `dealName` vs `subject`, `stage` vs `status`. Country is one column on ONE
 * endpoint: both cards that offer it are `/companies` reads, so a two-entry map
 * would state the same word twice and invite a third entry for a card whose
 * schema has no such key. Which cards may send it is decided by
 * `SortKeys.country` being optional, not by this constant.
 *
 * ⚠️ **`country`, not `countryId`.** The FILTER is `?countryId=` and the SORT is
 * `?sort=country`; they are one word apart and name different things, and the
 * schema is `.strict()`, so the wrong one is a 422 that surfaces as one card
 * failing to load beside four that look perfect.
 *
 * ⚠️ **It orders on the country's NAME, not its id** — the backend resolves this
 * to `co.country_name` through the join its page SELECT already carries, and
 * says so at the enum. Nothing here may imply the ordering is by id.
 */
const COUNTRY_SORT_KEY = 'country' as const;

/**
 * 🔴 **WHICH COLUMN THE SOURCE HEADER SORTS BY — wired 2026-09-14, on a member
 * `/companies` has declared since the column shipped.**
 *
 * ⚠️ **THIS CLOSES A GAP THAT NOTHING COULD FAIL ON.** The source column landed
 * filterable and not sortable: `?sort=source` was live on the wire, the funnel
 * was drawn, and no caret ever appeared — a capability present at both ends and
 * absent in the middle. **No build, type or test can see a missing entry in a
 * map**, which is why the page-side maps this pairs with were made exhaustive in
 * the same change.
 *
 * ⚠️ **A BARE STRING, like {@link COUNTRY_SORT_KEY} and for the same reason** —
 * one column on ONE endpoint, and both cards that offer it are `/companies`
 * reads. Which cards may send it is decided by `SortKeys.source` being optional,
 * not by this constant.
 *
 * ⚠️ **`source`, not `sourceId`.** The FILTER is `?sourceId=` and the SORT is
 * `?sort=source`; one word apart, `.strict()` on both, so the wrong one is a 422
 * that takes the whole card out rather than a no-op.
 *
 * ⚠️ **It orders the source's NAME (`src.name`), not its id** — an id ordering
 * looks alphabetical at a glance and is not, which reads as bad data.
 */
const SOURCE_SORT_KEY = 'source' as const;

/**
 * 🔴 **WHICH COLUMN THE COMPANY-TYPE HEADER SORTS BY — 2026-09-14, shipped with
 * the column.**
 *
 * ⛔ **`companyType`, AND THE FILTER BESIDE IT IS `companyTypeId`.** This is the
 * one place on this page where the sort key and the filter key differ by a
 * SUFFIX rather than by a whole word, which makes them the easiest pair here to
 * transpose — and `src/lib/types.ts` states at {@link CompanyListSort} that
 * `?sort=companyTypeId` is refused. ⚠️ **Nor is it `type`**: the long spelling is
 * deliberate on BOTH wire keys, for the reason {@link Company.companyType}
 * gives.
 *
 * ⚠️ **A BARE STRING**, like its two neighbours and for the same reason: one
 * column, one endpoint, and `SortKeys.companyType` being optional is what decides
 * which cards may send it.
 *
 * ⚠️ **It orders the type's NAME, never `company_type_id`** — the same ruling
 * `country` and `source` carry, and the same failure if ignored: a sequence that
 * looks alphabetical and is not.
 */
const COMPANY_TYPE_SORT_KEY = 'companyType' as const;

/**
 * 🔴 **THE MONEY COLUMN — `amount` on `/deals`, `/quotes` AND `/invoices`,
 * shipped 2026-09-13.**
 *
 * ⚠️ **A BARE STRING, like {@link COUNTRY_SORT_KEY} and unlike its two other
 * neighbours** — and here the sameness is a measured fact rather than a
 * convenience. `AZ_SORT_KEY` and {@link STATUS_SORT_KEY} are maps because the
 * same idea really is a different column per endpoint (`dealName` vs `subject`,
 * `stage` vs `status`). All three document schemas spell this one `amount`, so a
 * three-entry map would state one word three times and invite a fourth entry for
 * a card whose schema has no such key. **Which cards may send it is decided by
 * `SortKeys.value` being optional, not by this constant.**
 *
 * 🔴 **ONE WORD, TWO DIFFERENT SQL EXPRESSIONS BEHIND IT, AND THAT IS
 * DELIBERATE.** On quotes and invoices the server orders `grand_total`; on deals
 * it orders **`COALESCE(lines_total, d.amount)`** — the line total where lines
 * exist, the typed amount otherwise, which is `dealValue()`'s rule written as
 * SQL. **So the same member means "the figure the row shows" on all three**,
 * which is the property that matters, rather than "the same column on all
 * three", which would have been wrong on deals. Do not read the shared spelling
 * as a shared column.
 *
 * ⛔ **THE TWO `/companies` CARDS MUST NEVER OFFER THIS**, and their Value
 * column is exactly why it looks as though they could: that column holds a
 * company STAGE, not money — the page tells the two apart through a declared
 * `VALUE_COLUMN_KIND` map and never through the admin-facing `valueLabel`.
 * Neither `/companies` schema declares an `amount` member, so sending one is a
 * 422 naming the key; the optional `SortKeys.value` is what stops it being sent.
 */
const VALUE_SORT_KEY = 'amount' as const;

/**
 * 🔴 **THE INVOICE DUE DATE — `/invoices` ALONE, shipped 2026-09-13.**
 *
 * ⚠️ **`dueDate`, NOT `billDate`.** `/invoices` has sorted by `billDate` since
 * 2026-09-02 and both members are live: bill date is when the invoice was
 * raised, due date when it must be paid. This constant is an ADDITION, and
 * repointing it at the other word would silently change what every saved link
 * and column control means while answering 200 throughout.
 *
 * ⛔ **THE QUOTES CARD CANNOT HAVE THIS, and it is not symmetry waiting to be
 * finished:** `d_quotes` has no due-date column at all. Its near-neighbour is
 * `validTill`, which answers a different question (when the OFFER lapses, not
 * when money is owed) and is a separate decision nobody has taken.
 */
const DUE_DATE_SORT_KEY = 'dueDate' as const;

/**
 * 🔴 **THE OPEN-TASK FLAG — `openTask` on ALL FOUR endpoints, shipped
 * 2026-09-13.**
 *
 * ⚠️ **The only key here every one of the five cards can send**, so unlike its
 * three neighbours the optionality on `SortKeys` buys nothing today. It is
 * optional anyway, for the reason the whole family is: a card that does not
 * supply the key cannot emit the ordering whatever ends up in its URL, and a
 * sixth card over a fifth endpoint is one spec away.
 *
 * ⚠️ **It orders the PROJECTED `has_open_task` alias, an `EXISTS` — so it is 0
 * or 1 and never NULL**, which makes it the one new member with no
 * NULLs-first question at all. `asc` is therefore *no open task first*, which is
 * why the label reads "Open task last".
 *
 * ⛔ **A client must never re-derive this from the row.** The flag spreads across
 * the whole account — the company and its deals, quotes, invoices and
 * deliverables — so `row.hasOpenTask` and "this record has a task" are different
 * questions, and the server's answer is the wider one.
 */
const OPEN_TASK_SORT_KEY = 'openTask' as const;

/**
 * `/companies` `?sort=lastNoteAt` — the account's last human note, by date
 * (2026-10-02). The two company cards alone: no document endpoint declares it.
 *
 * ⚠️ **Accounts with no note sort LAST in both directions** — an explicit
 * `IS NULL` term server-side, deliberately unlike the other nullable sorts here.
 */
const LAST_NOTE_SORT_KEY = 'lastNoteAt' as const satisfies CompanyListSort;

/*
 * 2026-10-09 (backend `32057ed`). Each pinned with `satisfies` against the
 * endpoint's own sort union, so a misspelling is a `tsc` error rather than a
 * 422 that takes a whole card out.
 *
 *  - `stage` — the Companies card's Value column, a company STAGE ordered by
 *    the master's `sortOrder`. ⛔ Never `amount`: `/companies` has no money.
 *  - the four count keys order a SET by its number of distinct members. On
 *    `/companies`, `projectsParticipated` and `productsPurchased` count within
 *    `participatedCycleId` / `purchasedCycleId` when those are sent.
 *  - `lineProjects` is one spelling on all three document endpoints, so the
 *    intersection of their unions pins it on all three at once.
 */
const COMPANY_STAGE_SORT_KEY = 'stage' as const satisfies CompanyListSort;
const PROJECTS_OF_INTEREST_SORT_KEY = 'projectsOfInterest' as const satisfies CompanyListSort;
const PROJECTS_PARTICIPATED_SORT_KEY = 'projectsParticipated' as const satisfies CompanyListSort;
const PRODUCTS_PURCHASED_SORT_KEY = 'productsPurchased' as const satisfies CompanyListSort;
/** 2026-10-25 — per-stage status order; always paired with `sortStageId`. */
const STAGE_STATUS_SORT_KEY = 'stageStatus' as const satisfies CompanyListSort;
const LINE_PROJECTS_SORT_KEY = 'lineProjects' as const satisfies DealListSort &
  QuoteListSort &
  InvoiceListSort;
/** 2026-10-27 — `/deals` only: by the signed quote's date, unsigned last. */
const QUOTE_SIGNED_SORT_KEY = 'quoteSigned' as const satisfies DealListSort;
/** 2026-10-27 — one spelling on all four endpoints: the resolved lead's name. */
const ACCOUNT_LEAD_SORT_KEY = 'accountLead' as const satisfies CompanyListSort &
  DealListSort &
  QuoteListSort &
  InvoiceListSort;

/**
 * The query keys that express one ordering, for ONE card.
 *
 * ⚠️ **This replaced a `companySortParams` / `documentSortParams` PAIR, and the
 * pair is what made the A–Z key easy to get wrong.** Two functions meant two
 * places that knew how to name a column, and the second one did not name a
 * column at all — so extending A–Z to the document cards through that shape
 * would have meant either a third function or a `switch` on the card, and five
 * cards mapping to three different spellings of "the name column" is precisely
 * the thing that must not be written down more than once.
 *
 * The KEY is not decided here. It arrives as `azKey`, and every caller reads it
 * out of {@link AZ_SORT_KEY} — the one table of which column each card sorts
 * by — so the mapping is one `const` and this one translation.
 *
 * ⚠️ **`activityFirst` and `sort` are never both returned, and that is the
 * other half of this function's job.** They do not conflict server-side — they
 * COMPOSE, with `activityFirst` prefixed in front of the chosen column — which
 * is precisely why sending both is dangerous rather than merely redundant: the
 * request succeeds, the response is ordered by activity, and a control reading
 * "A–Z" sits above it. The return type says so structurally: every arm names
 * the keys the other arms must not carry, so a fourth arm emitting both is a
 * `tsc` error rather than a silent lie on screen.
 *
 * `activity` deliberately sends no `sort`: the ranking prefix decides the order
 * and whatever follows it is only a tie-break, so naming a column here would
 * imply a say it does not have.
 *
 * ⚠️ **`newest` returns the EMPTY object, and the emptiness is the point** —
 * `id DESC` is what every one of these repositories does when nothing is asked
 * of it, so the way to request it is to request nothing. Sending
 * `sort=id&order=desc` would be the same rows in the same order plus a
 * redundant pair in the URL.
 */
type OrderParams<K extends string> =
  | { activityFirst: true; sort?: undefined; order?: undefined; sortStageId?: undefined }
  | { activityFirst?: undefined; sort: K; order: ListOrder; sortStageId?: number }
  | { activityFirst?: undefined; sort?: undefined; order?: undefined; sortStageId?: undefined };

/**
 * The column names one card may ask for, read out of {@link AZ_SORT_KEY} and
 * {@link STATUS_SORT_KEY} at the call site.
 *
 * ⚠️ **`status` is OPTIONAL because three of the five endpoints have no such
 * key**, and that optionality is the guard: a card that does not supply one
 * cannot emit a status ordering, whatever ends up in its URL.
 */
interface SortKeys<K extends string> {
  az: K;
  status?: K;
  /**
   * ⚠️ **OPTIONAL for the same reason `status` is, and it is the mirror image of
   * it.** `country` exists on the two `/companies` cards and on no other list
   * schema here; `status` exists on quotes and invoices and on neither
   * `/companies` card. A card that does not supply the key cannot emit the
   * ordering, whatever ends up in its URL — which is the guard, not the type
   * being polite.
   */
  country?: K;
  /**
   * ⚠️ **The two `/companies` cards alone, exactly like `country` above** — a
   * source is a column on `d_companies` and on no document table, so the three
   * document cards supply no key and cannot emit the ordering whatever ends up in
   * their URL. See {@link SOURCE_SORT_KEY}.
   */
  source?: K;
  /**
   * ⚠️ **The two `/companies` cards alone**, for the identical reason `source`
   * directly above states. See {@link COMPANY_TYPE_SORT_KEY}, and note the key it
   * carries is `companyType` where the FILTER is `companyTypeId`.
   */
  companyType?: K;
  /**
   * ⚠️ **Absent on the two `/companies` cards, and their Value COLUMN is exactly
   * why that looks like an omission.** That column holds a company stage; only
   * the three document cards render money. See {@link VALUE_SORT_KEY}.
   */
  value?: K;
  /**
   * ⚠️ **The narrowest of the four — `/invoices` and nothing else.** `d_quotes`
   * has no due-date column, so this is a schema fact rather than a card that has
   * not been given it yet. See {@link DUE_DATE_SORT_KEY}.
   */
  dueDate?: K;
  /**
   * ⚠️ **Every card supplies this one**, so today it narrows nothing — kept
   * optional for the reason the family is, not because a card is missing it.
   */
  openTask?: K;
  /**
   * ⚠️ **The two `/companies` cards alone** — see {@link LAST_NOTE_SORT_KEY}.
   * A card that does not supply it cannot emit the ordering.
   */
  lastNote?: K;
  /** The Companies card alone — its Value column holds a company stage. */
  stage?: K;
  /** The two `/companies` cards — the count of projects of interest. */
  projectsOfInterest?: K;
  /** The Companies card alone — the two purchase-history counts. */
  projectsParticipated?: K;
  productsPurchased?: K;
  /** The three document cards — the count of projects their lines name. */
  lineProjects?: K;
  /** The DEALS card alone — the signed quote's date (2026-10-27). */
  quoteSigned?: K;
  /** All five cards — the resolved lead's name (2026-10-27). */
  accountLead?: K;
  /**
   * The two `/companies` cards — `sort=stageStatus`, sent WITH `sortStageId`
   * (the server refuses one without the other).
   */
  stageStatus?: K;
}

/** One optional key and its asc/desc pair, for the arms added 2026-10-09. */
function keyedOrder<K extends string>(
  key: K | undefined,
  ascending: boolean,
): OrderParams<K> {
  // Same fallback as every pair below: a card that supplies no key cannot emit
  // the ordering, whatever ends up in its URL.
  if (key === undefined) return { activityFirst: true };
  return { sort: key, order: ascending ? 'asc' : 'desc' };
}

function orderParamsFor<K extends string>(
  sort: PartnershipSort,
  keys: SortKeys<K>,
): OrderParams<K> {
  const stage = parseStageStatusSort(sort);
  if (stage !== null) {
    // Same fallback as every pair: a card with no key cannot emit the ordering.
    if (keys.stageStatus === undefined) return { activityFirst: true };
    return {
      sort: keys.stageStatus,
      order: stage.ascending ? 'asc' : 'desc',
      sortStageId: stage.stageId,
    };
  }
  switch (sort as StaticPartnershipSort) {
    case 'az':
      return { sort: keys.az, order: 'asc' };
    case 'za':
      return { sort: keys.az, order: 'desc' };
    case 'status-asc':
    case 'status-desc':
      /*
       * ⚠️ **The `undefined` branch falls back to the DEFAULT rather than
       * omitting `sort`, and the difference matters.** Omitting it would request
       * `id DESC` — a real, different order — under whatever the control says,
       * where the fallback lands on the same ordering `resolvePartnershipSort`
       * already gives an unusable URL value. One unusable choice, one outcome.
       *
       * It is unreachable through the UI: the status pair is absent from these
       * cards' `sortOptions`, so `resolvePartnershipSort` never returns it and
       * no header renders a control for it. This is the belt to that brace —
       * `?dealsSort=status-asc` typed by hand, or a spec that gains the option
       * without the key.
       */
      if (keys.status === undefined) return { activityFirst: true };
      return { sort: keys.status, order: sort === 'status-asc' ? 'asc' : 'desc' };
    case 'country-asc':
    case 'country-desc':
      /*
       * ⚠️ **Same fallback as the status pair, for the same reason** — see the
       * block above. Unreachable through the UI (the pair is absent from the
       * three document cards' `sortOptions`), so this is the belt to that
       * brace: `?dealsSort=country-asc` typed by hand, or a spec that gains the
       * option without the key.
       *
       * ⚠️ **`country_id` IS NULLABLE and the server coalesces no sentinel**, so
       * MariaDB puts the NULLs first ascending — "Country A–Z" therefore OPENS
       * on the companies nobody has classified. That is the ordering rather than
       * a fault, and it is the same contract the status pair above and
       * `?sort=startDate` on `/projects` already have. Do not "fix" it with a
       * client-side re-sort: this module refuses client-side ordering outright,
       * and the reason is written out at `PER_PAGE`.
       */
      if (keys.country === undefined) return { activityFirst: true };
      return { sort: keys.country, order: sort === 'country-asc' ? 'asc' : 'desc' };
    case 'source-asc':
    case 'source-desc':
      /*
       * ⚠️ **Same fallback as the pairs above, for the same reason.** Unreachable
       * through the UI — the pair is absent from the three document cards'
       * `sortOptions` — so this is the belt to that brace: `?dealsSort=source-asc`
       * typed by hand, or a spec that gains the option without the key.
       *
       * ⚠️ **`source_id` IS NULLABLE with no sentinel coalesced**, so ascending
       * OPENS on the companies nobody has recorded a source for — which on this
       * column is most of the table, so the first screen looks emptier than the
       * ordering is wrong. That is the ordering rather than a fault, and ⛔ a
       * client-side re-sort is refused outright here; the reason is at `PER_PAGE`.
       */
      if (keys.source === undefined) return { activityFirst: true };
      return { sort: keys.source, order: sort === 'source-asc' ? 'asc' : 'desc' };
    case 'company-type-asc':
    case 'company-type-desc':
      /*
       * ⚠️ **Same fallback again**, and this arm guards the key most easily
       * transposed on the whole page: the SORT is `companyType` and the FILTER is
       * `companyTypeId`, one suffix apart on one endpoint. Sending the filter's
       * spelling here would be a 422 naming the key and would take the card out.
       *
       * ⚠️ **`company_type_id` is NULLABLE with no sentinel**, so ascending opens
       * on the untyped companies — the majority state, as on `source` above.
       */
      if (keys.companyType === undefined) return { activityFirst: true };
      return {
        sort: keys.companyType,
        order: sort === 'company-type-asc' ? 'asc' : 'desc',
      };
    case 'value-asc':
    case 'value-desc':
      /*
       * ⚠️ **Same fallback as the two pairs above, for the same reason**, and
       * here the brace it belts is the one most likely to be loosened: the two
       * `/companies` cards render a Value column, so a later pass adding the
       * option to `COMPANY_SORTS` would look entirely reasonable and would send
       * `?sort=amount` to a schema with no such member — **a 422 that takes the
       * whole card out**, which reads as a flaky endpoint rather than a wrong
       * key. That card supplies no `value`, so it lands here instead.
       *
       * ⚠️ **`asc` opens on the cheapest work, and on quotes and invoices that
       * block is `0.00` rather than empty** — `grand_total` is `NOT NULL DEFAULT
       * 0.00`, so never-priced and priced-at-nothing are one value. Documented
       * at the label; nothing here may "fix" it.
       */
      if (keys.value === undefined) return { activityFirst: true };
      return { sort: keys.value, order: sort === 'value-asc' ? 'asc' : 'desc' };
    case 'overdue-asc':
    case 'overdue-desc':
      /*
       * ⚠️ **The narrowest guard here — only the invoices card supplies a key.**
       * `?quotesSort=overdue-asc` typed by hand lands on this line rather than
       * sending `dueDate` to a schema whose table has no such column.
       */
      if (keys.dueDate === undefined) return { activityFirst: true };
      return { sort: keys.dueDate, order: sort === 'overdue-asc' ? 'asc' : 'desc' };
    case 'task-asc':
    case 'task-desc':
      /*
       * ⚠️ **Unreachable today from any card — all five supply the key** — and
       * kept for the reason the others are, plus one of its own: this is the
       * pair a sixth card over a fifth endpoint would inherit by looking like
       * the others, and `has_open_task` is a PROJECTED ALIAS rather than a
       * column, so an endpoint that does not select it cannot order by it.
       */
      if (keys.openTask === undefined) return { activityFirst: true };
      return { sort: keys.openTask, order: sort === 'task-asc' ? 'asc' : 'desc' };
    case 'last-note-asc':
    case 'last-note-desc':
      // Same fallback as the pairs above: `?dealsSort=last-note-asc` typed by
      // hand lands here rather than sending a key `/deals` does not declare.
      if (keys.lastNote === undefined) return { activityFirst: true };
      return { sort: keys.lastNote, order: sort === 'last-note-asc' ? 'asc' : 'desc' };
    case 'stage-asc':
    case 'stage-desc':
      return keyedOrder(keys.stage, sort === 'stage-asc');
    case 'interest-count-asc':
    case 'interest-count-desc':
      return keyedOrder(keys.projectsOfInterest, sort === 'interest-count-asc');
    case 'participated-count-asc':
    case 'participated-count-desc':
      return keyedOrder(keys.projectsParticipated, sort === 'participated-count-asc');
    case 'purchased-count-asc':
    case 'purchased-count-desc':
      return keyedOrder(keys.productsPurchased, sort === 'purchased-count-asc');
    case 'line-projects-asc':
    case 'line-projects-desc':
      return keyedOrder(keys.lineProjects, sort === 'line-projects-asc');
    case 'quote-signed-asc':
    case 'quote-signed-desc':
      return keyedOrder(keys.quoteSigned, sort === 'quote-signed-asc');
    case 'account-lead-asc':
    case 'account-lead-desc':
      return keyedOrder(keys.accountLead, sort === 'account-lead-asc');
    case 'newest':
      return {};
    case 'activity':
      return { activityFirst: true };
  }
}

/*
 * --- the filter, translated per endpoint ----------------------------------
 *
 * 🔴 **THREE FUNCTIONS FOR TWO CONCEPTS, BECAUSE THE WIRE HAS THREE KEYS.** A
 * quote's stage and a deal's stage are `stageId`; an invoice's status is
 * `statusId`; a company's country is `countryId`. Sending one endpoint's
 * spelling to another is a **422 naming the unrecognised key** on a `.strict()`
 * schema — which surfaces as one card failing to load beside four that look
 * perfect, so it reads as a flaky endpoint rather than as a wrong key. Measured
 * against the deployed schemas, 2026-09-03.
 *
 * ⚠️ **Every one returns `{}` — an ABSENT KEY — for an unset filter, and that
 * is not the same as sending an empty value.** `?stageId=` coerces to `0` or
 * `NaN` on these schemas and is a 422, not "no filter"; `listQuery()` drops
 * empty keys everywhere else in this app for exactly this reason.
 *
 * ⚠️ **They read `filter.status` / `filter.country` and NOTHING ELSE**, which is
 * what makes a value in the member a card does not offer inert rather than
 * dangerous — see {@link PartnershipFilter}.
 */

/**
 * `/companies` — the country of record, COMMA-SEPARATED.
 *
 * ✅ **Any number of ids goes to the server**, because `/companies` is the one
 * endpoint in this API that parses a list. So this filter has no page-scoped
 * fallback and the funnel over it is `multi`.
 *
 * ⚠️ **Joined rather than sent as an array** — `IdListFilter` is `number |
 * string`, and `listQuery()` serialises a string verbatim. An array would be
 * stringified by whatever the query builder happens to do with one, which is
 * exactly the kind of thing that works until somebody changes the builder.
 */
function countryParams(filter: PartnershipFilter): { countryId?: string } {
  return filter.country.length === 0 ? {} : { countryId: filter.country.join(',') };
}

/**
 * `/companies` — the account's source, COMMA-SEPARATED.
 *
 * ✅ **Any number of ids goes to the server**, for the identical reason
 * {@link countryParams} gives: `/companies` is the one endpoint in this API that
 * parses a list, and `sourceId` is one of the keys it parses that way. So this
 * filter has no page-scoped fallback and the funnel over it is `multi`.
 *
 * ⚠️ **A SECOND FUNCTION rather than a parameter on the one above**, and the
 * duplication is the point: these two return DIFFERENT KEYS, and a shared helper
 * taking a key name would be one edit away from sending `countryId` for a source
 * — a `200` with the wrong rows, on a `.strict()` schema that would have caught
 * a misspelling but cannot catch a valid key meaning something else. The three
 * functions above this one exist for the same reason and say so.
 *
 * ⚠️ **Joined rather than sent as an array** — `IdListFilter` is
 * `number | string`, and `listQuery()` serialises a string verbatim.
 */
function sourceParams(filter: PartnershipFilter): { sourceId?: string } {
  return filter.source.length === 0 ? {} : { sourceId: filter.source.join(',') };
}

/**
 * `/companies` — what kind of institution the account is, COMMA-SEPARATED.
 *
 * ✅ **Any number of ids goes to the server**, for the third time on this
 * endpoint and the identical reason {@link countryParams} and
 * {@link sourceParams} give: `/companies` is the one list in this API that parses
 * a comma-separated filter, and `companyTypeId` is declared `IdListFilter`
 * there. So this filter has no page-scoped fallback and the funnel over it is
 * `multi`.
 *
 * ⚠️ **A THIRD FUNCTION rather than a parameter on either of the two above, and
 * the duplication is the point** — the reasoning is written out in full at
 * {@link sourceParams} and applies here unchanged. These three return DIFFERENT
 * KEYS; a shared helper taking a key name would be one edit away from sending
 * `sourceId` for a type — a `200` with the wrong rows, on a `.strict()` schema
 * that would have caught a misspelling but cannot catch a valid key meaning
 * something else. ⚠️ **The risk is at its highest on this one**, because all
 * three keys now live on one endpoint and differ only in the word before `Id`.
 *
 * ⚠️ **`companyTypeId`, never `typeId`, and never `companyType`** — the last is
 * the SORT member and carries no `Id` at all. Three spellings, one idea, and
 * `.strict()` turns the wrong one into a 422 that takes the whole card out.
 *
 * ⚠️ **Joined rather than sent as an array** — `IdListFilter` is
 * `number | string`, and `listQuery()` serialises a string verbatim.
 */
function companyTypeParams(filter: PartnershipFilter): { companyTypeId?: string } {
  return filter.companyType.length === 0
    ? {}
    : { companyTypeId: filter.companyType.join(',') };
}

/**
 * `/companies` — the cycles a company took part in a project in, and the
 * cycles it bought in, both COMMA-SEPARATED (backend `32057ed`). Two keys, two
 * members, written out separately for the reason {@link sourceParams} gives:
 * both are valid cycle-id lists, so a shared helper taking a key name would be
 * one edit away from a `200` with the wrong rows.
 */
function purchaseCycleParams(filter: PartnershipFilter): {
  participatedCycleId?: string;
  purchasedCycleId?: string;
} {
  return {
    ...(filter.participatedCycle.length === 0
      ? {}
      : { participatedCycleId: filter.participatedCycle.join(',') }),
    ...(filter.purchasedCycle.length === 0
      ? {}
      : { purchasedCycleId: filter.purchasedCycle.join(',') }),
  };
}

/**
 * `/companies` — the per-stage status columns' shared list, COMMA-SEPARATED
 * `stageStatusId`. The page passes the list already pruned to the card's
 * current columns ({@link stageStatusFilterForColumns}), so this sends it as is.
 */
function stageStatusParams(filter: PartnershipFilter): { stageStatusId?: string } {
  return filter.stageStatus.length === 0 ? {} : { stageStatusId: filter.stageStatus.join(',') };
}

/**
 * The ids of the shared `stageStatusId` list that belong to one of `columns`
 * (a card's CURRENT stage status columns) — the ONLY ids the page may put in
 * `PartnershipFilter.stageStatus` for that card, and the only ones that count
 * as an active filter. An id whose stage lost its column, or a stage the card
 * does not hold, is dropped. ⚠️ **While the masters are `loading`, the list is
 * returned UNCHANGED** — an unread master is not evidence an id is stale.
 */
export function stageStatusFilterForColumns(
  ids: readonly number[],
  columns: readonly StageStatusColumn[],
  allStatuses: Parameters<typeof columnSelection>[2],
  loading: boolean,
): readonly number[] {
  if (loading || ids.length === 0) return ids;
  const kept = new Set<number>();
  for (const column of columns) {
    for (const id of columnSelection(column, ids, allStatuses)) kept.add(id);
  }
  const pruned = ids.filter((id) => kept.has(id));
  return pruned.length === ids.length ? ids : pruned;
}

/**
 * `/companies` — the account's last human note, an inclusive date range.
 * Each bound is sent only when set; an absent bound is an ABSENT key, never an
 * empty value, which the `.strict()` schema refuses.
 */
function lastNoteParams(filter: PartnershipFilter): {
  lastNoteAfter?: string;
  lastNoteBefore?: string;
} {
  return {
    ...(filter.lastNoteAfter !== null ? { lastNoteAfter: filter.lastNoteAfter } : {}),
    ...(filter.lastNoteBefore !== null ? { lastNoteBefore: filter.lastNoteBefore } : {}),
  };
}

/**
 * The chosen ids as one CSV, or none — shared by the three document cards.
 *
 * ⚠️ *SUPERSEDED 2026-10-28 (owner: every filter takes several values):
 * "SEVERAL TICKED IDS SEND NOTHING … `?stageId=1,2` is a 422 on `/deals`,
 * `/quotes` and `/invoices`."* The backend widened all three to a CSV under the
 * same key, so several ticks are a real server OR now, exactly as on
 * `/companies`. A single id still sends the same string it always did.
 */
function statusIdList(filter: PartnershipFilter): string | undefined {
  return filter.status.length === 0 ? undefined : filter.status.join(',');
}

/**
 * Whether the card, not the server, is doing the narrowing for `status`.
 *
 * ⚠️ Always `false` since 2026-10-28 — every selection goes to the server as a
 * CSV (see `statusIdList`). Kept so the panel's page-narrowing arm and the
 * funnel's scope note keep reading one decision.
 */
export function statusPageFiltered(_filter: PartnershipFilter): boolean {
  return false;
}

/** `/deals` and `/quotes` — both spell it `stageId`, on two different tables. */
function stageParams(filter: PartnershipFilter): { stageId?: string } {
  const ids = statusIdList(filter);
  return ids === undefined ? {} : { stageId: ids };
}

/** `/invoices` — `statusId`, and `stageId` there is a 422. */
function statusParams(filter: PartnershipFilter): { statusId?: string } {
  const ids = statusIdList(filter);
  return ids === undefined ? {} : { statusId: ids };
}

interface TableSpec {
  key: PartnershipTableKey;
  title: string;
  /** `null` renders no value column — see {@link PartnershipTableState.valueLabel}. */
  valueLabel: string | null;
  scopedBy: PartnershipTableState['scopedBy'];
  /**
   * Where **Show all** goes, built from the same `userId` the card is scoped
   * to — see {@link PartnershipTableState.listHref} for what each link does and
   * does not promise.
   *
   * ⚠️ **A FUNCTION rather than a string, because the scope is not known when
   * this array is declared.** It is called once per render in the hook, where
   * `userId` is in hand.
   *
   * ⚠️ **`null` — the session has not resolved — yields the BARE module path,
   * with no `accountLeadId` at all.** Never `accountLeadId=null`, never
   * `accountLeadId=`: the first is a 422 naming the key, the second is a 422 on
   * a strict schema (an empty value is not "no filter" on this API). An
   * unscoped list is the honest degradation, and it is momentary — the cards
   * beside it are still skeletons at that point, so nothing offers a link and a
   * count that disagree.
   */
  listHref: (scope: PartnershipLeadScope | null) => string;
  /** Which extra columns this card renders. See {@link PartnershipColumn}. */
  extraColumns: readonly PartnershipColumn[];
  /**
   * Declared beside the `load` that honours it. ⚠️ These two move together: an
   * option listed here that `load` does not translate is a control that appears
   * to work and does not, and `load` is the only thing that can honour one.
   */
  sortOptions: readonly StaticPartnershipSort[];
  /**
   * The filters this card offers, in the order they are rendered.
   *
   * ⚠️ Declared beside the `load` that honours it, for the same reason
   * `sortOptions` is: a kind listed here that `load` does not translate is a
   * control that appears to narrow and does not — and an unnarrowed table under
   * a filtered-looking control reads as the data being wrong. See
   * {@link PartnershipFilterKind} for why the two `/companies` cards get
   * `country` alone and the other three `status` alone.
   */
  filterKinds: readonly PartnershipFilterKind[];
  /** Which create modal the card's **+** opens. See {@link PartnershipCreate}. */
  create: PartnershipCreate;
  /**
   * Which stages' status columns the card renders: `'lead'` (the Leads card's
   * one stage), `'non-lead'` (the stage ids the Companies card's own request
   * sent, reported by `load`), or `null` (no stage status columns).
   */
  stageStatusScope: 'lead' | 'non-lead' | null;
  updatedAtAlsoWrittenByXero: boolean;
  load: (
    scope: PartnershipLeadScope,
    sort: PartnershipSort,
    filter: PartnershipFilter,
    signal: AbortSignal,
  ) => Promise<TablePage>;
}

/**
 * The two `/companies` cards.
 *
 * ⚠️ **RENAMED FROM `COUNTRY_FILTER` on 2026-09-14, when `source` joined it.** A
 * constant whose name ENUMERATES its members is wrong the first time a member is
 * added, and a `COUNTRY_FILTER` holding two kinds is exactly the stale artefact
 * nothing typechecks — the same correction `COMPANY_CARD_COLUMNS` records against
 * its own old name, `COUNTRY_AND_NOTE`. This name says which CARDS it belongs to,
 * which is the fact that does not change when the list grows.
 *
 * ⚠️ **All three kinds are INDEPENDENT KEYS on the wire** (`countryId` AND
 * `sourceId` AND `companyTypeId`), so offering three on one card cannot produce
 * the overwrite {@link PartnershipFilterKind} forbids; the server ANDs them.
 *
 * ✅ **`companyType` joined on 2026-09-14**, appended for the reason every
 * addition on this page is: the order here is the order the funnels' kinds are
 * resolved in and nothing a reader knows moves.
 */
const COMPANY_FILTERS: readonly PartnershipFilterKind[] = [
  'country',
  'source',
  'companyType',
  'stageStatus',
];
/**
 * The COMPANIES card — {@link COMPANY_FILTERS} plus the two cycle filters over
 * its purchase-history columns (2026-10-09). The Leads card renders neither
 * column, so it does not offer them. Spelled out rather than spread, like the
 * sort lists: two lists sharing a prefix, not one list plus extras.
 */
const COMPANIES_FILTERS: readonly PartnershipFilterKind[] = [
  'country',
  'source',
  'companyType',
  'purchasedCycle',
  'participatedCycle',
  'stageStatus',
];
/** The three document cards. */
const STATUS_FILTER: readonly PartnershipFilterKind[] = ['status'];

/**
 * What the two `/companies` cards offer. Alphabetical is available because the
 * endpoint declares `sort: z.enum(['name','createdAt','updatedAt'])`.
 *
 * ⚠️ **`newest` is ABSENT, and it is the one option that genuinely cannot be
 * offered on all five.** That enum has **no `id` member**, so `/companies`
 * cannot express the `id DESC` the other three call "Newest first". Its only
 * near-neighbour is `createdAt DESC`, and the backend measured against a real
 * database that `created_at DESC` and `id DESC` are **different sequences** —
 * which matters most on exactly this table, since the 148 live companies are a
 * bulk Xero import and imported rows are the population whose creation stamps
 * are most likely to run out of id order.
 *
 * So offering it here would put ONE LABEL over TWO DIFFERENT ORDERS, on two
 * cards sitting side by side in the same grid, disagreeing precisely on the
 * rows that dominate the data — which is the admin-editable-free-text failure
 * in a different costume: a client deciding two things are the same because
 * they read the same. The asymmetric dropdown is the lesser cost, and it is
 * visible rather than silent.
 */
const COMPANY_SORTS: readonly StaticPartnershipSort[] = [
  'activity',
  'az',
  'za',
  /*
   * ✅ **The country pair, added 2026-09-12 — owner's request, and the backend
   * half shipped first.** *SUPERSEDED, the sentence this list carried and the
   * page's `SORTABLE_COLUMN` still explained at length: "`/companies` declares
   * `sort: z.enum(['name','createdAt','updatedAt'])` and no country member."*
   * It declares four now (backend `0e30700`), so the clause is history rather
   * than a limit. ⚠️ **That measurement was correct when taken** — which is why
   * it is superseded here rather than deleted: the next reader meeting a
   * three-member enum somewhere else in this file should distrust the copy, not
   * this one.
   *
   * ⚠️ **Appended, not interleaved**, like `newest` and the status pair before
   * it: the positions a reader already knows do not move.
   *
   * 🔴 **These two cards get it on BOTH counts, which is the same test the
   * status pair had to pass** — they RENDER a country column and their endpoint
   * declares the key. A card offering the ordering with no column to click
   * hides it in a dropdown nobody opens; a column with no key behind it is a
   * header that looks clickable and does nothing. The deals card renders no
   * country column and `/deals` has no such key, so it gets neither.
   */
  'country-asc',
  'country-desc',
  /*
   * 🔴 **THE SOURCE PAIR — 2026-09-14, and it was OWED rather than new.** The
   * source column shipped filterable and not sortable while `/companies` had
   * declared the `source` member all along; the funnel was drawn and no caret
   * ever was. **Owner's decision: close it in the same change that adds the type
   * column**, so the two columns beside each other behave alike.
   *
   * ⚠️ **Appended, like every pair before it**, so the positions a reader already
   * knows do not move.
   *
   * 🔴 **BOTH PAIRS PASS THE SAME TWO-PART TEST the country pair had to** — these
   * two cards RENDER the column and their endpoint declares the key. A pair
   * offered with no column to click hides it in a dropdown nobody opens; a column
   * with no key behind it is a header that looks clickable and does nothing, which
   * is precisely what `source` was for a day.
   */
  'source-asc',
  'source-desc',
  /*
   * ✅ **The company-type pair, 2026-09-14, shipped with the column.** Same test,
   * same endpoint, same append-don't-interleave rule.
   */
  'company-type-asc',
  'company-type-desc',
  /*
   * ✅ **The task pair, 2026-09-13 — `/companies` gained an `openTask` member
   * the same day the other three did.**
   *
   * ⛔ **There is deliberately NO value pair here, and the Value column on both
   * these cards is exactly what makes its absence look like an oversight.** That
   * column holds a company STAGE; only the three document cards render money,
   * and neither `/companies` schema declares an `amount` member. See
   * {@link VALUE_SORT_KEY}.
   */
  'task-asc',
  'task-desc',
  /*
   * The account's last note, by date — 2026-10-02, appended like every pair
   * before it. These two cards render the `lastNoteAt` column and `/companies`
   * declares the key: both halves of the test.
   */
  'last-note-asc',
  'last-note-desc',
  /*
   * 2026-10-09: the projects-of-interest count — both cards render the column
   * and `/companies` declares `projectsOfInterest`. Appended, as always.
   */
  'interest-count-asc',
  'interest-count-desc',
  /* 2026-10-27: the "Account lead" column, on all five cards. */
  'account-lead-asc',
  'account-lead-desc',
];

/**
 * What the COMPANIES card offers — {@link COMPANY_SORTS} plus the three
 * orderings only its own columns carry (2026-10-09): its Value column (a
 * company STAGE, ordered as `stage`) and the two purchase-history counts.
 * ⛔ Not the Leads card: it renders no Value column (every row is one stage)
 * and neither purchase-history column. Spelled out rather than spread, for the
 * reason {@link STATUS_DOCUMENT_SORTS} gives.
 */
const COMPANIES_SORTS: readonly StaticPartnershipSort[] = [
  'activity',
  'az',
  'za',
  'country-asc',
  'country-desc',
  'source-asc',
  'source-desc',
  'company-type-asc',
  'company-type-desc',
  'task-asc',
  'task-desc',
  'last-note-asc',
  'last-note-desc',
  'interest-count-asc',
  'interest-count-desc',
  'stage-asc',
  'stage-desc',
  'purchased-count-asc',
  'purchased-count-desc',
  'participated-count-asc',
  'participated-count-desc',
  'account-lead-asc',
  'account-lead-desc',
];

/**
 * What the deal, quote and invoice cards offer.
 *
 * ✅ **`az` and `za` are HERE NOW, and this comment used to explain at length
 * why they could not be.** `/deals`, `/quotes` and `/invoices` gained `sort` and
 * `order`; the keys are in {@link AZ_SORT_KEY}. The old text — "the endpoints
 * cannot do it … the fix is a `sort` parameter on three backend schemas" — was
 * accurate when written and was falsified by a change in the other repo, which
 * is exactly the class of stale sentence nothing in this repo can fail on.
 *
 * ⚠️ **The first three members are in the SAME ORDER as `COMPANY_SORTS`, and
 * that is deliberate.** These cards sit in one grid with the other two, so a
 * reader moving between them should find "A–Z" in the same position rather
 * than hunting for it. The endpoint-specific extra is appended rather than
 * interleaved.
 *
 * ⚠️ **`newest` is KEPT rather than dropped now that A–Z exists.** It was added
 * because these cards would otherwise have had a one-item dropdown, and that
 * reason is spent — but two others are not. It is a real, distinct question
 * ("most recently created", which is neither "most recently touched" nor
 * alphabetical); and `?dealsSort=newest` links already exist, so removing the
 * option would not break them loudly — it would silently re-point them at
 * `activity`, which is the one outcome a shared link must never have.
 */
const DOCUMENT_SORTS: readonly StaticPartnershipSort[] = [
  'activity',
  'az',
  'za',
  'newest',
  /*
   * ✅ **The value and task pairs, 2026-09-13.** Both are appended rather than
   * interleaved, for the reason `newest` was: the positions a reader already
   * knows do not move.
   *
   * ⚠️ **This list is now the DEALS card's alone in practice** — quotes and
   * invoices have their own below, and the invoices one is no longer this list
   * plus a suffix. Read the name as "what a document card can always do", not as
   * "what the three document cards offer".
   */
  'value-asc',
  'value-desc',
  'task-asc',
  'task-desc',
  /* 2026-10-09: the count of projects this document's lines name. */
  'line-projects-asc',
  'line-projects-desc',
  /*
   * 2026-10-27: the "Quote signed" column. Safe here because this list is the
   * DEALS card's alone (see above) — `/quotes` and `/invoices` have no such key.
   */
  'quote-signed-asc',
  'quote-signed-desc',
  'account-lead-asc',
  'account-lead-desc',
];

/**
 * What the QUOTES and INVOICES cards offer — {@link DOCUMENT_SORTS} plus the
 * status pair.
 *
 * ⚠️ **The first four members are `DOCUMENT_SORTS` in its own order, spelled
 * out rather than spread.** A spread would read as "these two are that list plus
 * extras", which is true today and is the sentence that makes somebody add a
 * member to `DOCUMENT_SORTS` for the deals card and hand it to these two as
 * well. They are two lists that happen to share a prefix, and only the prefix's
 * ORDER is a shared decision (a reader moving between the five cards should find
 * "A–Z" in the same position on each).
 *
 * ⚠️ **The pair is appended, not interleaved**, for the same reason `newest`
 * was: the positions a reader already knows do not move.
 *
 * 🔴 **These two cards get it because they RENDER a status column and their
 * endpoints declare the key — both halves, and neither alone is enough.** A card
 * offering the ordering with no column to click would hide it in a dropdown
 * nobody opens; a column with no key behind it would be a header that looks
 * clickable and does nothing. See {@link STATUS_SORT_KEY} for why the key is a
 * different word on each.
 */
const STATUS_DOCUMENT_SORTS: readonly StaticPartnershipSort[] = [
  'activity',
  'az',
  'za',
  'newest',
  'value-asc',
  'value-desc',
  'task-asc',
  'task-desc',
  'status-asc',
  'status-desc',
  'line-projects-asc',
  'line-projects-desc',
  'account-lead-asc',
  'account-lead-desc',
];

/**
 * 🔴 **WHAT THE INVOICES CARD OFFERS — a FOURTH list, added 2026-09-13, and the
 * first time quotes and invoices have not shared one.**
 *
 * {@link STATUS_DOCUMENT_SORTS} plus the overdue pair, **because `d_quotes` has
 * no due-date column and `d_invoices` does**. This is a schema fact, not a card
 * that has not been given the option yet — see {@link DUE_DATE_SORT_KEY}.
 *
 * ⚠️ **Spelled out rather than spread, exactly as `STATUS_DOCUMENT_SORTS` is.**
 * A spread would read as "invoices are quotes plus extras", which is true today
 * and is the sentence that makes somebody add a member to the quotes list for
 * the quotes card and hand it to this one as well. They are two lists that share
 * a prefix, and **only the prefix's ORDER is a shared decision** — a reader
 * moving between the five cards should find each ordering in the same position.
 *
 * ⚠️ **The pair is appended LAST, after the status pair**, so every position a
 * reader already knows is unmoved and the one genuinely invoice-only ordering is
 * where a new thing goes.
 */
const INVOICE_SORTS: readonly StaticPartnershipSort[] = [
  'activity',
  'az',
  'za',
  'newest',
  'value-asc',
  'value-desc',
  'task-asc',
  'task-desc',
  'status-asc',
  'status-desc',
  'overdue-asc',
  'overdue-desc',
  'line-projects-asc',
  'line-projects-desc',
  'account-lead-asc',
  'account-lead-desc',
];

/**
 * 🔴 **THE COLUMNS EVERY CARD CARRIES — the tail of all five `extraColumns`
 * lists, and the invariant its name states is what licenses the spread below.**
 *
 * ✅ Replaces `NOTE_ONLY` (2026-09-12). *SUPERSEDED, that constant and its whole
 * doc: "**The extra columns of a card that has nothing endpoint-specific to
 * show — leads, companies and deals.** ⚠️ **Written once because it must not be
 * read as three independent decisions.** These three carry the note column for
 * the same reason the other two do: all four endpoints return `latestNote`. If a
 * future column becomes universal it belongs here; if one of these three ever
 * needs a column of its own it stops sharing this and declares its own array.
 * What must not happen is two of the three quietly ending up with different sets
 * because somebody extended one array and not its neighbours."*
 *
 * **That constant named its own exit condition twice and BOTH fired**, which is
 * why this is a replacement rather than an edit:
 *
 *  - *"if one of these three ever needs a column of its own it stops sharing"* —
 *    leads and companies left for {@link COMPANY_CARD_COLUMNS} when `country`
 *    landed, and deals gained `projectOfInterest` and `upcomingTask` today. Every
 *    one of the three named in its doc has stopped sharing it.
 *  - *"if a future column becomes universal it belongs here"* — two just did.
 *
 * 🔴 **AND IT HAD ALREADY BEEN A ONE-CALL-SITE CONSTANT DESCRIBING THREE CARDS,
 * SILENTLY, SINCE THE COUNTRY COLUMN LANDED.** Its doc read "leads, companies and
 * deals" while only the deals card still referenced it — precisely the stale
 * artefact {@link COMPANY_CARD_COLUMNS} warns about one paragraph further down
 * when it records being renamed away from `COUNTRY_AND_NOTE`. ⚠️ **A constant
 * whose doc ENUMERATES its sharers goes wrong the moment one leaves, and nothing
 * typechecks a membership list** — so this one names the invariant instead, and
 * the invariant is what a reader has to break rather than merely outgrow.
 *
 * ⚠️ **`NOTE_ONLY` also could not enforce the universality it asserted**, which
 * is the other half of why it is gone. It said all five carry `note` "for the
 * same reason", while the quotes and invoices cards spelled `'note'` INLINE —
 * so the claim lived in prose and two of the five sharers were only sharing it
 * by coincidence. This array is spread into all five, so the claim is now
 * structural.
 *
 * ⚠️ **SPREAD rather than spelled out, which is the OPPOSITE of what
 * {@link STATUS_DOCUMENT_SORTS} does — and the difference is the invariant, not
 * a change of taste.** That list refuses a spread because `DOCUMENT_SORTS` is
 * *not* universal: it belongs to three cards, so a member added there for the
 * deals card would leak to the other two and the spread would be the sentence
 * that made somebody do it. This array is defined BY being universal, so an
 * addition reaching all five is not a leak — it is the only correct outcome.
 * ⛔ **Which means the name is load-bearing: putting a column here that is not on
 * every card is the one way to misuse it**, and it will not fail — it will render
 * a heading over a permanently empty cell, which is what
 * {@link PartnershipColumn} exists to refuse.
 *
 * ⚠️ **`note` stays LAST, here and therefore everywhere.** It is the only
 * free-text column and the only one that wraps to several lines, so it sits
 * nearest the value column rather than between two badges — and because this
 * array is the TAIL of every list, the cards that prefix it with `status`,
 * `signature`, `overdue` or `country` all keep the positions a reader already
 * knows.
 *
 * ⚠️ **Every member widens ALL FIVE cards at once** — see the width note on
 * {@link PartnershipColumn}. This is the cheapest place in the module to add a
 * column and the most expensive place on the page.
 *
 * 🔴 **`projectOfInterest` LEFT THIS ARRAY ON 2026-09-14, AND ITS NAME IS WHAT
 * FORCED THAT RATHER THAN A PREFERENCE.** *SUPERSEDED: the array held
 * `['projectOfInterest', 'upcomingTask', 'note']` for two days.* The owner moved
 * the three document cards onto {@link PartnershipRow.lineProjects}, so the
 * column is no longer on every card — and this array is **defined BY being
 * universal**, which its own doc calls the one way to misuse it. Leaving it here
 * and dropping it per card is not the smaller change; it is the change that makes
 * the name a lie and puts a heading over a permanently empty cell on whichever
 * card is added next.
 *
 * ⚠️ **It moved DOWN into {@link COMPANY_CARD_COLUMNS} and
 * {@link DOCUMENT_CARD_COLUMNS} took its slot on the other three**, so nothing a
 * reader already knows moved position: on all five cards the column between the
 * card's own columns and `upcomingTask` is still a projects column. **What
 * changed is which question it asks**, which is a heading rather than a layout —
 * see `company-projects.ts`, which now owns two of them.
 *
 * ✅ **This is the invariant working exactly as written.** The doc promised that
 * an addition reaching all five is *"the only correct outcome"* and that the name
 * is load-bearing; a REMOVAL was the untested direction and it behaved the same
 * way — the moment a member stopped being universal, the array had to give it up
 * or stop meaning what it says.
 */
/*
 * 🔴 **`note` LEFT THIS ARRAY ON 2026-10-02, FOR THE SAME REASON
 * `projectOfInterest` DID.** *SUPERSEDED: `['upcomingTask', 'note']`.* Owner's
 * decision: on the two COMPANY cards "Your last note" is REPLACED by the
 * account-wide `lastNote` + `lastNoteAt`, so `note` is no longer on every card
 * and this array — defined BY being universal — had to give it up. It is now
 * spelled at the END of {@link DOCUMENT_CARD_COLUMNS}, so the three document
 * cards render exactly the columns, in exactly the order, they did before; the
 * company cards spell their own two note columns at the end of theirs.
 */
/*
 * ✅ **`accountLead` joined on 2026-10-27 (owner)** — on all five cards, which is
 * exactly what this array is for. First, so `upcomingTask` keeps its place
 * nearest the note columns.
 */
const ALL_CARD_COLUMNS: readonly PartnershipColumn[] = ['accountLead', 'upcomingTask'];

/**
 * The two `/companies` cards — owner's instruction, 2026-09-03: *"in company and
 * leads bring their country as a column"*, and 2026-09-12: their projects of
 * interest beside it.
 *
 * 🔴 **IT IS `country` PLUS {@link ALL_CARD_COLUMNS}, AND `country` IS NOW THE
 * ONLY THING IT DECIDES.** Everything after the first member is the tail every
 * card carries, spread in rather than restated, so these two cards cannot come
 * to disagree with the other three about a column all five are supposed to have.
 *
 * ⚠️ *SUPERSEDED 2026-09-12: "**Its own array rather than a member of
 * `NOTE_ONLY`, because these two cards no longer share a column set with the
 * deals card.** That constant's own note says exactly this: if one of the three
 * ever needs a column of its own it stops sharing and declares one. Two of them
 * just did — and the deals card keeps `NOTE_ONLY` unchanged, because a deal has
 * neither a country of record nor projects of interest and either column would
 * be a heading over a permanently empty cell."*
 *
 * **Half of that last clause is now false and the other half is why this array
 * still exists.** A deal still has no country of record — `country` remains
 * these two cards' own column and would be a permanently empty heading anywhere
 * else. But a deal's ACCOUNT does have projects of interest, and the deals card
 * renders them today; the reasoning is re-derived at {@link PartnershipColumn}
 * rather than assumed from the old sentence. ⚠️ **`NOTE_ONLY` itself is gone** —
 * see {@link ALL_CARD_COLUMNS}, which records why both of that constant's stated
 * exit conditions fired at once.
 *
 * ⚠️ **EXTENDED IN PLACE, not forked, and the readers were counted before it was
 * touched.** This array has exactly TWO call sites — the Leads card and the
 * Companies card — and both take every column in it, so a fork would produce two
 * identical arrays under two names. That is the failure {@link ALL_CARD_COLUMNS}
 * warns about from the other direction: what must not happen is cards that share
 * a decision quietly ending up with different sets.
 * ⚠️ **Leads and Companies are a PARTITION of one master** (see the module
 * header), so a company appears on exactly one of them — the same column on both
 * is one decision, never a duplication.
 *
 * ⚠️ **Renamed from `COUNTRY_AND_NOTE`, because a name that ENUMERATES its
 * members is wrong the first time a member is added** — and a constant reading
 * "country and note" while holding three columns is precisely the stale artefact
 * nothing typechecks. The new name says which CARDS it belongs to, which is the
 * fact that does not change when the list grows.
 *
 * ⚠️ **`country` KEEPS ITS PLACE AT THE FRONT and the tail keeps its own order**,
 * so no column a reader already knows moves. `note` is still last — that is now
 * {@link ALL_CARD_COLUMNS}'s decision rather than this array's, which is the
 * point of spreading it: the free-text column sits nearest the value column on
 * all five cards, decided once.
 *
 * ⛔ **A further column for these two cards goes BEFORE the spread, never
 * after.** Appending would put it past `note` on these two alone and break the
 * one ordering all five share.
 *
 * ✅ **`source` joined it on 2026-09-14 — owner's instruction — and it went
 * BEFORE the spread, which is that rule being followed rather than restated.**
 * It sits directly after `country` because the two are the same kind of fact
 * about the same record (a foreign key on `d_companies`, resolved to a name),
 * they carry the same width, and both are filterable on the server where the
 * three columns after them are not. ⚠️ **Nothing a reader already knows moved**:
 * the universal tail keeps its order and `note` is still last.
 *
 * 🔴 **`projectOfInterest` IS SPELLED HERE NOW, LATER THE SAME DAY, AND THAT IS A
 * DEMOTION RATHER THAN AN ADDITION.** It used to arrive through the spread, when
 * it was on all five cards; the owner moved the three document cards onto their
 * own line projects, so it became these two cards' column and had to leave
 * {@link ALL_CARD_COLUMNS}, whose name asserts universality. ⚠️ **No card's
 * rendering changed as a result** — these two showed it before and show it now,
 * in the same position — which is exactly why the move is easy to mistake for
 * churn and worth the sentence.
 *
 * ⚠️ **It goes AFTER `source` and BEFORE the spread**, which keeps the promise
 * the rule above makes: the columns a reader already knows do not move, and
 * `note` is still last on all five.
 */
const COMPANY_CARD_COLUMNS: readonly PartnershipColumn[] = [
  'country',
  'source',
  /*
   * ✅ **`companyType` joined on 2026-09-14 — owner's instruction — and it went
   * BEFORE the spread**, which is the rule two paragraphs up being followed
   * rather than restated. It sits directly after `source` because the three are
   * the same kind of fact about the same record: a foreign key on `d_companies`,
   * resolved to an admin-editable master name, filterable AND sortable on the
   * server where the two columns after them are neither. ⚠️ **Nothing a reader
   * already knows moved**: the universal tail keeps its order and `note` is
   * still last.
   */
  'companyType',
  'projectOfInterest',
  // 2026-10-25 — the per-stage status columns go here; see `expandStageStatusSlot`.
  'stageStatuses',
  ...ALL_CARD_COLUMNS,
  /*
   * 🔴 **The account-wide note pair REPLACES `note` here — 2026-10-02, owner's
   * decision** — last, where `note` sat, so nothing a reader knows moved. See
   * {@link PartnershipColumn}.
   */
  'lastNote',
  'lastNoteAt',
];

/**
 * The COMPANIES card's columns — {@link COMPANY_CARD_COLUMNS} plus the credit
 * balance, owner's decision 2026-09-24, and on this card alone: the Leads card
 * beside it keeps the shared array, as the owner ruled.
 *
 * ⚠️ **Placed BEFORE the universal tail**, after `projectOfInterest`, so the
 * columns every card shares keep their order and `note` is still last.
 */
const COMPANIES_CARD_COLUMNS: readonly PartnershipColumn[] = [
  'country',
  'source',
  'companyType',
  'projectOfInterest',
  /*
   * Purchase history, read-only, Companies card only — right after the
   * projects of interest so the two "which projects" questions sit together.
   */
  'productsPurchased',
  'projectsParticipated',
  'creditBalance',
  // 2026-10-25 — the per-stage status columns; see `expandStageStatusSlot`.
  'stageStatuses',
  ...ALL_CARD_COLUMNS,
  // The account-wide note pair, replacing `note` — see `COMPANY_CARD_COLUMNS`.
  'lastNote',
  'lastNoteAt',
];

/**
 * The three DOCUMENT cards — deals, quotes and invoices. Owner's decision,
 * 2026-09-14: those cards show the projects **their own line items name**, not
 * the projects their ACCOUNT is being pursued for.
 *
 * 🔴 **IT IS `lineProjects` PLUS {@link ALL_CARD_COLUMNS}, AND `lineProjects` IS
 * THE ONLY THING IT DECIDES.** Everything after the first member is the tail
 * every card carries, spread in rather than restated, so these three cannot come
 * to disagree with the other two about a column all five are supposed to have.
 * Exactly the shape {@link COMPANY_CARD_COLUMNS} has, for exactly the same
 * reason.
 *
 * ⚠️ **IT EXISTS BECAUSE THE ALTERNATIVE WAS THREE LITERALS.** The deals card
 * took `ALL_CARD_COLUMNS` directly and the quotes and invoices cards prefixed it
 * with their own columns, so `lineProjects` would otherwise have been spelled
 * three times — and the failure that produces is documented twice in this file
 * already: `NOTE_ONLY` asserted a universality two of its five sharers were only
 * honouring by coincidence, because they spelled `'note'` inline. **Three cards
 * that share a decision must share an array, or they share a habit.**
 *
 * ⚠️ **`lineProjects` TAKES THE SLOT `projectOfInterest` HELD**, immediately
 * before the universal tail, so on all five cards the column between a card's own
 * columns and `upcomingTask` is still a projects column, in the same place, the
 * same width. ⛔ **What changed is the HEADING and therefore the question** —
 * `PROJECTS_ON_DOCUMENT_LABEL` rather than `PROJECTS_OF_INTEREST_LABEL` — and a
 * reader who does not notice the heading will read a document's scope as its
 * account's intent. That is the one thing this change cannot make structural.
 *
 * ⛔ **A further column for these three goes BEFORE the spread, never after**, or
 * it lands past `note` on these three alone and breaks the one ordering all five
 * share. The quotes and invoices cards prefix their OWN columns in front of this
 * array for the same reason.
 *
 * ⚠️ **`projectsOfInterest` is still CARRIED on all three rows and is simply not
 * declared here** — see `dealRow`. This array is the only gate on it, and it is a
 * gate nothing typechecks: a page rendering that field on one of these cards
 * compiles and prints the account's list under a heading that denies it is.
 */
const DOCUMENT_CARD_COLUMNS: readonly PartnershipColumn[] = [
  'lineProjects',
  ...ALL_CARD_COLUMNS,
  /*
   * ⚠️ **Spelled here since 2026-10-02**, when it left `ALL_CARD_COLUMNS` — the
   * three document cards keep "Your last note" exactly as before, last.
   */
  'note',
];

/**
 * The DEALS card — {@link DOCUMENT_CARD_COLUMNS} with "Quote signed" in front
 * (owner 2026-10-27). Before the shared tail, so `note` stays last; only a deal
 * carries `quoteSigned`, so the quotes and invoices cards do not take it.
 */
const DEAL_CARD_COLUMNS: readonly PartnershipColumn[] = [
  'quoteSigned',
  ...DOCUMENT_CARD_COLUMNS,
];

/* --- whose records: the page's account-lead scope (2026-10-27) ------------- */

/**
 * Whose records the five cards show — owner 2026-10-27.
 *
 *  - `me`: the signed-in person, `ids: [userId]`. Sends `accountLeadId` exactly
 *    as the page always has, so the default view is unchanged.
 *  - `leads`: the chosen leads (`ids`) and/or the unassigned records
 *    (`includeUnassigned`, the wire token `none` = resolved lead NULL). Sends
 *    `accountLeadIds`. At least one of the two must be set.
 *  - `all`: no lead filter at all (`ids: []`).
 *
 * The resolved lead is the record's own override, else its company's lead
 * today — the same one the "Account lead" column shows.
 */
export interface PartnershipLeadScope {
  kind: 'me' | 'leads' | 'all';
  ids: number[];
  includeUnassigned: boolean;
}

/** The page's URL key: absent = me, `all`, or `5,9,none`. */
export const PARTNERSHIP_LEADS_PARAM = 'leads';

/** The wire token for "no resolved lead". */
const UNASSIGNED_LEAD_TOKEN = 'none';

/** The server's cap on `accountLeadIds` entries (ids plus `none`). */
const MAX_LEAD_SCOPE_ENTRIES = 50;

/** The default scope: the signed-in person. */
export function meLeadScope(userId: number): PartnershipLeadScope {
  return { kind: 'me', ids: [userId], includeUnassigned: false };
}

/** The unfiltered scope. */
export function allLeadScope(): PartnershipLeadScope {
  return { kind: 'all', ids: [], includeUnassigned: false };
}

/**
 * Read the `leads` URL value. Absent, empty or with no usable entry -> `me`.
 * Unusable entries are dropped silently (the same rule as a sort param); ids
 * are de-duplicated and sorted, and the list is capped at the server's 50.
 *
 * ⚠️ Whether the reader may switch at all (`scopeOf('companies') === 'all'`)
 * is the PAGE's decision: for anybody else it passes `null` here.
 */
export function parsePartnershipLeadScope(
  raw: string | null,
  userId: number,
): PartnershipLeadScope {
  const value = raw?.trim() ?? '';
  if (value === '') return meLeadScope(userId);
  if (value === 'all') return allLeadScope();
  const ids = new Set<number>();
  let includeUnassigned = false;
  for (const part of value.split(',')) {
    const token = part.trim();
    if (token === UNASSIGNED_LEAD_TOKEN) {
      includeUnassigned = true;
      continue;
    }
    if (!/^[1-9]\d{0,15}$/.test(token)) continue;
    const id = Number(token);
    if (Number.isSafeInteger(id)) ids.add(id);
  }
  const sorted = [...ids].sort((a, b) => a - b);
  const room = MAX_LEAD_SCOPE_ENTRIES - (includeUnassigned ? 1 : 0);
  const capped = sorted.slice(0, room);
  if (capped.length === 0 && !includeUnassigned) return meLeadScope(userId);
  return { kind: 'leads', ids: capped, includeUnassigned };
}

/**
 * The `leads` URL value for a scope, or `null` = write no key (the default
 * `me`, and an empty `leads` selection, which would otherwise read back as me).
 */
export function serialisePartnershipLeadScope(scope: PartnershipLeadScope): string | null {
  if (scope.kind === 'me') return null;
  if (scope.kind === 'all') return 'all';
  const csv = leadIdsCsv(scope);
  return csv === '' ? null : csv;
}

/** `5,9,none` — ids ascending, `none` last. `''` when nothing is chosen. */
function leadIdsCsv(scope: PartnershipLeadScope): string {
  const ids = [...new Set(scope.ids)].sort((a, b) => a - b).map(String);
  if (scope.includeUnassigned) ids.push(UNASSIGNED_LEAD_TOKEN);
  return ids.slice(0, MAX_LEAD_SCOPE_ENTRIES).join(',');
}

/**
 * A stable string for a scope, or `null` when it cannot be sent: a `me` with
 * no id, or a `leads` with nothing chosen. Never sent unscoped instead — that
 * would return the whole pipeline under a narrower control.
 */
function leadScopeStamp(scope: PartnershipLeadScope | null): string | null {
  if (scope === null) return null;
  if (scope.kind === 'all') return 'all';
  if (scope.kind === 'me') {
    const id = scope.ids[0];
    return id === undefined ? null : `me:${id}`;
  }
  const csv = leadIdsCsv(scope);
  return csv === '' ? null : `leads:${csv}`;
}

/** The scope a stamp describes — the effect reads the stamp, never the object. */
function leadScopeFromStamp(stamp: string): PartnershipLeadScope {
  if (stamp === 'all') return allLeadScope();
  if (stamp.startsWith('me:')) return meLeadScope(Number(stamp.slice(3)));
  const tokens = stamp.slice('leads:'.length).split(',');
  return {
    kind: 'leads',
    ids: tokens.filter((token) => token !== UNASSIGNED_LEAD_TOKEN).map(Number),
    includeUnassigned: tokens.includes(UNASSIGNED_LEAD_TOKEN),
  };
}

/**
 * The lead filter on the wire. `me` -> `accountLeadId` (as always); `leads` ->
 * `accountLeadIds`; `all` -> neither. The two keys are never both sent (422).
 */
function leadScopeParams(scope: PartnershipLeadScope): {
  accountLeadId?: number;
  accountLeadIds?: string;
} {
  if (scope.kind === 'all') return {};
  if (scope.kind === 'me') {
    const id = scope.ids[0];
    return id === undefined ? {} : { accountLeadId: id };
  }
  const csv = leadIdsCsv(scope);
  return csv === '' ? {} : { accountLeadIds: csv };
}

/**
 * Append the card's lead scope to a list-page URL, as far as that page can
 * reproduce it.
 *
 *  - `me` / a single chosen lead -> `accountLeadId=N` (all four pages read it).
 *  - several leads -> `accountLeadId=5,9` on `/companies` alone, which parses
 *    the key as a list; the document pages read one id, so the link is bare.
 *  - unassigned in the set, or `all` -> bare: no list page can ask for "no
 *    lead" (`accountLeadIds` is read by none of them).
 *  - `null` (session unresolved) -> bare.
 *
 * ⚠️ **`separator` is a parameter because one of the five links already carries
 * a param.** The Leads card links to `/companies?stageId=1`, so its scope key
 * joins with `&` where the other four join with `?`. Written once rather than
 * inline at five call sites: a `?` where an `&` belongs produces a path the
 * router does not recognise, and a `&` where a `?` belongs produces a query
 * string whose first key is `&accountLeadId` — neither fails loudly, and both
 * end at an unscoped or a 404 page.
 */
function scopedListHref(
  path: string,
  scope: PartnershipLeadScope | null,
  listCapable: boolean,
  separator: '?' | '&' = '?',
): string {
  if (scope === null || scope.kind === 'all' || scope.includeUnassigned) return path;
  const ids = [...new Set(scope.ids)].sort((a, b) => a - b);
  if (ids.length === 0) return path;
  if (ids.length > 1 && !listCapable) return path;
  return `${path}${separator}accountLeadId=${ids.join(',')}`;
}

/** The two cards that read `/companies` — see {@link companyCardListParams}. */
export type CompanyCardKey = Extract<PartnershipTableKey, 'leads' | 'companies'>;

export function isCompanyCardKey(key: PartnershipTableKey): key is CompanyCardKey {
  return key === 'leads' || key === 'companies';
}

/**
 * The `/companies` list params one company card sends, WITHOUT paging.
 *
 * ⚠️ **The ONE copy, read by both `load` and the card's Export** (2026-10-18), so
 * the file a reader downloads is scoped, filtered and ordered exactly like the
 * card. Each caller adds its own `perPage` (or none, for the export).
 *
 * The Companies card awaits `/company-stages` first — see `nonLeadStageFilter`,
 * whose refusal propagates rather than widening the scope.
 */
export async function companyCardListParams(
  key: CompanyCardKey,
  scope: PartnershipLeadScope,
  sort: PartnershipSort,
  filter: PartnershipFilter,
  signal: AbortSignal,
): Promise<Omit<CompanyListParams, 'page' | 'perPage'>> {
  if (key === 'leads') {
    return {
      stageId: LEAD_COMPANY_STAGE_ID,
      ...leadScopeParams(scope),
      ...countryParams(filter),
      ...sourceParams(filter),
      ...companyTypeParams(filter),
      ...stageStatusParams(filter),
      ...lastNoteParams(filter),
      ...orderParamsFor(sort, {
        az: AZ_SORT_KEY.leads,
        country: COUNTRY_SORT_KEY,
        source: SOURCE_SORT_KEY,
        companyType: COMPANY_TYPE_SORT_KEY,
        openTask: OPEN_TASK_SORT_KEY,
        lastNote: LAST_NOTE_SORT_KEY,
        projectsOfInterest: PROJECTS_OF_INTEREST_SORT_KEY,
        stageStatus: STAGE_STATUS_SORT_KEY,
        accountLead: ACCOUNT_LEAD_SORT_KEY,
        /* No `value`: this card's Value column is a company STAGE. */
      }),
    };
  }
  const stageId = await nonLeadStageFilter(signal);
  return {
    stageId,
    ...leadScopeParams(scope),
    ...countryParams(filter),
    ...sourceParams(filter),
    ...companyTypeParams(filter),
    ...purchaseCycleParams(filter),
    ...stageStatusParams(filter),
    ...lastNoteParams(filter),
    ...orderParamsFor(sort, {
      stageStatus: STAGE_STATUS_SORT_KEY,
      az: AZ_SORT_KEY.companies,
      country: COUNTRY_SORT_KEY,
      source: SOURCE_SORT_KEY,
      companyType: COMPANY_TYPE_SORT_KEY,
      openTask: OPEN_TASK_SORT_KEY,
      lastNote: LAST_NOTE_SORT_KEY,
      projectsOfInterest: PROJECTS_OF_INTEREST_SORT_KEY,
      /*
       * No `value`: this card's Value column is a company STAGE, and it
       * orders through its own `stage` pair instead — never `amount`.
       */
      stage: COMPANY_STAGE_SORT_KEY,
      projectsParticipated: PROJECTS_PARTICIPATED_SORT_KEY,
      productsPurchased: PRODUCTS_PURCHASED_SORT_KEY,
      accountLead: ACCOUNT_LEAD_SORT_KEY,
    }),
  };
}

/**
 * Written out per table rather than derived, because the five calls differ in
 * ways that are easy to homogenise wrongly:
 *
 *  - **All four endpoints take `sort` and `order`, and they take THREE
 *    DIFFERENT KEYS for the same question** — `name`, `dealName`, `subject`.
 *    That mapping is {@link AZ_SORT_KEY} and each loader reads its own row out
 *    of it; nothing here writes a column name inline. Sending the wrong
 *    spelling is a 422 naming `sort`, which shows up as one card failing to
 *    load beside four that look perfect — so it reads as a flaky endpoint
 *    rather than as a wrong constant, which is why the mapping is typed against
 *    each endpoint's own union rather than as `string`.
 *  - **A quote's and an invoice's `updatedAt` has a second writer.** A
 *    successful Xero push stamps the document number and the currency rate back
 *    onto the row, so the column climbs without a person. That is a caveat to
 *    PRINT, never a reason to prefer a per-viewer stamp — and it is a reason not
 *    to campaign for `sort=updatedAt` on those two endpoints if they ever offer
 *    it: an integration would decide the top of the table.
 *  - **All five are scoped by `accountLeadId`, and quotes and invoices must
 *    NOT go back to `ownerId`.** Nothing on the server defaults `owner_id` on
 *    a quote or invoice create — the acting user goes to `created_by_id` — and
 *    this app removed the Owner picker from both forms, so `?ownerId=<me>`
 *    returned nothing for every user, permanently, and looked exactly like
 *    having no quotes. `accountLeadId` on both list endpoints resolves the
 *    document's own account lead when it has one, otherwise its company's,
 *    the same as `/deals`.
 */
const TABLES: readonly TableSpec[] = [
  {
    key: 'leads',
    title: 'Leads',
    /*
     * 🔴 NO VALUE COLUMN. Owner's instruction, 12 September 2026.
     *
     * This card asks for `stageId: LEAD_COMPANY_STAGE_ID` and nothing else, so
     * EVERY row it can return is at that one stage — the column printed the
     * same word down the page and spent width the country and note columns use
     * better. It is the only card here whose value is a constant of its own
     * filter.
     *
     * ⚠️ `meta` is still POPULATED below, deliberately. The row type is shared
     * with four other cards, the value costs nothing to carry, and blanking it
     * would make this card's rows a different shape for no gain. What changed
     * is what is RENDERED.
     *
     * ⚠️ Do not copy this to the Companies card beside it. That one spans every
     * other active stage, so its value column is the thing that distinguishes
     * one row from the next.
     */
    valueLabel: null,
    scopedBy: 'accountLeadId',
    /*
     * ⚠️ The ONE link that is not a bare module path, and the one whose counts
     * match exactly: `stageId` here is the same single id `load` sends below,
     * so the list page reproduces this card's scope precisely. It reads
     * `LEAD_COMPANY_STAGE_ID` rather than a literal `1` for the reason that
     * constant states at length — the id is a measurement with an expiry, and a
     * second copy of it would go wrong silently and separately from the first.
     */
    listHref: (scope) =>
      scopedListHref(`/companies?stageId=${LEAD_COMPANY_STAGE_ID}`, scope, true, '&'),
    extraColumns: COMPANY_CARD_COLUMNS,
    sortOptions: COMPANY_SORTS,
    filterKinds: COMPANY_FILTERS,
    create: 'company',
    stageStatusScope: 'lead',
    updatedAtAlsoWrittenByXero: false,
    /*
     * ⚠️ **`stageId` here is the PARTITION and neither the country nor the source
     * filter may touch it** — see {@link PartnershipFilterKind}. All three keys
     * are independent on the wire (`stageId` AND `countryId` AND `sourceId`),
     * which is what makes those filters safe on this card at all.
     */
    load: async (scope, sort, filter, signal) => {
      const params = await companyCardListParams('leads', scope, sort, filter, signal);
      const result = await api.listCompanies({ ...params, perPage: PER_PAGE }, signal);
      return pageOf(result, companyRow);
    },
  },
  /*
   * ⚠️ **Order here IS the layout**, and it is expressed by position in this
   * list and nowhere else. Reordering these entries moves the cards on screen;
   * there is no separate layout config to keep in step, and equally nothing to
   * warn you that you have just re-ordered the dashboard.
   *
   * ⚠️ *SUPERSEDED 2026-09-03: "The page renders this array into a two-column
   * grid, so the PAIRING the owner asked for — Leads beside Companies, then
   * Deals beside Quotes, then Invoices alone — is expressed by position."* The
   * cards are **one per row at every width** now, so there are no pairs left to
   * disturb — but the reading order is still exactly this array's order, which
   * is the half that survives. The owner's stated order is **Leads, Deals,
   * Quotes, Invoices, Companies**; note that this array is NOT in that order and
   * never has been (Companies sits second, next to Leads, because the two are
   * halves of one master and were meant to be read together). That was true
   * before this change and is deliberately left alone — it is a question for the
   * owner, not something to silently re-order while changing the grid.
   */
  {
    key: 'companies',
    /*
     * ⚠️ *SUPERSEDED 2026-09-03, the TITLE and the argument for it: "**⚠️ The
     * title carries the scope because nothing else on the card can.** This
     * table is the COMPLEMENT of the Leads table beside it, and the difference
     * is invisible from the rows: a reader comparing this count against
     * `/companies` finds two different numbers with nothing accounting for the
     * gap, and the honest reading of a bare "Companies" is "all of them".
     * Naming the exclusion in the heading is the whole correction — the panel
     * takes no sentence for it, deliberately." — and the title it defended,
     * `'Companies (excluding leads)'`.*
     *
     * Owner's instruction: the heading reads **`Companies`**.
     *
     * ⚠️ **The DIVERGENCE that paragraph described is REAL and has not been
     * fixed — it has been ACCEPTED, and saying so is the point of keeping the
     * text.** This card still excludes leads, so its count still differs from
     * the count on `/companies`, and the heading no longer says why. The owner
     * was told and took it: the card now carries a **Show all** link to
     * `/companies` (see `listHref` below), so a reader who wants the whole
     * master is one click from it rather than left to reconcile two numbers.
     * ⛔ **Do not "restore" the qualifier as a fix** — that is the decision, not
     * an oversight, and a heading and a link cannot both be the scope.
     *
     * 🔴 **THE OTHER HALF STANDS UNCHANGED AND IS NOT ABOUT THIS TITLE.**
     * Whatever a heading here says, it may say "leads" — never the names of the
     * stages it DOES include. Those names are admin-editable free text; a
     * heading reading "Prospects & Clients" would be a second copy of the
     * master, stale the day somebody renames a row, and this codebase has been
     * bitten three times by a client deciding what a record means from a name
     * an administrator can change. That rule binds any future heading on this
     * card, and on the Leads card beside it.
     */
    title: 'Companies',
    valueLabel: 'Stage',
    scopedBy: 'accountLeadId',
    /*
     * 🔴 **THE ONE LINK WHOSE COUNT WILL NOT MATCH ITS CARD, AND THAT IS
     * ACCEPTED.** No `stageId` is sent, so this lands on the whole master
     * INCLUDING leads, where the card excludes them. The filter this card
     * really uses is derived inside `load` from `/company-stages` — a request
     * that has not been made when this function runs — and reproducing it here
     * would mean a second copy of `nonLeadStageFilter`'s CSV, built from a
     * master this function does not hold. See `listHref` on the state for the
     * decision and why the heading no longer carries the caveat either.
     */
    listHref: (scope) => scopedListHref('/companies', scope, true),
    extraColumns: COMPANIES_CARD_COLUMNS,
    sortOptions: COMPANIES_SORTS,
    filterKinds: COMPANIES_FILTERS,
    create: 'company',
    stageStatusScope: 'non-lead',
    updatedAtAlsoWrittenByXero: false,
    /*
     * Two requests, in order: the master, then the list it scopes. The stage
     * filter cannot be built without the first, and there is deliberately no
     * branch that proceeds without it — see `nonLeadStageFilter`.
     *
     * Both share the caller's `signal`, so a user change, a SORT change, a
     * FILTER change or an unmount aborts whichever is in flight and the
     * effect's own `signal.aborted` guards keep the result off screen either
     * way.
     *
     * ⚠️ **The country and source filters are SECOND AND THIRD keys beside the
     * derived `stageId`, not replacements for it.** Narrowing by either must not
     * widen the card back across the lead/non-lead partition, and neither does:
     * all three AND together server-side.
     */
    load: async (scope, sort, filter, signal) => {
      const params = await companyCardListParams('companies', scope, sort, filter, signal);
      const result = await api.listCompanies({ ...params, perPage: PER_PAGE }, signal);
      // The stage ids this request was scoped to — the card's status columns.
      const stageIds =
        typeof params.stageId === 'string' ? stageIdSetOf(params.stageId) : NO_STAGE_IDS;
      return { ...pageOf(result, companyRow), stageStatusStageIds: stageIds };
    },
  },
  {
    key: 'deals',
    title: 'Deals',
    valueLabel: 'Deal value',
    scopedBy: 'accountLeadId',
    listHref: (scope) => scopedListHref('/deals', scope, false),
    /*
     * ⚠️ **The universal tail and NOTHING ELSE — the only card with no column of
     * its own, and the emptiness beside it is still not for want of a badge.**
     * `d_deals` has no quote stage and no invoice status; its own pipeline stage
     * is a third master with a third flag set and already renders in the value
     * column under `Deal value`'s fallback. A deal has no signature envelope, no
     * due date, and no country of record.
     *
     * ⚠️ *SUPERSEDED 2026-09-12: `extraColumns: NOTE_ONLY` — "the note and
     * nothing else … It does have notes, like all five."* It has notes, an open-
     * task flag and an ACCOUNT with projects of interest, like all five. That
     * constant is gone; see {@link ALL_CARD_COLUMNS}.
     *
     * ⛔ **This card takes the shared array DIRECTLY rather than spreading it into
     * a literal**, because it adds nothing — a `[...ALL_CARD_COLUMNS]` here would
     * be a fork waiting for somebody to append a member to, on the one card whose
     * whole statement is that it has no column of its own.
     *
     * 🔴 **IT TAKES {@link DOCUMENT_CARD_COLUMNS} NOW — 2026-09-14, owner's
     * decision.** *SUPERSEDED: `extraColumns: ALL_CARD_COLUMNS`, and with it both
     * the clause above about "an ACCOUNT with projects of interest, like all
     * five" and the claim that this card has no column of its own.* It has one —
     * the projects its own LINE ITEMS name, which no company card can have. What
     * it still lacks is a column the other two DOCUMENT cards do not, which is
     * why it takes that array directly and the ⛔ above still binds word for
     * word: a `[...DOCUMENT_CARD_COLUMNS]` here would be the same fork under a
     * new name.
     *
     * *SUPERSEDED 2026-10-27, owner's decision:* it takes
     * {@link DEAL_CARD_COLUMNS} — "Quote signed" plus that array — because a
     * deal now has a column the other two document cards do not.
     */
    extraColumns: DEAL_CARD_COLUMNS,
    sortOptions: DOCUMENT_SORTS,
    /*
     * ⚠️ **A DEAL STAGE, which is a third master — `f_deal_stages`, carrying
     * `isWon`/`isLost`.** It is neither the `/quote-stages` row the quotes card
     * filters by nor the `/invoice-statuses` row the invoices card does, and
     * the page fetches it separately for exactly that reason. What the three
     * cards share is the query KEY's shape, not the master behind it.
     */
    filterKinds: STATUS_FILTER,
    create: 'deal',
    stageStatusScope: null,
    updatedAtAlsoWrittenByXero: false,
    load: (scope, sort, filter, signal) =>
      api
        .listDeals(
          {
            ...leadScopeParams(scope),
            perPage: PER_PAGE,
            ...stageParams(filter),
            ...orderParamsFor(sort, {
              az: AZ_SORT_KEY.deals,
              /*
               * ⚠️ **`amount` here orders `COALESCE(lines_total, d.amount)`**,
               * not the typed column — the same figure `dealValue()` renders.
               * See {@link VALUE_SORT_KEY}.
               */
              value: VALUE_SORT_KEY,
              openTask: OPEN_TASK_SORT_KEY,
              lineProjects: LINE_PROJECTS_SORT_KEY,
              quoteSigned: QUOTE_SIGNED_SORT_KEY,
              accountLead: ACCOUNT_LEAD_SORT_KEY,
            }),
          },
          signal,
        )
        .then((result) => pageOf(result, dealRow)),
  },
  {
    key: 'quotes',
    title: 'Quotes',
    valueLabel: 'Quote value',
    scopedBy: 'accountLeadId',
    listHref: (scope) => scopedListHref('/quotes', scope, false),
    /*
     * Owner's instruction, 2026-09-03: *"in quotes and invoices cards … i want
     * the status as other column … in quotes i want the signature status column
     * as well"*. Both are properties of a quote and of nothing else here — only
     * a quote carries a `/quote-stages` row, and only a quote can have a
     * DocuSign envelope against it.
     */
    /*
     * ⚠️ **The two own columns FIRST, then the shared tail** — so `status` and
     * `signature` keep the positions a reader knows and `note` stays last, which
     * is now {@link ALL_CARD_COLUMNS}'s decision rather than this literal's.
     * *SUPERSEDED 2026-09-12: `['status', 'signature', 'note']`, which spelled
     * `'note'` inline and made this card's share of the universal tail a
     * coincidence rather than a structure.*
     *
     * 🔴 **THE TAIL IS {@link DOCUMENT_CARD_COLUMNS} NOW — 2026-09-14.**
     * *SUPERSEDED: `['status', 'signature', ...ALL_CARD_COLUMNS]`.* The projects
     * column between `signature` and `upcomingTask` is still there, in the same
     * place and the same width; it asks about this QUOTE's line items rather than
     * about its account. ⚠️ **Nothing a reader already knows moved, which is
     * precisely why the heading is the only thing telling them the question
     * changed** — see `PROJECTS_ON_DOCUMENT_LABEL`.
     */
    extraColumns: ['status', 'signature', ...DOCUMENT_CARD_COLUMNS],
    sortOptions: STATUS_DOCUMENT_SORTS,
    /* `stageId` — a `/quote-stages` row, the same master `StageBadge` resolves. */
    filterKinds: STATUS_FILTER,
    create: 'quote',
    stageStatusScope: null,
    updatedAtAlsoWrittenByXero: true,
    load: (scope, sort, filter, signal) =>
      api
        .listQuotes(
          {
            ...leadScopeParams(scope),
            perPage: PER_PAGE,
            ...stageParams(filter),
            ...orderParamsFor(sort, {
              az: AZ_SORT_KEY.quotes,
              /* `stage`, not `status` — see `STATUS_SORT_KEY`. */
              status: STATUS_SORT_KEY.quotes,
              /* `grand_total` here, where deals order an expression. */
              value: VALUE_SORT_KEY,
              openTask: OPEN_TASK_SORT_KEY,
              lineProjects: LINE_PROJECTS_SORT_KEY,
              accountLead: ACCOUNT_LEAD_SORT_KEY,
              /*
               * ⛔ **No `dueDate`: `d_quotes` HAS NO SUCH COLUMN.** Its
               * near-neighbour `validTill` answers when the OFFER lapses, not
               * when money is owed — a different question and a decision nobody
               * has taken. See {@link DUE_DATE_SORT_KEY}.
               */
            }),
          },
          signal,
        )
        .then((result) => pageOf(result, quoteRow)),
  },
  {
    key: 'invoices',
    title: 'Invoices',
    valueLabel: 'Invoice value',
    scopedBy: 'accountLeadId',
    listHref: (scope) => scopedListHref('/invoices', scope, false),
    /*
     * Owner's instruction, same sentence as the quotes card: *"in invoices i
     * want the overdue by column as well"*. `overdue` is here and nowhere else
     * because `d_invoices` is the only one of these five tables with a due
     * date — and the column reads `row.dueDate` PLUS the `/invoice-statuses`
     * master the PAGE holds, never this module. See `PartnershipRow.dueDate`.
     */
    /*
     * ⚠️ **Own columns first, then the shared tail** — same shape as the quotes
     * card beside it, and `note` stays last by {@link ALL_CARD_COLUMNS}'s
     * ordering rather than by this literal repeating it. *SUPERSEDED 2026-09-12:
     * `['status', 'overdue', 'note']`.*
     *
     * 🔴 **THE TAIL IS {@link DOCUMENT_CARD_COLUMNS} NOW — 2026-09-14**, exactly
     * as on the quotes card beside it. *SUPERSEDED: `['status', 'overdue',
     * ...ALL_CARD_COLUMNS]`.* The projects column is in the same position and now
     * asks about this INVOICE's line items rather than about its account.
     */
    extraColumns: ['status', 'overdue', ...DOCUMENT_CARD_COLUMNS],
    /*
     * ⚠️ **The one card with a list of its own, 2026-09-13** — see
     * {@link INVOICE_SORTS}. It is `STATUS_DOCUMENT_SORTS` plus the overdue
     * pair, and the two are not spread from one another on purpose.
     */
    sortOptions: INVOICE_SORTS,
    /*
     * ⚠️ **`statusId`, NOT `stageId`** — `d_invoices` has a status column and no
     * stage column, so the key the quotes card sends is a 422 here. Same split
     * as `STATUS_SORT_KEY`, and `statusParams` is what keeps the two apart.
     */
    filterKinds: STATUS_FILTER,
    create: 'invoice',
    stageStatusScope: null,
    updatedAtAlsoWrittenByXero: true,
    load: (scope, sort, filter, signal) =>
      api
        .listInvoices(
          {
            ...leadScopeParams(scope),
            perPage: PER_PAGE,
            ...statusParams(filter),
            ...orderParamsFor(sort, {
              az: AZ_SORT_KEY.invoices,
              /* `status`, not `stage` — see `STATUS_SORT_KEY`. */
              status: STATUS_SORT_KEY.invoices,
              value: VALUE_SORT_KEY,
              openTask: OPEN_TASK_SORT_KEY,
              /*
               * ⚠️ **`dueDate`, and this card is the ONLY one that may send
               * it.** `/invoices` also sorts by `billDate` and the two are
               * different columns answering different questions — see
               * {@link DUE_DATE_SORT_KEY}.
               */
              dueDate: DUE_DATE_SORT_KEY,
              lineProjects: LINE_PROJECTS_SORT_KEY,
              accountLead: ACCOUNT_LEAD_SORT_KEY,
            }),
          },
          signal,
        )
        .then((result) => pageOf(result, invoiceRow)),
  },
];

/**
 * Each card's offerable orderings, keyed for the page — which has to resolve a
 * URL param BEFORE it can call the hook, so it cannot read them off the
 * returned state.
 *
 * Derived from `TABLES` rather than written out, so the list the page validates
 * against and the list a card renders cannot disagree. A hand-written copy would
 * agree on the day it was typed and drift the first time an endpoint gains a
 * `sort` — and the drift is silent in the worst direction: the page would accept
 * `?dealsSort=az`, the card would render "A–Z", and `load` would ignore it.
 */
const PARTNERSHIP_SORT_OPTIONS: Record<PartnershipTableKey, readonly StaticPartnershipSort[]> =
  Object.fromEntries(TABLES.map((spec) => [spec.key, spec.sortOptions])) as Record<
    PartnershipTableKey,
    readonly StaticPartnershipSort[]
  >;

/** The specs by key, so the hook can call one per table by name. */
const SPEC: Record<PartnershipTableKey, TableSpec> = Object.fromEntries(
  TABLES.map((spec) => [spec.key, spec]),
) as Record<PartnershipTableKey, TableSpec>;

/*
 * --- there is no client-side re-ordering step, and there must not be one ----
 *
 * Do not add a client-side "sort by activity" step re-stamping each row with
 * `lastNoteAt` (one `noteSummary` call per table) and re-sorting the five rows
 * by it. Two independent reasons, either of which is sufficient:
 *
 *  1. **The stamp would be per-viewer.** See the module header. It cannot be
 *     labelled truthfully for a viewer with the notes audit grant and one
 *     without at the same time, and there is no honest client-side test for
 *     which of the two is looking.
 *  2. **It would re-order the PAGE, not the table.** The server has already
 *     chosen five rows; shuffling those five cannot surface the record that
 *     did not come back, so the most recently touched record in the table
 *     could be absent from a list claiming to be ordered by activity.
 *
 * Do not reintroduce a client-side sort of any kind here. Reason 2 kills it
 * whatever the key: the page IS the sample, and sorting a sample is not
 * ordering a table. Ordering belongs in the request — which is what
 * `orderParamsFor` does, on all five tables.
 *
 * ⚠️ **THIS WAS ALSO THE ANSWER TO "just sort the deals card A–Z on the
 * client", and that question is now MOOT — the argument is kept because it was
 * never about what the server could do.** The three endpoints have a `sort`
 * parameter now, so nobody needs the client-side version; but the reasoning
 * below is what makes a client-side sort wrong even where no server order
 * exists, and the next surface here that wants one will be in exactly that
 * position. It is written down rather than left to be re-derived:
 *
 *  - This module fetches `PER_PAGE` rows and the server has already chosen
 *    WHICH rows those are, by `id DESC`. Sorting them alphabetically reorders a
 *    WINDOW, and the window was selected by a different key than the one being
 *    displayed — so a company beginning with "A" that happens to be the oldest
 *    record is absent from a list claiming to be alphabetical. Row 1 of page 2
 *    belongs before row 1 of page 1.
 *  - The card shows five above the fold out of `PER_PAGE` and reports
 *    `meta.total` — often far more — so the mismatch between "what this claims
 *    to be ordered by" and "what it is a sample of" is on screen and still
 *    invisible. (⚠️ *SUPERSEDED 2026-09-03: "five of fifteen"* — the page size
 *    is 100 now. The ratio moved; the argument does not depend on it.)
 *  - 🔴 **"Fetching the whole set instead (`perPage` 100)" IS WHAT THIS MODULE
 *    NOW DOES — and it did NOT make a client-side sort honest.** *SUPERSEDED,
 *    as a hypothetical: "would make it honest only while `total <= 100`".* That
 *    is not a hypothetical any more, and the objection it raised is now a live
 *    property of this file: 100 is the endpoints' ceiling, so a client-side sort
 *    here would be correct for a junior account lead and silently wrong for a
 *    senior one, which is the worst possible distribution of a defect. The
 *    reason it costs nothing is that the depth was raised for SCROLLING while
 *    ordering stayed in the request — see `PER_PAGE`.
 *
 * ⚠️ **This block ended "the endpoints need a `sort` parameter; until they have
 * one, the option is not offered." They have one now.** That sentence was a
 * claim about the ABSENCE of something in another repo, which is the shape that
 * rots the moment somebody builds it — and the builder is never the one reading
 * the sentence. It is corrected in place rather than deleted, because the
 * paragraph above it is still the reason a client-side sort is refused.
 */

/* --- the hook ------------------------------------------------------------- */

/*
 * The per-fetch half of the state. `sortOptions` and
 * `updatedAtAlsoWrittenByXero` are deliberately NOT here: both are properties
 * of the endpoint, fixed before a request is made, so they are spread in from
 * the spec at the end. Carrying them through the fetch would let a failure, a
 * race or a forgotten branch report an ordering the request never asked for —
 * which is exactly the class of silent-wrong-sentence this page was repaired
 * for once already.
 */
interface TableData {
  rows: PartnershipRow[];
  total: number;
  loading: boolean;
  error: string | null;
  /**
   * The Companies card's stage status scope from its LAST successful load. Kept
   * across reloads (a sort change must not make the columns blink out), and
   * merged in by the hook rather than read off a pending stamp.
   */
  stageStatusStageIds?: ReadonlySet<number>;
}

/** A card's stage status ids, from its scope and its last load. */
function stageStatusStageIdsOf(
  spec: TableSpec,
  loaded: ReadonlySet<number> | undefined,
): ReadonlySet<number> | null {
  if (spec.stageStatusScope === null) return null;
  if (spec.stageStatusScope === 'lead') return LEAD_STAGE_IDS;
  return loaded ?? NO_STAGE_IDS;
}

const PENDING: TableData = { rows: [], total: 0, loading: true, error: null };

/**
 * The five tables, always in the same order: leads, deals, quotes, invoices,
 * companies.
 *
 * `scope` is whose records to show (2026-10-27: was a bare `userId`) — see
 * {@link PartnershipLeadScope}; build it with {@link meLeadScope} or
 * {@link parsePartnershipLeadScope}. `null` while the session has not
 * resolved, and **`null` fires nothing** — nor does a `me` with no id or a
 * `leads` with nothing chosen. Dropping the filter would not return "no records
 * yet"; it would return the whole company's pipeline under a heading saying it
 * is yours. The five states stay `loading` until there is a scope to send.
 *
 * Each table loads and reports independently: one 500 leaves the other four on
 * screen with their own rows, rather than blanking the dashboard.
 */
export function usePartnershipTables(
  scope: PartnershipLeadScope | null,
  sorts: PartnershipSorts,
  filters: PartnershipFilters,
  /**
   * Bumped by the page after a record is created, to refetch that ONE card.
   *
   * ⚠️ **A nonce rather than a `reload()` callback, because the fetch lives in
   * an effect keyed on a stamp.** A callback would have to reach inside five
   * independent effects; a number folded into the stamp makes a reload
   * structurally identical to a sort or a filter change, which is the path that
   * already handles the two races this hook is built around (see
   * {@link useOneTable}).
   *
   * ⚠️ **Per card, not per page.** Creating a quote must not blank the other
   * four cards to skeletons — five requests instead of one, for four cards
   * that cannot have changed.
   * ⚠️ *SUPERSEDED: "against a 120-per-60s per-IP budget shared across the
   * office."* It is **12,000/60s** (corrected 2026-09-17); the four needless
   * requests and the four needless skeletons are the reason, not the budget.
   */
  reloadTokens: Record<PartnershipTableKey, number>,
): PartnershipTableState[] {
  /*
   * ⚠️ **One effect PER TABLE, not one for all five, and the reason is the sort
   * control.** A single effect keyed on the five sorts together would refetch
   * every card whenever any one of them changed: five requests instead of one,
   * and — more visibly — four unrelated cards dropping to a skeleton because
   * somebody re-ordered a fifth. Each card owns its own request and its own
   * lifecycle. ⚠️ *SUPERSEDED: "against a 120-per-60s per-IP budget shared
   * across the office."* It is **12,000/60s** (corrected 2026-09-17); the
   * visible half of this reason was always the load-bearing one.
   *
   * Called by name rather than in a loop over `TABLES`, because the number and
   * order of hook calls must be fixed at compile time. `SPEC` is derived from
   * `TABLES`, so the five specs are still written down exactly once.
   */
  /*
   * The scope as a STRING, so the five effects key on its value: a page building
   * the scope object inline each render must not refetch (the same guard the
   * filter id lists carry in `useOneTable`). `null` = nothing sendable yet.
   */
  const scopeStamp = leadScopeStamp(scope);
  const leads = useOneTable(SPEC.leads, scopeStamp, sorts.leads, filters.leads, reloadTokens.leads);
  const companies = useOneTable(
    SPEC.companies,
    scopeStamp,
    sorts.companies,
    filters.companies,
    reloadTokens.companies,
  );
  const deals = useOneTable(SPEC.deals, scopeStamp, sorts.deals, filters.deals, reloadTokens.deals);
  const quotes = useOneTable(
    SPEC.quotes,
    scopeStamp,
    sorts.quotes,
    filters.quotes,
    reloadTokens.quotes,
  );
  const invoices = useOneTable(
    SPEC.invoices,
    scopeStamp,
    sorts.invoices,
    filters.invoices,
    reloadTokens.invoices,
  );

  const tables: Record<PartnershipTableKey, TableData> = {
    leads,
    companies,
    deals,
    quotes,
    invoices,
  };

  /*
   * ⚠️ No sorting here, and none is wanted — see the block above `TABLES`. Rows
   * arrive in the order the SERVER chose, because ordering is applied in SQL
   * before the limit and no client-side pass over a returned page can reproduce
   * that.
   *
   * Mapped over `TABLES` so the returned ORDER is the layout order, which is
   * the one thing about this array the page depends on.
   */
  return TABLES.map((spec) => ({
    key: spec.key,
    title: spec.title,
    valueLabel: spec.valueLabel,
    /*
     * Built here, where `userId` is in hand, and from the SAME value the five
     * requests are scoped by — so the link cannot come to name a different
     * person than the rows beneath it. It is deliberately NOT part of
     * `TableData`: it is fixed before any request is made, and carrying it
     * through the fetch would let a failure or a race report a scope the
     * request never asked for, exactly as the note above `TableData` says of
     * `sortOptions`.
     */
    listHref: spec.listHref(scopeStamp === null ? null : leadScopeFromStamp(scopeStamp)),
    extraColumns: spec.extraColumns,
    scopedBy: spec.scopedBy,
    sort: sorts[spec.key],
    sortOptions: spec.sortOptions,
    filter: filters[spec.key],
    filterKinds: spec.filterKinds,
    create: spec.create,
    updatedAtAlsoWrittenByXero: spec.updatedAtAlsoWrittenByXero,
    ...tables[spec.key],
    stageStatusStageIds: stageStatusStageIdsOf(spec, tables[spec.key].stageStatusStageIds),
    stageStatusSortable: spec.stageStatusScope !== null,
  }));
}

/**
 * One card's fetch.
 *
 * The state is STAMPED with the user AND the ordering it describes, and a stamp
 * that does not match what is being asked for now reads as pending during
 * render. Two different races close on that one mechanism:
 *
 *  - the signed-in person changing, which must never show one person's rows
 *    under another's heading; and
 *  - the ordering changing twice quickly, where the FIRST response can land
 *    after the second and would otherwise leave rows in one order under a
 *    control reading another.
 *
 * The obvious shape — a bare `TableData` reset by `setState(PENDING)` at the
 * top of the effect — is a synchronous setState inside an effect: it renders
 * the previous rows once under the new heading and then throws them away.
 * Deriving it here means there is no such frame and no cascading render.
 */
function useOneTable(
  spec: TableSpec,
  /** {@link leadScopeStamp} of the page's scope; `null` fires nothing. */
  scopeStamp: string | null,
  sort: PartnershipSort,
  filter: PartnershipFilter,
  reloadToken: number,
): TableData {
  const [loaded, setLoaded] = useState<{ stamp: string; data: TableData } | null>(null);
  // Outlives the stamp on purpose — see `TableData.stageStatusStageIds`.
  const [lastStageIds, setLastStageIds] = useState<ReadonlySet<number> | undefined>(undefined);

  /*
   * 🔴 **THE FOUR ID LISTS ARE PULLED OUT HERE SO THE EFFECT NEVER READS THE
   * FILTER OBJECT, AND THAT IS A GUARD RATHER THAN A TIDY-UP.**
   * ⚠️ *SUPERSEDED 2026-09-14: "**THE THREE ID LISTS**", when `companyType`
   * joined them; and before that "**THE TWO IDS**", when `source` did.*
   * ⛔ **Each new member needs a line here, a segment in the stamp, an entry in
   * `scopedFilter` and a name in the dependency list — FOUR edits, none of which
   * `tsc` asks for.** Missing the dependency is a card that does not refetch;
   * missing the stamp is a stale response landing under a control naming
   * something else.*
   *
   * An effect depending on `filter` re-runs whenever that object's IDENTITY
   * changes — which, for a caller building it inline, is every render. The
   * effect aborts and refetches, the response calls `setLoaded`, that renders,
   * and the cycle repeats: an unbounded request loop, on all five cards at
   * once, with nothing on screen looking wrong. The page memoises its filters
   * today; this makes that a performance choice there rather than a
   * correctness requirement here.
   * ⚠️ *SUPERSEDED: "a request loop against a 120-per-60s per-IP budget shared
   * across the office."* It is **12,000/60s** (corrected 2026-09-17) — which a
   * render loop reaches in seconds rather than being stopped by. **An
   * unbounded loop is not bounded by a wider ceiling.**
   *
   * The effect rebuilds a filter from these three lists instead, so its
   * dependency list names exactly what it reads.
   */
  const filterStatus = filter.status;
  const filterCountry = filter.country;
  const filterSource = filter.source;
  const filterCompanyType = filter.companyType;
  const filterParticipatedCycle = filter.participatedCycle;
  const filterPurchasedCycle = filter.purchasedCycle;
  // Two strings, so stable by value — four edits each, per the note above.
  const filterLastNoteAfter = filter.lastNoteAfter;
  const filterLastNoteBefore = filter.lastNoteBefore;
  /*
   * The stage-status list as a STRING, so the effect keys on its value: the page
   * prunes it per render (`stageStatusFilterForColumns`), and a fresh array each
   * render must not refetch.
   */
  const filterStageStatusKey = filter.stageStatus.join(',');

  /*
   * `null` for a null user, so it can never collide with a real stamp. The hook
   * holds at `PENDING` until there is somebody to scope the request to — every
   * one of these calls is scoped to a person, and dropping the filter would
   * return the whole company's pipeline under a heading saying it is yours.
   *
   * ⚠️ **THE FILTER IS IN THE STAMP, AND IT HAS TO BE — it is not decoration on
   * the dependency list.** The stamp is what makes a late response readable as
   * stale: a card filtered to one country and then to another can land the
   * FIRST response second, and without the filter in the stamp those two
   * responses are indistinguishable, so rows for one country would sit under a
   * control naming the other. Exactly the race the ordering already closes here.
   *
   * ⚠️ **`reloadToken` too**, so a refetch after a create takes the same path a
   * sort change does rather than needing a second mechanism. It is a number
   * nobody reads back, so it only ever forces a miss.
   *
   * ⚠️ **EVERY filter member is stamped even on a card that offers one**, so
   * this string cannot come to describe less than the request did — a card that
   * later gains another filter needs no edit here. ✅ **That promise was tested
   * and held TWICE on 2026-09-14**: `source` landed on the two company cards, and
   * then `companyType` did, and each time this line needed one more segment
   * rather than a new mechanism.
   *
   * ⚠️ **"No edit here" means no new MECHANISM, not no new SEGMENT** — the claim
   * above is about the shape, and a member left out of this string is a stale
   * response landing under a control naming something else. Nothing typechecks
   * the segment count against {@link PartnershipFilter}.
   */
  const stamp = `${scopeStamp ?? 'pending'}|${sort}|${filterStatus ?? ''}|${filterCountry ?? ''}|${filterSource ?? ''}|${filterCompanyType ?? ''}|${filterParticipatedCycle ?? ''}|${filterPurchasedCycle ?? ''}|${filterStageStatusKey}|${filterLastNoteAfter ?? ''}|${filterLastNoteBefore ?? ''}|${reloadToken}`;

  useEffect(() => {
    if (scopeStamp === null) return;

    // Rebuilt from the stamp, so the effect reads a value rather than an object.
    const scopedTo = leadScopeFromStamp(scopeStamp);
    const controller = new AbortController();
    const { signal } = controller;

    /*
     * Every write goes through here so it cannot land on a stamp it does not
     * describe: a response arriving after the user or the ordering changed
     * rebuilds from pending under its OWN stamp rather than merging into rows
     * describing something else. `signal.aborted` already covers the ordinary
     * case; this covers the one where two effects overlap.
     */
    const update = (change: (entry: TableData) => TableData) => {
      setLoaded((current) => {
        const mine = current !== null && current.stamp === stamp;
        return { stamp, data: change(mine ? current.data : PENDING) };
      });
    };

    /*
     * Rebuilt from the three lists rather than closed over — see the note above
     * `filterStatus`. This object is the request's, and it lives exactly as long
     * as the request.
     */
    const scopedFilter: PartnershipFilter = {
      status: filterStatus,
      country: filterCountry,
      source: filterSource,
      companyType: filterCompanyType,
      participatedCycle: filterParticipatedCycle,
      purchasedCycle: filterPurchasedCycle,
      stageStatus:
        filterStageStatusKey === '' ? [] : filterStageStatusKey.split(',').map(Number),
      lastNoteAfter: filterLastNoteAfter,
      lastNoteBefore: filterLastNoteBefore,
    };

    spec
      .load(scopedTo, sort, scopedFilter, signal)
      .then((page) => {
        if (signal.aborted) return;
        // Cached by value in `stageIdSetOf`, so an unchanged scope is the same Set.
        if (page.stageStatusStageIds !== undefined) setLastStageIds(page.stageStatusStageIds);
        update((entry) => ({ ...entry, rows: page.rows, total: page.total, error: null }));
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        /*
         * Rows are cleared alongside the error. Leaving stale rows under a
         * failure message would be worse than an empty table, and the page
         * tells failed from empty on `error`, never on `rows`.
         */
        update((entry) => ({
          ...entry,
          rows: [],
          total: 0,
          error:
            caught instanceof Error ? caught.message : `Failed to load ${spec.title.toLowerCase()}`,
        }));
      })
      .finally(() => {
        if (signal.aborted) return;
        update((entry) => ({ ...entry, loading: false }));
      });

    return () => controller.abort();
    /*
     * ⚠️ The four filter ID LISTS, never the object — see the note above
     * `filterStatus`, which is what stops an inline filter object turning this
     * into a request loop. The list is complete: this effect reads the spec, the
     * user, the ordering, these four lists and the stamp built from them.
     */
  }, [
    spec,
    scopeStamp,
    sort,
    filterStatus,
    filterCountry,
    filterSource,
    filterCompanyType,
    filterParticipatedCycle,
    filterPurchasedCycle,
    filterStageStatusKey,
    filterLastNoteAfter,
    filterLastNoteBefore,
    stamp,
  ]);

  const data = loaded !== null && loaded.stamp === stamp ? loaded.data : PENDING;
  return lastStageIds === undefined ? data : { ...data, stageStatusStageIds: lastStageIds };
}
